import React, { useState, useEffect, useRef, useMemo, useCallback, memo } from 'react';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import DraggableGoalCard from '../components/DraggableGoalCard';
import GoalDropZone from '../components/GoalDropZone';
import {
    Button,
    AlertBar,
    CircularLoader,
    colors,
    IconView16,
    IconChevronDown24, IconChevronRight24,
    IconDelete16,
    IconAdd16,
    IconEdit16
} from '@dhis2/ui';
import { useDataEngine } from '@dhis2/app-runtime';
import { usePerformanceContext, dataElementMapping } from '../PerformanceContext';
import { useAuth } from '../../auth/AuthContext';
import { fetchObjectiveWithDetails, saveObjectiveEvent, createEventPayload } from '../hooks/ObjectiveService';
import { ORG_GOALS_PROGRAM, ORG_GOALS_STAGE } from '../api/GoalsConstants';
import DraggableHigherLevelObjective from '../components/DraggableHigherLevelObjective';
import HigherLevelObjectiveDropZone from '../components/HigherLevelObjectiveDropZone';
import useNotificationService from '../../notifications/services/notificationService';
import { POSITION_ATTRIBUTES } from '../../staff/api/constants';



// ===== UPDATED COMPONENT WITH PER-OBJECTIVE GOAL SELECTION =====
const EditModeObjectiveList = memo(({
        objectives,
        expandedObjectiveIndex,
        totalWeight,
        isSubmitting,
        onCancel,
        handleSubmit,
        handleRemoveObjectiveInEditMode,
        handleAddObjectiveInEditMode,
        updateObjectiveField,
        handleEditObjectiveWeightChange,
        setExpandedObjectiveIndex,
        // NEW PROPS
        goals,
        loadingGoals,
        isTopLevelUser,
        onGoalSelect, // New callback for when goal is selected
        selectedHigherLevelObjective,
        higherLevelObjectives,
        loadingHigherLevel,
        onHigherLevelObjectiveSelect,
        userRole, // To determine if CEO or not
        handleKraChange,
        handleAddKra,
        handleRemoveKra,
        handleKpiChange,
        handleAddKpi,
        handleRemoveKpi
    }) => {
        if (!objectives?.length) return null;
            const remainingWeight = 100 - totalWeight;

        // Helper to render goal dropdown
        const renderGoalDropdown = (objective, index) => {
            if (!isTopLevelUser) return null; // Only show for top-level users

            return (
                <div style={{ marginBottom: '16px' }}>
                    <label style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                        <strong>Company Goal Alignment *</strong>
                        <select
                            value={objective.companyAlignedGoal || ''}
                            onChange={(e) => {
                                const goalId = e.target.value;
                                // Update the objective field
                                updateObjectiveField(index, 'companyAlignedGoal', goalId);
                                // Call the parent callback if provided
                                if (onGoalSelect) {
                                    onGoalSelect(goalId, index);
                                }
                            }}
                            style={{
                                padding: '10px',
                                border: `1px solid ${colors.blue300}`,
                                borderRadius: '4px',
                                fontSize: '0.95rem',
                                backgroundColor: objective.companyAlignedGoal ? colors.blue050 : 'white',
                                fontWeight: objective.companyAlignedGoal ? 'bold' : 'normal',
                                width: '100%',
                                boxSizing: 'border-box'
                            }}
                            disabled={loadingGoals || isSubmitting}
                        >
                            <option value="">-- Select a Company Goal --</option>
                            {goals.map(goal => (
                                <option key={goal.id} value={goal.id}>
                                    {goal.name} {goal.description ? `- ${goal.description.substring(0, 30)}...` : ''}
                                </option>
                            ))}
                        </select>
                        {loadingGoals && <small style={{ color: colors.blue600 }}>Loading goals...</small>}
                    </label>
                    
                    {/* Show goal details if selected */}
                    {objective.companyAlignedGoal && (
                        <div style={{
                            marginTop: '8px',
                            padding: '8px',
                            backgroundColor: colors.green050,
                            border: `1px solid ${colors.green300}`,
                            borderRadius: '4px',
                            fontSize: '0.9rem'
                        }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <span style={{ fontSize: '1.2rem' }}>🎯</span>
                                <span>
                                    <strong>Aligned Goal:</strong>{' '}
                                    {goals.find(g => g.id === objective.companyAlignedGoal)?.name || 'Unknown Goal'}
                                </span>
                            </div>
                        </div>
                    )}
                </div>
            );
        };

        
        // Add this helper function at the top of your EditModeObjectiveList component
        const findLinkedGoal = (objId) => {
            if (!objId || !higherLevelObjectives.length || !goals.length) return null;
            
            const selectedObj = higherLevelObjectives.find(obj => obj.id === objId);
            if (!selectedObj) return null;
            
            // Try to find direct goal linkage
            const directGoalId = selectedObj.dataValues?.find(
                dv => dv.dataElement === 'VhrIQQIVpB9'
            )?.value;
            
            if (directGoalId) {
                const goal = goals.find(g => g.id === directGoalId);
                if (goal) return goal;
            }
            
            // Try to find through parent chain (for level 6 objectives that might link to CEO objectives)
            const parentId = selectedObj.dataValues?.find(
                dv => dv.dataElement === 'neuilg3L4d2' || dv.dataElement === 'plJovUlxfQ0'
            )?.value;
            
            if (parentId) {
                const parentObj = higherLevelObjectives.find(obj => obj.id === parentId);
                if (parentObj) {
                    const parentGoalId = parentObj.dataValues?.find(
                        dv => dv.dataElement === 'VhrIQQIVpB9'
                    )?.value;
                    
                    if (parentGoalId) {
                        return goals.find(g => g.id === parentGoalId);
                    }
                }
            }
            
            return null;
        };
        
        
     
        // Helper to render higher-level objective dropdown (for non-CEO)        
            const renderHigherLevelDropdown = (objective, index) => {
                if (isTopLevelUser) return null;

                // Find which goal is currently linked (if any)
                const currentGoalId = objective.companyAlignedGoal || 
                                    objective.linkedGoalId || 
                                    (objective.objectiveAlignment ? 
                                        higherLevelObjectives.find(o => o.id === objective.objectiveAlignment)?.companyAlignedGoal : 
                                        null);

                return (
                    <div style={{ marginBottom: '16px' }}>
                        {/* Higher-Level Objective Selection */}
                        <label style={{ display: 'flex', flexDirection: 'column', gap: '4px', marginBottom: '12px' }}>
                            <strong>Higher-Level Objective Alignment *</strong>
                            <select
                                value={objective.objectiveAlignment || ''}
                                onChange={(e) => {
                                    const objectiveId = e.target.value;
                                    const selectedObj = higherLevelObjectives.find(obj => obj.id === objectiveId);
                                    
                                    if (selectedObj) {
                                        updateObjectiveField(index, 'objectiveAlignment', selectedObj.id);
                                        updateObjectiveField(index, 'higherLevelObjectiveId', selectedObj.id);
                                        updateObjectiveField(index, 'higherLevelObjectiveName', selectedObj.name);
                                        
                                        // If this manager objective has a goal, suggest it
                                        if (selectedObj.companyAlignedGoal) {
                                            const goal = goals.find(g => g.id === selectedObj.companyAlignedGoal);
                                            if (goal) {
                                                updateObjectiveField(index, '_managerGoalName', goal.name);
                                                updateObjectiveField(index, 'suggestedGoalId', goal.id);
                                            }
                                        }
                                    }
                                }}
                                style={{
                                    padding: '8px',
                                    WebkitAppearance: 'menulist-button',
                                    appearance: 'menulist-button',
                                    backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24'%3E%3Cpath fill='${colors.purple600}' d='M7 10l5 5 5-5z'/%3E%3C/svg%3E")`,
                                    backgroundRepeat: 'no-repeat',
                                    backgroundPosition: 'right 10px center',
                                    backgroundSize: '24px 24px',
                                    paddingRight: '40px',
                                    border: `1px solid ${colors.purple300}`,
                                    borderRadius: '4px',
                                    fontSize: '0.95rem',
                                    backgroundColor: objective.objectiveAlignment ? colors.purple050 : 'white',
                                    fontWeight: objective.objectiveAlignment ? 'bold' : 'normal',
                                    width: '100%',
                                    boxSizing: 'border-box'
                                }}
                            >
                                <option value="">-- Select a Manager Objective --</option>
                                {higherLevelObjectives.map(obj => (
                                    <option key={obj.id} value={obj.id}>
                                        {obj.name} (by: {obj.createdBy})
                                    </option>
                                ))}
                            </select>
                        </label>
              {/* Show ALL Goals for selection (independent of manager's goal) */}
              <div style={{ marginTop: '16px', borderTop: '1px solid #eee', paddingTop: '12px' }}>
              <label style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                <strong>Link to Company Goal (Optional)</strong>
                                <select
                                    value={objective.companyAlignedGoal || currentGoalId || ''}
                                    onChange={(e) => {
                                        const goalId = e.target.value;
                                        const goal = goals.find(g => g.id === goalId);
                                        
                                        updateObjectiveField(index, 'companyAlignedGoal', goalId);
                                        if (goal) {
                                            updateObjectiveField(index, '_managerGoalName', goal.name);
                                        }
                                    }}
                                    style={{
                                        padding: '8px',
                                        padding: '8px',
                                        WebkitAppearance: 'menulist-button',
                                        appearance: 'menulist-button',
                                        backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24'%3E%3Cpath fill='${colors.purple600}' d='M7 10l5 5 5-5z'/%3E%3C/svg%3E")`,
                                        backgroundRepeat: 'no-repeat',
                                        backgroundPosition: 'right 10px center',
                                        backgroundSize: '24px 24px',
                                        paddingRight: '40px',
                                        border: `1px solid ${colors.purple300}`,
                                        borderRadius: '4px',
                                        fontSize: '0.95rem',
                                        backgroundColor: objective.objectiveAlignment ? colors.purple050 : 'white',
                                        fontWeight: objective.objectiveAlignment ? 'bold' : 'normal',
                                        width: '100%',
                                        boxSizing: 'border-box'
                                    }}
                                >
                                    <option value="">-- Select a Company Goal --</option>
                                    {goals.map(goal => (
                                        <option key={goal.id} value={goal.id}>
                                            {goal.name}
                                        </option>
                                    ))}
                                </select>
                            </label>
                            
                            {/* Show suggestion if manager's objective has a goal */}
                            {objective.objectiveAlignment && 
                            higherLevelObjectives.find(o => o.id === objective.objectiveAlignment)?.companyAlignedGoal && (
                                <div style={{
                                    marginTop: '8px',
                                    padding: '8px',
                                    backgroundColor: '#e3f2fd',
                                    borderRadius: '4px',
                                    fontSize: '0.9rem'
                                }}>
                                    💡 <strong>Suggested:</strong> Your manager's objective is linked to "{goals.find(g => 
                                        g.id === higherLevelObjectives.find(o => o.id === objective.objectiveAlignment)?.companyAlignedGoal
                                    )?.name}"
                                </div>
                            )}
                        </div>

                        {/* Show current alignment summary */}
                        {objective.objectiveAlignment && (
                            <div style={{
                                marginTop: '12px',
                                padding: '8px',
                                backgroundColor: '#f5f5f5',
                                borderRadius: '4px'
                            }}>
                                <strong>Aligned to:</strong> {objective.higherLevelObjectiveName}
                                {(objective.companyAlignedGoal || currentGoalId) && (
                                    <>
                                        <br />
                                        <strong>Goal:</strong> {goals.find(g => g.id === (objective.companyAlignedGoal || currentGoalId))?.name}
                                    </>
                                )}
                            </div>
                        )}
                    </div>
                );
            };

        return (
            <div style={{ marginBottom: '24px' }}>
                {/* Weight Summary Banner - unchanged */}
                <div style={{
                    backgroundColor: colors.blue050,
                    border: `1px solid ${colors.blue300}`,
                    borderRadius: '6px',
                    padding: '16px',
                    marginBottom: '16px'
                }}>
                    <h4 style={{
                        margin: '0 0 12px 0',
                        color: colors.blue900,
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px'
                    }}>
                        <div style={{
                            width: '28px',
                            height: '28px',
                            backgroundColor: colors.blue600,
                            color: 'white',
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '1.1rem',
                            fontWeight: 'bold'
                        }}>
                            {objectives.length}
                        </div>
                        Edit Weight Group Objectives
                    </h4>
                    <p style={{ margin: '8px 0 0 0', fontSize: '0.95rem', color: colors.blue800 }}>
                        You have {objectives.length} objective{objectives.length > 1 ? 's' : ''} sharing 100% total weight.
                        {remainingWeight > 0 && (
                            <span style={{ fontWeight: 'bold', color: colors.orange700 }}>
                                {' '}⚠️ {remainingWeight}% weight not allocated yet.
                            </span>
                        )}
                    </p>

                    {/* Weight Visualization */}
                    <div style={{ marginTop: '12px' }}>
                        <div style={{
                            height: '8px',
                            backgroundColor: '#e0e0e0',
                            borderRadius: '4px',
                            overflow: 'hidden',
                            marginBottom: '8px'
                        }}>
                            {objectives.map((obj, idx) => (
                                <div
                                    key={`weight-bar-${obj.id || obj._tempId || idx}`}
                                    style={{
                                        width: `${obj.weight || 0}%`,
                                        height: '100%',
                                        backgroundColor: idx % 2 === 0 ? colors.blue500 : colors.blue700,
                                        float: 'left',
                                        transition: 'width 0.3s ease'
                                    }}
                                    title={`${obj.name || `Objective ${idx + 1}`}: ${obj.weight || 0}%`}
                                />
                            ))}
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.9rem' }}>
                            <strong style={{ color: totalWeight === 100 ? colors.green700 : colors.blue700 }}>
                                Total: {totalWeight}%
                            </strong>
                            {totalWeight === 100 ? (
                                <span style={{ color: colors.green700, fontWeight: 500 }}>✅ Complete</span>
                            ) : remainingWeight > 0 ? (
                                <span style={{ color: colors.orange700, fontWeight: 500 }}>
                                    ⚠️ {remainingWeight}% remaining
                                </span>
                            ) : (
                                <span style={{ color: colors.red700, fontWeight: 500 }}>
                                    ❌ Over by {Math.abs(remainingWeight)}%
                                </span>
                            )}
                        </div>
                    </div>
                </div>
                
                {/* CRITICAL: Stable keys on EVERY objective card */}
                {objectives.map((objective, index) => (
                    <div
                        key={objective.id || objective._tempId || `obj-${index}`}
                        style={{
                            marginBottom: '12px',
                            border: `1px solid ${colors.grey300}`,
                            borderRadius: '6px',
                            overflow: 'hidden',
                            boxShadow: expandedObjectiveIndex === index ? `0 4px 12px ${colors.blue400}` : 'none'
                        }}
                    >
                        {/* Card Header */}
                        <div
                            onClick={() => setExpandedObjectiveIndex(expandedObjectiveIndex === index ? null : index)}
                            style={{
                                padding: '16px',
                                backgroundColor: expandedObjectiveIndex === index ? colors.blue100 : colors.blue200,
                                cursor: 'pointer',
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                borderBottom: expandedObjectiveIndex === index ? `1px solid ${colors.blue200}` : 'none'
                            }}
                        > 
                            <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: '12px' }}>
                                <div style={{
                                    width: '32px',
                                    height: '32px',
                                    backgroundColor: colors.blue600,
                                    color: 'white',
                                    borderRadius: '50%',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontWeight: 'bold',
                                    flexShrink: 0
                                }}>
                                    {index + 1} 
                                </div>
                                
                                <div style={{ flex: 1 }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                        <strong style={{
                                            color: colors.grey900,
                                            fontSize: '1.1rem',
                                            maxWidth: '60%',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis'
                                        }}>
                                            {objective.name || `Objective ${index + 1}`}
                                        </strong>
                                        {objective.id && (
                                            <span style={{
                                                fontSize: '0.8rem',
                                                backgroundColor: colors.green100,
                                                color: colors.green800,
                                                padding: '2px 8px',
                                                borderRadius: '12px',
                                                fontWeight: 500
                                            }}>
                                                ✓ Saved
                                            </span>
                                        )}
                                    </div>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: '100px' }}>
                                        <div style={{
                                            width: '40px',
                                            height: '40px',
                                            borderRadius: '50%',
                                            backgroundColor: colors.blue100,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            border: `2px solid ${colors.blue500}`
                                        }}>
                                            <span style={{
                                                fontWeight: 'bold',
                                                color: colors.blue900,
                                                fontSize: '1.1rem'
                                            }}>
                                                {objective.weight || 0}%
                                            </span>
                                        </div>
                                        <span style={{ fontSize: '0.85rem', color: colors.grey900 }}>Weight</span>
                                    </div>
                                </div>

                                {/* Alignment status badge */}
                                {objective.objectiveAlignment && (
                                    <div style={{
                                        padding: '4px 8px',
                                        backgroundColor: colors.purple100,
                                        color: colors.purple800,
                                        borderRadius: '12px',
                                        fontSize: '0.8rem',
                                        fontWeight: 500
                                    }}>
                                        ⬆️ Aligned
                                    </div>
                                )}
                                {objective.companyAlignedGoal && (
                                    <div style={{
                                        padding: '4px 8px',
                                        backgroundColor: colors.green100,
                                        color: colors.green800,
                                        borderRadius: '12px',
                                        fontSize: '0.8rem',
                                        fontWeight: 500
                                    }}>
                                        🎯 Goal Linked
                                    </div>
                                )}
                            </div>

                            <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                                <div style={{
                                    width: '24px',
                                    height: '24px',
                                    borderRadius: '50%',
                                    backgroundColor: colors.grey200,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                }}>
                                    {expandedObjectiveIndex === index ? <IconChevronDown24 /> : <IconChevronRight24 />}
                                </div>
                            </div>
                        </div>

                        {/* Card Content - ONLY RENDER WHEN EXPANDED */}
                        {expandedObjectiveIndex === index && (
                            <div style={{ padding: '20px', backgroundColor: colors.blue050 }}>
                                <div style={{
                                    display: 'grid',
                                    gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
                                    gap: '16px',
                                    marginBottom: '16px'
                                }}>
                                    {/* NAME INPUT */}
                                    <div>
                                        <label style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                            <strong>Name *</strong>
                                            <input
                                                type="text"
                                                value={objective.name || ''}
                                                onChange={e => {
                                                    const val = e?.target?.value;
                                                    if (val !== undefined && val !== null) {
                                                        updateObjectiveField(index, 'name', val);
                                                    }
                                                }}
                                                style={{
                                                    padding: '10px',
                                                    border: `1px solid ${colors.grey300}`,
                                                    borderRadius: '4px',
                                                    fontSize: '0.95rem',
                                                    width: '100%',
                                                    boxSizing: 'border-box'
                                                }}
                                            />
                                        </label>
                                    </div>

                                    {/* WEIGHT INPUT */}
                                    <div>
                                        <label style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                            <strong>Weight (%) *</strong>
                                            <input
                                                type="number"
                                                min="1"
                                                max="100"
                                                value={objective.weight || ''}
                                                onChange={e => {
                                                    const rawValue = e?.target?.value;
                                                    if (rawValue === undefined || rawValue === '' || rawValue === null) return;
                                                    const val = parseInt(rawValue, 10);
                                                    if (!isNaN(val) && val >= 1 && val <= 100) {
                                                        handleEditObjectiveWeightChange(index, val);
                                                    }
                                                }}
                                                style={{
                                                    padding: '10px',
                                                    border: `1px solid ${colors.blue300}`,
                                                    borderRadius: '4px',
                                                    fontSize: '0.95rem',
                                                    backgroundColor: colors.blue050,
                                                    fontWeight: 'bold',
                                                    width: '100%',
                                                    boxSizing: 'border-box'
                                                }}
                                            />
                                            <small style={{ fontSize: '0.8rem', color: colors.grey600 }}>
                                                Current: {objective.weight || 0}%, Total: {totalWeight}%
                                            </small>
                                        </label>
                                    </div>
                                </div>

                                {/* ALIGNMENT SECTION - GOAL DROPDOWN FOR CEO / HIGHER-LEVEL FOR STAFF */}
                                {isTopLevelUser ? renderGoalDropdown(objective, index) : renderHigherLevelDropdown(objective, index)}

                                {/* Description */}
                                <div style={{ marginBottom: '16px' }}>
                                    <label style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                        <strong>Description</strong>
                                        <textarea
                                            value={objective.description || ''}
                                            onChange={e => {
                                                const val = e?.target?.value;
                                                if (val !== undefined && val !== null) {
                                                    updateObjectiveField(index, 'description', val);
                                                }
                                            }}
                                            rows="3"
                                            style={{
                                                padding: '10px',
                                                border: `1px solid ${colors.grey300}`,
                                                borderRadius: '4px',
                                                fontSize: '0.95rem',
                                                resize: 'vertical',
                                                width: '100%',
                                                boxSizing: 'border-box'
                                            }}
                                        />
                                    </label>
                                </div>

                                {/* ===== KRA & KPI EDITING SECTION ===== */}
                                <div style={{
                                marginTop: '24px',
                                padding: '16px',
                                border: `1px solid ${colors.grey300}`,
                                borderRadius: '6px',
                                backgroundColor: colors.grey050
                                }}>
                                <div style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    marginBottom: '16px',
                                    paddingBottom: '12px',
                                    borderBottom: `2px solid ${colors.grey300}`
                                }}>
                                    <div>
                                    <h6 style={{
                                        color: colors.grey800,
                                        margin: '0 0 4px 0',
                                        fontWeight: 600,
                                        fontSize: '1.1rem'
                                    }}>
                                        KRAs & KPIs for: "{objective.name?.trim() || `Objective ${index + 1}`}"
                                    </h6>
                                    <p style={{
                                        margin: 0,
                                        fontSize: '0.85rem',
                                        color: colors.grey600
                                    }}>
                                        Define measurable outcomes for this objective
                                    </p>
                                    </div>
                                    <span style={{
                                    fontSize: '0.8rem',
                                    backgroundColor: colors.blue100,
                                    color: colors.blue800,
                                    padding: '4px 12px',
                                    borderRadius: '12px',
                                    fontWeight: 500
                                    }}>
                                    KPIs required • KRAs optional
                                    </span>
                                </div>

                                {/* KRAs Section (OPTIONAL) */}
                                <div style={{ marginBottom: '24px' }}>
                                    <div style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    marginBottom: '12px',
                                    padding: '8px',
                                    backgroundColor: colors.grey100,
                                    borderRadius: '4px'
                                    }}>
                                    <div>
                                        <label style={{ fontWeight: 600, color: colors.grey700, margin: 0 }}>
                                        Key Result Areas
                                        </label>
                                        <p style={{
                                        margin: '4px 0 0 0',
                                        fontSize: '0.8rem',
                                        color: colors.grey600,
                                        fontStyle: 'italic'
                                        }}>
                                        Optional - High-level areas of focus
                                        </p>
                                    </div>
                                    <button
                                        type="button"
                                        style={{
                                            boxShadow: "0 2px 4px rgba(108, 105, 105, 0.5)",
                                            color: 'white',
                                            backgroundColor: 'rgb(35, 59, 240)',
                                            borderRadius: '8px',
                                            border: 'none',
                                            width: '100px',
                                            height: '35px',
                                            cursor: 'pointer',
                                            margin: '10px'
                                        }}
                                        onClick={() => handleAddKra(index)}
                                    >
                                        <IconAdd16 /> Add KRA
                                    </button>
                                    </div>


                                    {console.log('🔍 [RENDER DEBUG] Objective', index, {
                                        id: objective?.id,
                                        krasCount: objective?.kras?.length,
                                        kpisCount: objective?.kpis?.length,
                                        firstKpi: objective?.kpis?.[0]?.name
                                        }) || null}
                                    
                                    {objective.kras?.map((kra, kraIndex) => (
                                    <div key={kraIndex} style={{
                                        display: 'grid',
                                        gridTemplateColumns: '1fr 100px 45px',
                                        gap: '12px',
                                        marginBottom: '12px',
                                        alignItems: 'center',
                                        padding: '12px',
                                        backgroundColor: 'white',
                                        border: `1px solid ${colors.grey300}`,
                                        borderRadius: '4px'
                                    }}>
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                        <input
                                            placeholder="e.g., Improve customer satisfaction"
                                            value={kra?.name || ''}
                                            onChange={(e) => handleKraChange(index, kraIndex, 'name', e.target.value)}
                                            style={{
                                            padding: '8px 12px',
                                            border: `1px solid ${colors.grey300}`,
                                            borderRadius: '4px',
                                            fontSize: '0.9rem',
                                            width: '100%',
                                            boxSizing: 'border-box'
                                            }}
                                        />
                                        {kra?.name?.trim() && (
                                            <textarea
                                            placeholder="Describe this KRA in detail..."
                                            value={kra?.description || ''}
                                            onChange={(e) => handleKraChange(index, kraIndex, 'description', e.target.value)}
                                            rows="2"
                                            style={{
                                                padding: '6px 10px',
                                                border: `1px solid ${colors.grey200}`,
                                                borderRadius: '4px',
                                                fontSize: '0.85rem',
                                                width: '100%',
                                                boxSizing: 'border-box',
                                                resize: 'vertical'
                                            }}
                                            />
                                        )}
                                        </div>
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                        <label style={{ fontSize: '0.75rem', color: colors.grey600, fontWeight: 500 }}>
                                            Weight (%)
                                        </label>
                                        <input
                                            type="number"
                                            placeholder="0"
                                            value={kra?.weight || ''}
                                            onChange={(e) => handleKraChange(index, kraIndex, 'weight', Number(e.target.value) || 0)}
                                            min="0"
                                            max="100"
                                            style={{
                                            padding: '8px',
                                            border: `1px solid ${colors.grey300}`,
                                            borderRadius: '4px',
                                            fontSize: '0.9rem',
                                            textAlign: 'center',
                                            width: '100%',
                                            boxSizing: 'border-box'
                                            }}
                                        />
                                        </div>
                                        <Button
                                        type="button"
                                        small
                                        destructive
                                        onClick={() => handleRemoveKra(index, kraIndex)}
                                        style={{ minWidth: '40px', height: '40px' }}
                                        disabled={objective.kras?.length <= 1}
                                        >
                                        <IconDelete16 />
                                        </Button>
                                    </div>
                                    ))}
                                    
                                    {objective.kras?.length === 0 && (
                                    <div style={{
                                        textAlign: 'center',
                                        padding: '20px',
                                        backgroundColor: colors.grey050,
                                        borderRadius: '4px',
                                        border: `1px dashed ${colors.grey300}`
                                    }}>
                                        <p style={{
                                        margin: '8px 0 0 0',
                                        fontSize: '0.8rem',
                                        color: colors.grey300,
                                        fontStyle: 'italic'
                                        }}>
                                        Click "Add KRA" above if you want to define key result areas
                                        </p>
                                    </div>
                                    )}
                                </div>

                                {/* KPIs Section (MANDATORY) */}
                                <div style={{ borderTop: `2px solid ${colors.grey300}`, paddingTop: '20px' }}>
                                    <div style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    marginBottom: '12px',
                                    padding: '8px',
                                    backgroundColor: colors.orange050,
                                    borderRadius: '4px',
                                    border: `1px solid ${colors.orange300}`
                                    }}>
                                    <div>
                                        <label style={{ fontWeight: 600, color: colors.grey700, margin: 0 }}>
                                        Key Performance Indicators <span style={{ color: colors.red500 }}>*</span>
                                        </label>
                                        <p style={{
                                        margin: '4px 0 0 0',
                                        fontSize: '0.8rem',
                                        color: colors.grey600,
                                        fontStyle: 'italic'
                                        }}>
                                        Required - Specific, measurable metrics
                                        </p>
                                    </div>
                                    <button
                                        type="button"
                                        style={{
                                            boxShadow: "0 2px 4px rgba(108, 105, 105, 0.5)",
                                            color: 'white',
                                            backgroundColor: 'rgb(35, 59, 240)',
                                            borderRadius: '8px',
                                            border: 'none',
                                            width: '100px',
                                            height: '35px',
                                            cursor: 'pointer',
                                            margin: '10px'
                                        }}
                                        onClick={() => handleAddKpi(index)}
                                    >
                                        <IconAdd16 /> Add KPI
                                    </button>
                                    </div>
                                    
                                    {objective.kpis?.map((kpi, kpiIndex) => (
                                    <div key={kpiIndex} style={{
                                        display: 'grid',
                                        gridTemplateColumns: '1fr 1fr 100px 45px',
                                        gap: '12px',
                                        marginBottom: '12px',
                                        alignItems: 'center',
                                        padding: '12px',
                                        backgroundColor: 'white',
                                        border: kpi?.name?.trim() ? `1px solid ${colors.green300}` : `1px solid ${colors.grey300}`,
                                        borderRadius: '4px'
                                    }}>
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                        <input
                                            placeholder="e.g., Revenue growth, Response time"
                                            value={kpi?.name || ''}
                                            onChange={(e) => handleKpiChange(index, kpiIndex, 'name', e.target.value)}
                                            required
                                            style={{
                                            padding: '8px 12px',
                                            border: `1px solid ${kpi?.name?.trim() ? colors.green300 : colors.grey300}`,
                                            borderRadius: '4px',
                                            fontSize: '0.9rem',
                                            width: '100%',
                                            boxSizing: 'border-box',
                                            fontWeight: kpi?.name?.trim() ? 500 : 400,
                                            backgroundColor: kpi?.name?.trim() ? colors.green050 : 'white'
                                            }}
                                        />
                                        {kpi?.name?.trim() && (
                                            <textarea
                                            placeholder="Describe how this KPI will be measured..."
                                            value={kpi?.description || ''}
                                            onChange={(e) => handleKpiChange(index, kpiIndex, 'description', e.target.value)}
                                            rows="2"
                                            style={{
                                                padding: '6px 10px',
                                                border: `1px solid ${colors.grey200}`,
                                                borderRadius: '4px',
                                                fontSize: '0.85rem',
                                                width: '100%',
                                                boxSizing: 'border-box',
                                                resize: 'vertical'
                                            }}
                                            />
                                        )}
                                        </div>
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                        <label style={{ fontSize: '0.75rem', color: colors.grey600, fontWeight: 500 }}>
                                            Target Value
                                        </label>
                                        <input
                                            placeholder="e.g., 90%, 15%, 24 hours"
                                            value={kpi?.target || ''}
                                            onChange={(e) => handleKpiChange(index, kpiIndex, 'target', e.target.value)}
                                            required
                                            style={{
                                            padding: '8px 12px',
                                            border: `1px solid ${kpi?.target?.trim() ? colors.green300 : colors.grey300}`,
                                            borderRadius: '4px',
                                            fontSize: '0.9rem',
                                            width: '100%',
                                            boxSizing: 'border-box',
                                            backgroundColor: kpi?.target?.trim() ? colors.green050 : 'white'
                                            }}
                                        />
                                        </div>
                                        <div style={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        backgroundColor: kpi?.name?.trim() && kpi?.target?.trim() ? colors.green100 : colors.grey100,
                                        borderRadius: '50%',
                                        width: '40px',
                                        height: '40px'
                                        }}>
                                        {kpi?.name?.trim() && kpi?.target?.trim() ? (
                                            <span style={{ color: colors.green900, fontSize: '1.2rem' }}>✓</span>
                                        ) : (
                                            <span style={{ color: colors.grey500, fontSize: '0.9rem' }}>%</span>
                                        )}
                                        </div>
                                        <Button
                                        type="button"
                                        small
                                        destructive
                                        onClick={() => handleRemoveKpi(index, kpiIndex)}
                                        style={{ minWidth: '40px', height: '40px' }}
                                        disabled={objective.kpis?.length <= 1}
                                        >
                                        <IconDelete16 />
                                        </Button>
                                    </div>
                                    ))}
                                    
                                    {/* KPI Validation Warning */}
                                    {!objective.kpis?.some(k => k?.name?.trim()) && (
                                    <div style={{
                                        marginTop: '12px',
                                        padding: '12px',
                                        backgroundColor: colors.orange050,
                                        border: `1px solid ${colors.orange500}`,
                                        borderRadius: '4px',
                                        fontSize: '0.9rem',
                                        color: colors.orange800,
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '10px'
                                    }}>
                                        <span style={{ fontSize: '1.5rem' }}>⚠️</span>
                                        <div>
                                        <strong>At least one KPI is required</strong>
                                        </div>
                                    </div>
                                    )}
                                    
                                    {/* KPI Summary */}
                                    {objective.kpis?.filter(k => k?.name?.trim()).length > 0 && (
                                    <div style={{
                                        marginTop: '12px',
                                        padding: '10px',
                                        backgroundColor: colors.green050,
                                        border: `1px solid ${colors.green300}`,
                                        borderRadius: '4px',
                                        fontSize: '0.85rem'
                                    }}>
                                        <strong>{objective.kpis?.filter(k => k?.name?.trim()).length} KPI(s) defined:</strong>
                                        <ul style={{ margin: '6px 0 0 20px', paddingLeft: 0 }}>
                                        {objective.kpis
                                            .filter(k => k?.name?.trim())
                                            .map((kpi, idx) => (
                                            <li key={idx} style={{ margin: '2px 0' }}>
                                                <strong>{kpi?.name}</strong> - Target: {kpi?.target || 'Not set'}
                                            </li>
                                            ))}
                                        </ul>
                                    </div>
                                    )}
                                </div>
                                </div>



                                {/* Dates */}
                                <div style={{
                                    display: 'grid',
                                    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                                    gap: '16px',
                                    marginBottom: '16px'
                                }}>
                                    <div>
                                        <label style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                            <strong>Start Date</strong>
                                            <input
                                                type="date"
                                                value={objective.startDate || ''}
                                                onChange={e => {
                                                    const val = e?.target?.value;
                                                    if (val !== undefined && val !== null) {
                                                        updateObjectiveField(index, 'startDate', val);
                                                    }
                                                }}
                                                style={{
                                                    padding: '10px',
                                                    border: `1px solid ${colors.grey300}`,
                                                    borderRadius: '4px',
                                                    fontSize: '0.95rem',
                                                    width: '100%',
                                                    boxSizing: 'border-box'
                                                }}
                                            />
                                        </label>
                                    </div>
                                    <div>
                                        <label style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                            <strong>Completion Date</strong>
                                            <input
                                                type="date"
                                                value={objective.completionDate || ''}
                                                onChange={e => {
                                                    const val = e?.target?.value;
                                                    if (val !== undefined && val !== null) {
                                                        updateObjectiveField(index, 'completionDate', val);
                                                    }
                                                }}
                                                style={{
                                                    padding: '10px',
                                                    border: `1px solid ${colors.grey300}`,
                                                    borderRadius: '4px',
                                                    fontSize: '0.95rem',
                                                    width: '100%',
                                                    boxSizing: 'border-box'
                                                }}
                                            />
                                        </label>
                                    </div>
                                </div>              

                                {/* Remove button */}
                                <div style={{
                                    display: 'flex',
                                    justifyContent: 'flex-end',
                                    gap: '12px',
                                    paddingTop: '16px',
                                    borderTop: `1px solid ${colors.grey200}`
                                }}>
                                </div>

                                {/* Remarks Field */}
                                {objective.remarks && (
                                    <div style={{ 
                                        marginTop: '20px', 
                                        paddingTop: '16px', 
                                        borderTop: `2px solid ${colors.orange300}`,
                                        backgroundColor: colors.orange050,
                                        borderRadius: '6px'
                                    }}>
                                        <label style={{ 
                                            display: 'flex', 
                                            flexDirection: 'column', 
                                            gap: '8px'
                                        }}>
                                            <div style={{ 
                                                display: 'flex', 
                                                alignItems: 'center', 
                                                gap: '8px',
                                                color: colors.orange800,
                                                fontWeight: 'bold',
                                                fontSize: '1.05rem'
                                            }}>
                                                <span role="img" aria-label="comment">💬</span>
                                                <span>Manager's Remarks</span>
                                                {objective.approvalStatus === 'returned' && (
                                                    <span style={{
                                                        fontSize: '0.8rem',
                                                        backgroundColor: colors.orange200,
                                                        color: colors.orange900,
                                                        padding: '2px 8px',
                                                        borderRadius: '12px',
                                                        fontWeight: 500
                                                    }}>
                                                        ⚠️ Returned for Revision
                                                    </span>
                                                )}
                                             {/* Draft Badge */}
                                            {String(objective.status || '').toLowerCase() === 'draft' && (
                                                <span style={{
                                                    fontSize: '0.75rem',
                                                    backgroundColor: '#6c757d',
                                                    color: 'white',
                                                    padding: '3px 10px',
                                                    borderRadius: '12px',
                                                    fontWeight: 600,
                                                    marginLeft: '8px',
                                                    display: 'inline-flex',
                                                    alignItems: 'center',
                                                    gap: '4px'
                                                }}>
                                                    📝 Draft
                                                </span>
                                            )}

                                            </div>
                                            <textarea
                                                value={objective.remarks}
                                                readOnly
                                                rows="4"
                                                style={{
                                                    padding: '12px',
                                                    border: `2px solid ${colors.orange400}`,
                                                    borderRadius: '4px',
                                                    backgroundColor: colors.orange100,
                                                    fontSize: '0.95rem',
                                                    fontStyle: 'italic',
                                                    resize: 'vertical',
                                                    width: '100%',
                                                    boxSizing: 'border-box',
                                                    color: colors.grey900,
                                                    fontWeight: 500
                                                }}
                                            />
                                        </label>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                ))}

                    {/* Add New Button */}
                    {remainingWeight > 0 && (
                        <Button
                            primary
                            onClick={handleAddObjectiveInEditMode}
                            style={{ marginTop: '16px', width: '100%' }}
                            disabled={isSubmitting}
                        >
                            ➕ Add New Objective ({remainingWeight}% remaining)
                        </Button>
                    )}

                    {/* Save Buttons */}
                    <div style={{
                        display: 'flex',
                        justifyContent: 'flex-end',
                        gap: '12px',
                        marginTop: '24px',
                        paddingTop: '16px',
                        borderTop: `2px solid ${colors.grey200}`
                    }}>
                    <button
                        onClick={onCancel}
                        disabled={isSubmitting}
                         style={{ 
                            boxShadow: "0 2px 4px rgba(241, 18, 18, 0.5)",
                            color: 'white',
                            backgroundColor: 'rgb(208, 9, 9)',
                            borderRadius: '8px',
                            border: 'none',
                            width: '90px',
                            height: '35px',
                            cursor: 'pointer' }}
                        >
                        Cancel
                    </button>
                    {/* NEW: Save as Draft Button */}
                    <button
                        type="button"
                        onClick={() => handleSubmit(null, 'draft')} // Pass 'draft' mode
                        disabled={isSubmitting}
                        style={{
                            boxShadow: "0 2px 4px rgba(108, 105, 105, 0.5)",
                            color: 'white',
                            backgroundColor: isSubmitting ? colors.grey400 : '#6c757d',
                            borderRadius: '8px',
                            border: 'none',
                            width: '130px',
                            height: '35px',
                            cursor: isSubmitting ? 'not-allowed' : 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '8px'
                        }}
                    >
                        {isSubmitting ? (
                            <>
                                <CircularLoader small />
                                <span>Saving...</span>
                            </>
                        ) : (
                            'Save as Draft'
                        )}
                    </button>
                    {/* Existing: Save Final Button */}
                    <button
                        type="button" // Changed to type button to prevent default form submit if inside form
                        style={{
                            boxShadow: "0 2px 4px rgba(108, 105, 105, 0.5)",
                            color: 'white',
                            backgroundColor: 'rgb(35, 59, 240)',
                            borderRadius: '8px',
                            border: 'none',
                            width: '150px',
                            height: '35px',
                            cursor: 'pointer'
                        }}
                        onClick={() => handleSubmit(null, 'final')} // Pass 'final' mode
                        disabled={isSubmitting || (totalWeight !== 100)} // Disable if weight not 100% for final
                    >
                        {isSubmitting ? 'Saving...' : totalWeight !== 100 ?
                            `Save (${100 - totalWeight}% remaining)` :
                            'Save as Final'}
                    </button>
                </div>
            </div>
        );
    });

const ObjectiveForm = ({
    mode,
    objective,
    selectedHigherLevelObjective: propSelectedHigherLevelObjective,
    setSelectedHigherLevelObjective: propSetSelectedHigherLevelObjective,
    onSave,
    onCancel,
    onComplete,
    onCancelisCEO = false,
    isHOD = false,
    isTopStaff = false,
    isSeniorStaff = false,
    title = "Objectives"
}) => {

    const { user } = useAuth();
    const [statusOptions, setStatusOptions] = useState([]);
    const engine = useDataEngine();
    const [notification, setNotification] = useState(null);
    const [goals, setGoals] = useState([]);
    const [selectedGoal, setSelectedGoal] = useState('');
    const [loadingGoals, setLoadingGoals] = useState(false);
    const [currentUserPosition, setCurrentUserPosition] = useState(null);
    const [isTopLevelUser, setIsTopLevelUser] = useState(false);
    const [reportingStructureLoaded, setReportingStructureLoaded] = useState(false);

    const [higherLevelObjectives, setHigherLevelObjectives] = useState([]);
    const [loadingHigherLevelObjectives, setLoadingHigherLevelObjectives] = useState(false);

    const notificationService = useNotificationService();
    const [goalItems, setGoalItems] = useState([]);
    const [weightGroupEnrollment, setWeightGroupEnrollment] = useState(null);
    const [saveMode, setSaveMode] = useState('draft');
    const submissionInProgress = useRef(false);
    const [isInitialLoad, setIsInitialLoad] = useState(true);

    const [filteredManagerObjectives, setFilteredManagerObjectives] = useState([]);
    const [loadingFilteredObjectives, setLoadingFilteredObjectives] = useState(false);
    const [mounted, setMounted] = useState(true);
    const [expandedObjectiveIndex, setExpandedObjectiveIndex] = useState(null);
    const [fiscalYear, setFiscalYear] = useState({ start: '', end: '' });

    const [showDraftsOnly, setShowDraftsOnly] = useState(false);

    const [programAccess, setProgramAccess] = useState({
        isAssigned: false,
        checked: false,
        missingOrgUnit: null,
        assignedOrgUnits: []
    });

    const emailNotificationSent = useRef(false);

    const isResubmittedObjective = (objective) => {
    const status = String(objective?.approvalStatus || objective?.status || '').toLowerCase();
        return status === 'returned' || status === 'resubmitted';
    };

    // Add to your state declarations
    const [editingWeightFor, setEditingWeightFor] = useState(null);
    const [weightEditValue, setWeightEditValue] = useState(0);
    const getObjectiveWeight = useCallback((objective) => {
        const parsed = Number.parseFloat(objective?.weight);
        return Number.isFinite(parsed) ? parsed : 0;
    }, []);
    const sumObjectiveWeights = useCallback((objectiveList = []) => (
        objectiveList.reduce((sum, o) => sum + getObjectiveWeight(o), 0)
    ), [getObjectiveWeight]);
    const createBlankObjective = useCallback((indexHint = 0) => ({
        id: null,
        _tempId: `temp_${Date.now()}_${indexHint}`,
        name: '',
        description: '',
        weight: 0,
        startDate: fiscalYear.start || '',
        completionDate: fiscalYear.end || '',
        orgUnit: user?.organisationUnits?.[0]?.id || '',
        remarks: '',
        score: 0,
        performanceYear: new Date().getFullYear(),
        workType: '',
        companyAlignedGoal: '',
        kras: [],
        kpis: [{ name: '', description: '', target: '' }],
    }), [fiscalYear.start, fiscalYear.end, user?.organisationUnits]);

    // this is weight editor modal component
    const WeightEditorModal = memo(() => {
        console.log('WeightEditorModal rendering, editingWeightFor:', editingWeightFor);
        if (editingWeightFor == null) return null;

        const objective = objectives[editingWeightFor];
        if (!objective) return null;

        const currentTotal = objectives.reduce((sum, o) => sum + getObjectiveWeight(o), 0);
        const otherObjectivesTotal = currentTotal - getObjectiveWeight(objective);
        const maxAllowed = 100 - otherObjectivesTotal;

        return (
            <div style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(0,0,0,0.5)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 1000
            }}>
                <div style={{
                    backgroundColor: 'white',
                    padding: '24px',
                    borderRadius: '8px',
                    minWidth: '400px',
                    maxWidth: '600px',
                    boxShadow: '0 4px 20px rgba(0,0,0,0.15)'
                }}>
                    <h3 style={{ marginTop: 0, color: colors.blue700 }}>
                        Edit Weight for: {objective.name}
                    </h3>

                    <div style={{ marginBottom: '20px' }}>
                        <p><strong>Current Weight:</strong> {objective.weight || 0}%</p>
                        <p><strong>Other Objectives Total:</strong> {otherObjectivesTotal}%</p>
                        <p><strong>Maximum Allowed:</strong> {maxAllowed}%</p>
                        <p><strong>Total Must Equal:</strong> 100%</p>
                    </div>

                    <div style={{ marginBottom: '20px' }}>
                        <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>
                            New Weight (%):
                        </label>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                            <input
                                type="range"
                                min="1"
                                max={maxAllowed}
                                value={weightEditValue}
                                onChange={(e) => setWeightEditValue(parseInt(e.target.value))}
                                style={{ flex: 1 }}
                            />
                            <input
                                type="number"
                                min="1"
                                max={maxAllowed}
                                value={weightEditValue}
                                onChange={(e) => {
                                    const val = parseInt(e.target.value);
                                    if (!isNaN(val) && val >= 1 && val <= maxAllowed) {
                                        setWeightEditValue(val);
                                    }
                                }}
                                style={{
                                    width: '80px',
                                    padding: '8px',
                                    border: `2px solid ${colors.blue300}`,
                                    borderRadius: '4px',
                                    textAlign: 'center',
                                    fontSize: '1.1rem'
                                }}
                            />
                            <span style={{ fontWeight: 'bold', fontSize: '1.1rem' }}>%</span>
                        </div>
                        <div style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            marginTop: '4px',
                            fontSize: '0.85rem',
                            color: colors.grey600
                        }}>
                            <span>1%</span>
                            <span>{maxAllowed}%</span>
                        </div>
                    </div>

                    {/* Visual representation */}
                    <div style={{ marginBottom: '24px' }}>
                        <div style={{
                            height: '20px',
                            backgroundColor: colors.grey200,
                            borderRadius: '10px',
                            overflow: 'hidden',
                            marginBottom: '8px'
                        }}>
                            {/* Other objectives */}
                            <div style={{
                                width: `${otherObjectivesTotal}%`,
                                height: '100%',
                                backgroundColor: colors.grey400,
                                float: 'left'
                            }}></div>
                            {/* This objective (old) */}
                            <div style={{
                                width: `${objective.weight || 0}%`,
                                height: '100%',
                                backgroundColor: colors.orange400,
                                float: 'left'
                            }}></div>
                            {/* This objective (new) */}
                            <div style={{
                                width: `${weightEditValue}%`,
                                height: '100%',
                                backgroundColor: colors.green400,
                                float: 'left',
                                marginLeft: `-${objective.weight || 0}%`
                            }}></div>
                        </div>
                        <div style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            fontSize: '0.85rem'
                        }}>
                            <div>
                                <span style={{
                                    display: 'inline-block',
                                    width: '12px',
                                    height: '12px',
                                    backgroundColor: colors.grey400,
                                    marginRight: '4px'
                                }}></span>
                                Other Objectives: {otherObjectivesTotal}%
                            </div>
                            <div>
                                <span style={{
                                    display: 'inline-block',
                                    width: '12px',
                                    height: '12px',
                                    backgroundColor: colors.orange400,
                                    marginRight: '4px'
                                }}></span>
                                Current: {objective.weight || 0}%
                            </div>
                            <div>
                                <span style={{
                                    display: 'inline-block',
                                    width: '12px',
                                    height: '12px',
                                    backgroundColor: colors.green400,
                                    marginRight: '4px'
                                }}></span>
                                New: {weightEditValue}%
                            </div>
                        </div>
                    </div>

                    <div style={{
                        display: 'flex',
                        justifyContent: 'flex-end',
                        gap: '12px',
                        marginTop: '24px'
                    }}>
                        <Button
                            secondary
                            onClick={() => {
                                setEditingWeightFor(null);
                                setWeightEditValue(0);
                            }}
                        >
                            Cancel
                        </Button>

                        <Button
                            primary
                            onClick={() => {
                                console.log('[DEBUG-WEIGHT] ========== WEIGHT UPDATE STARTED ==========');
                                console.log('[DEBUG-WEIGHT] editingWeightFor:', editingWeightFor, 'weightEditValue:', weightEditValue);
                                console.log('[DEBUG-WEIGHT] Original weights:', objectives.map(o => ({ name: o.name?.substring(0, 20), weight: o.weight })));

                                // ✅ FIX: Only adjust proportionally, DON'T normalize to 100%
                                // The user's intended weight should be preserved
                                const updatedObjectives = adjustWeightsProportionally(
                                    editingWeightFor,
                                    weightEditValue,
                                    objectives
                                );

                                console.log('[DEBUG-WEIGHT] After adjustment - weights:', updatedObjectives.map(o => o.weight));
                                console.log('[DEBUG-WEIGHT] User wanted:', weightEditValue, 'Actual saved:', updatedObjectives[editingWeightFor]?.weight);

                                setObjectives(updatedObjectives);

                                // Close modal
                                setEditingWeightFor(null);
                                setWeightEditValue(0);

                                // Show success message
                                showAlert(
                                    'Weight Updated',
                                    'success',
                                    `Weight for "${objectives[editingWeightFor]?.name?.substring(0, 30)}" updated to ${updatedObjectives[editingWeightFor]?.weight}%`
                                );

                                console.log('[DEBUG-WEIGHT] ========== WEIGHT UPDATE COMPLETED ==========');
                            }}
                        >
                            Update Weight
                        </Button>
                    </div>
                </div>
            </div>
        );
    });

    // ============HELPERS function to extract attributes from position===============
    const extractAttributes = (attributes = []) => {
        const map = {};
        attributes.forEach((attr) => {
            map[attr.attribute] = attr.value;
        });
        return map;
    };

    const mapDataValuesToObjective = (event) => {
    const dataValuesMap = {};
    event.dataValues?.forEach(dv => {
        dataValuesMap[dv.dataElement] = dv.value;
    });
    return {
        id: event.event,
        name: dataValuesMap['EuN5ztFnXsQ'] || '',
        description: dataValuesMap['nWqW5BrcgP1'] || '',
        // Weight parsing with fallback
        weight: (() => {
            const rawWeight = dataValuesMap['yfb9RCRpMB2'];
            if (rawWeight === null || rawWeight === undefined || rawWeight === '') return 0;
            const parsed = parseInt(String(rawWeight).trim(), 10);
            return isNaN(parsed) ? 0 : parsed;
        })(),
        startDate: dataValuesMap['eDcVy661j9e'] || '',
        completionDate: dataValuesMap['J0rjxrDKn1l'] || '',
        status: dataValuesMap['DL7KxNhiV0x'] || '',
        objectiveAlignment: dataValuesMap['neuilg3L4d2'] || '',
        companyAlignedGoal: dataValuesMap['VhrIQQIVpB9'] || '',
        objectiveRoleAssigned: dataValuesMap['vFoMLF5eZ5X'] || '',
        performanceYear: dataValuesMap['CVaNcskWvmN'] || '',
        remarks: dataValuesMap['KeGWxm1IDYF'] || '',
        approvalStatus: dataValuesMap['DzQorireRBf'] || '',
        orgUnit: event.orgUnit,
        trackedEntityInstance: event.trackedEntityInstance,
        enrollment: event.enrollment,
        enrollmentId: event.enrollment,
        eventDate: event.eventDate,
        kras: [],
        kpis: []
    };
};
    const adjustWeightsProportionally = (changedIdx, newWeight, objectivesArray) => {
        const total = objectivesArray.reduce((sum, o) => sum + getObjectiveWeight(o), 0);
        const otherObjectives = objectivesArray.filter((_, idx) => idx !== changedIdx);
        const otherTotal = otherObjectives.reduce((sum, o) => sum + getObjectiveWeight(o), 0);
        const difference = newWeight - (objectivesArray[changedIdx].weight || 0);

        if (otherTotal === 0 || difference === 0) {
            return objectivesArray.map((obj, idx) =>
                idx === changedIdx ? { ...obj, weight: newWeight } : obj
            );
        }

        // Adjust other objectives proportionally
        return objectivesArray.map((obj, idx) => {
            if (idx === changedIdx) {
                return { ...obj, weight: newWeight };
            }

            const proportionalReduction = (obj.weight / otherTotal) * difference;
            const newObjWeight = Math.max(1, Math.round(obj.weight - proportionalReduction));

            return { ...obj, weight: newObjWeight };
        });
    };

    // Handle goal selection
    const handleGoalSelect = (goalId) => {
        setSelectedGoal(goalId);
        setSelectedHigherLevelObjective(null);

        loadManagerObjectivesForGoal(goalId);
    };

    const loadObjectivesInEnrollment = async (engine, enrollmentId) => {
        if (!enrollmentId) return [];
        
        try {
            console.log('🎯 [LOAD GROUP] Fetching objectives in enrollment:', enrollmentId);
            
            const response = await engine.query({
                groupObjectives: {
                    resource: 'events',
                    params: {
                        program: 'yiT00XKrrHP',
                        programStage: 'VIBtpvkpHFR',
                        orgUnit: user?.organisationUnits?.[0]?.id,
                        enrollment: enrollmentId,
                        fields: 'event,dataValues[dataElement,value],createdBy,enrollment,eventDate,orgUnit',
                        ouMode: 'ACCESSIBLE'
                    }
                }
            });
            
            const events = response.groupObjectives?.events || [];
            console.log('✅ [LOAD GROUP] Total events fetched:', events.length);
            
            // Filter to current financial year AND current user's objectives
            const fiscalYearStart = fiscalYear.start;
            const fiscalYearEnd = fiscalYear.end;

            // CRITICAL FIX: Validate dates BEFORE filtering
            const yearStart = fiscalYearStart ? new Date(fiscalYearStart) : null;
            const yearEnd = fiscalYearEnd ? new Date(fiscalYearEnd) : null;
            const isValidDateRange = yearStart && yearEnd && !isNaN(yearStart.getTime()) && !isNaN(yearEnd.getTime());

            if (!isValidDateRange) {
            console.warn('⚠️ [LOAD GROUP] Fiscal year dates invalid/missing. SKIPPING date filter.');
            console.log('⚠️ [LOAD GROUP] Fiscal year context:', { 
                start: fiscalYearStart, 
                end: fiscalYearEnd,
                hasValidDates: isValidDateRange 
            });
            }

            const filteredEvents = events.filter(event => {
            const dvMap = {};
            event.dataValues?.forEach(dv => { dvMap[dv.dataElement] = dv.value; });
            
            // Position TEI check (unchanged)
            const objectivePositionTei = dvMap['SlgO60cMK3F'];
            const isCurrentUserObjective = objectivePositionTei === currentUserPosition?.trackedEntityInstance;
            const isBeingEdited = editingObjective?.id && event.event === editingObjective.id;
            
            if (!isCurrentUserObjective && !isBeingEdited) return false;

            // Always include drafts even if dates are incomplete/outside fiscal range.
            const objectiveStatus = String(dvMap['DL7KxNhiV0x'] || '').toLowerCase();
            if (objectiveStatus === 'draft') return true;
            
            // ✅ ONLY apply date filter if dates are VALID
            if (isValidDateRange) {
                const completionDateStr = dvMap['J0rjxrDKn1l'] || dvMap['eDcVy661j9e'] || '';
                if (!completionDateStr) return false;
                
                const completionDate = new Date(completionDateStr);
                if (isNaN(completionDate.getTime())) return false;
                
                const inDateRange = completionDate >= yearStart && completionDate <= yearEnd;
                if (!inDateRange) {
                console.log('❌ Filtering out - outside date range:', {
                    event: event.event,
                    completionDate: completionDateStr,
                    fiscalStart: fiscalYearStart,
                    fiscalEnd: fiscalYearEnd
                });
                return false;
                }
            } else {
                // ✅ CRITICAL: Skip date filter when fiscal year not configured
                console.log('⚠️ Skipping date filter - fiscal year not configured');
            }
            
            return true;
            });
            
            console.log('✅ [LOAD GROUP] Filtered to', filteredEvents.length, 'objectives for', fiscalYear.start, 'to', fiscalYear.end);
            
            // Map to full objectives
            const groupedObjectives = filteredEvents.map(event => 
                mapDataValuesToObjective(event)
            );
            
            console.log('✅ [LOAD GROUP] Mapped to', groupedObjectives.length, 'full objectives');
            console.log('🔍 [GROUP OBJECTIVES DEBUG]', groupedObjectives.map(o => ({
                id: o.id?.substring(0, 8),
                name: o.name,
                positionTei: o.objectiveRoleAssigned // This should match currentUserPositionTei
            })));
            
            return groupedObjectives;
        } catch (error) {
            console.error('❌ Error loading objectives in enrollment:', error);
            return [];
        }
    };

    const TotalWeightTracker = memo(({ currentWeight, isComplete }) => {
        const remaining = 100 - currentWeight;
        const overage = currentWeight - 100;

        // Calculate weight distribution summary
        const weightSummary = objectives
            .filter(obj => obj.weight > 0)
            .map((obj, idx) => `${idx + 1}. ${obj.name.substring(0, 30)}...: ${obj.weight}%`)
            .join('\n');

        return (
            <div style={{
                backgroundColor: isComplete ? colors.green050 : colors.blue050,
                border: `1px solid ${isComplete ? colors.green300 : colors.blue300}`,
                borderRadius: '6px',
                padding: '12px',
                marginBottom: '20px',
                position: 'relative'
            }}>
                <div style={{
                    height: '8px',
                    backgroundColor: '#e0e0e0',
                    borderRadius: '4px',
                    overflow: 'hidden',
                    marginBottom: '8px'
                }}>
                    <div style={{
                        width: `${Math.min(currentWeight, 100)}%`,
                        height: '100%',
                        backgroundColor: isComplete ? colors.green500 :
                            (currentWeight > 100 ? colors.red500 : colors.blue500),
                        transition: 'width 0.3s ease'
                    }} />
                </div>

                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.95rem' }}>
                    <strong style={{ color: isComplete ? colors.green700 : colors.grey800 }}>
                        Total Weight: {currentWeight}%
                    </strong>
                    {!isComplete && currentWeight < 100 && (
                        <span style={{ color: colors.blue700, fontWeight: 500 }}>
                            Need {remaining}% more to reach 100%
                        </span>
                    )}
                    {currentWeight > 100 && (
                        <span style={{ color: colors.red700, fontWeight: 500 }}>
                            ⚠️ Over by {overage}% - Reduce weights
                        </span>
                    )}
                    {isComplete && (
                        <span style={{ color: colors.green700, fontWeight: 500 }}>
                            ✅ Target reached!
                        </span>
                    )}
                </div>

                {/* Show weight breakdown on hover */}
                <div style={{
                    marginTop: '8px',
                    fontSize: '0.85rem',
                    color: colors.grey600,
                    backgroundColor: 'rgba(255,255,255,0.7)',
                    padding: '8px',
                    borderRadius: '4px',
                    border: '1px dashed rgba(0,0,0,0.1)'
                }}>
                    <strong>Weight Distribution:</strong>
                    <ul style={{ margin: '4px 0 0 0', paddingLeft: '20px' }}>
                        {objectives
                            .filter(obj => obj.weight > 0)
                            .map((obj, idx) => (
                                <li key={idx} style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    marginBottom: '2px'
                                }}>
                                    <span style={{ maxWidth: '70%', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                        {obj.name || `Objective ${idx + 1}`}
                                    </span>
                                    <span style={{ fontWeight: 'bold' }}>{obj.weight}%</span>
                                </li>
                            ))
                        }
                    </ul>
                    {objectives.some(obj => obj.weight === 0) && (
                        <div style={{
                            marginTop: '4px',
                            fontStyle: 'italic',
                            color: colors.orange700
                        }}>
                            ⚠️ {objectives.filter(obj => obj.weight === 0).length} objective(s) have 0% weight
                        </div>
                    )}
                </div>
            </div>
        );
    });

    const resolvePositionManagerNames = async (positionIds) => {
        if (!positionIds?.length || !engine) return {};

        try {
            // 🔹 Remove duplicates and validate IDs
            const uniquePositionIds = [...new Set(positionIds.filter(id =>
                id && typeof id === 'string' && id.trim() !== ''
            ))];

            console.log('🔹 [RESOLVE MANAGERS] Position IDs:', uniquePositionIds);

            if (uniquePositionIds.length === 0) return {};

            // 🔹 Fetch positions individually to avoid filter issues
            const positions = [];
            for (const id of uniquePositionIds) {
                try {
                    const positionResponse = await engine.query({
                        position: {
                            resource: `trackedEntityInstances/${id}`,
                            params: {
                                fields: 'trackedEntityInstance,attributes[attribute,value]'
                            }
                        }
                    });
                    if (positionResponse.position) {
                        positions.push(positionResponse.position);
                    }
                } catch (error) {
                    console.warn(`⚠️ Failed to fetch position ${id}:`, error);
                }
            }
            console.log('✅ [POSITIONS LOADED]', positions.length, 'positions');

            // Debug: Log each position's incumbent
            positions.forEach(pos => {
                const attrs = {};
                pos.attributes?.forEach(attr => { attrs[attr.attribute] = attr.value; });
                console.log('🔍 [POSITION DEBUG]', {
                    positionTei: pos.trackedEntityInstance,
                    incumbentId: attrs['V66IFsw5kq6'],
                    positionName: attrs['w3bwqUZoc6V']
                });
            });

            // 🔹 Extract incumbent IDs efficiently
            const positionMap = new Map();
            const incumbentIdSet = new Set();

            positions.forEach(position => {
                const attributes = {};
                position.attributes?.forEach(attr => {
                    attributes[attr.attribute] = attr.value;
                });

                const incumbentId = attributes['V66IFsw5kq6']; // Current Incumbent Staff ID
                const reportingToTei = attributes['SjP47qi3JeB']; // Reporting To Position TEI

                if (incumbentId) {
                    positionMap.set(position.trackedEntityInstance, {
                        positionId: position.trackedEntityInstance,
                        incumbentId,
                        reportingToTei,
                        attributes
                    });
                    incumbentIdSet.add(incumbentId);
                } else {
                    console.warn('⚠️ No incumbent found for position:', position.trackedEntityInstance);
                    positionMap.set(position.trackedEntityInstance, {
                        positionId: position.trackedEntityInstance,
                        incumbentId: null,
                        reportingToTei,
                        attributes
                    });
                }
            });

            // 🔹 Fetch reporting to positions if not already fetched
            const reportingToTeis = Array.from(positionMap.values())
                .map(pos => pos.reportingToTei)
                .filter(tei => tei && !positionMap.has(tei));

            if (reportingToTeis.length > 0) {
                console.log('🔹 Fetching additional reporting to positions:', reportingToTeis);
                for (const tei of reportingToTeis) {
                    try {
                        const positionResponse = await engine.query({
                            position: {
                                resource: `trackedEntityInstances/${tei}`,
                                params: {
                                    fields: 'trackedEntityInstance,attributes[attribute,value]'
                                }
                            }
                        });
                        if (positionResponse.position) {
                            const pos = positionResponse.position;
                            const attributes = {};
                            pos.attributes?.forEach(attr => {
                                attributes[attr.attribute] = attr.value;
                            });
                            const incumbentId = attributes['V66IFsw5kq6'];
                            const reportingToTei = attributes['SjP47qi3JeB'];
                            positionMap.set(tei, {
                                positionId: tei,
                                incumbentId,
                                reportingToTei,
                                attributes
                            });
                            if (incumbentId) incumbentIdSet.add(incumbentId);
                        }
                    } catch (error) {
                        console.warn(`⚠️ Failed to fetch reporting to position ${tei}:`, error);
                    }
                }
            }

            const incumbentIds = Array.from(incumbentIdSet);
            console.log('✅ [INCUMBENT IDS FOUND]', incumbentIds.length, 'unique staff IDs');

            // 🔹 Get staff details in batch
            const staffMap = new Map();

            if (incumbentIds.length > 0) {
                // Try to get usernames from staff TEIs first
                const staffFilter = incumbentIds.map(id => `trackedEntityInstance:eq:${id}`).join(';');

                try {
                    const staffResponse = await engine.query({
                        staff: {
                            resource: 'trackedEntityInstances',
                            params: {
                                trackedEntityType: 'OaAbpIs1cV7', // Staff TE type
                                filter: staffFilter,
                                fields: 'trackedEntityInstance,attributes[attribute,value]',
                                pageSize: incumbentIds.length
                            }
                        }
                    });

                    const staffMembers = staffResponse.staff?.trackedEntityInstances || [];

                    // 🔹 Collect usernames to batch user API calls
                    const usernames = [];
                    const staffUsernameMap = new Map();

                    staffMembers.forEach(staff => {
                        const attributes = {};
                        staff.attributes?.forEach(attr => {
                            attributes[attr.attribute] = attr.value;
                        });

                        const username = attributes['g3RRIfDiQra']?.trim();
                        if (username) {
                            staffUsernameMap.set(staff.trackedEntityInstance, username);
                            usernames.push(username);
                        }
                    });

                    // 🔹 Batch fetch user details
                    if (usernames.length > 0) {
                        const userFilter = usernames.map(u => `username:eq:${u}`).join(';');

                        try {
                            const usersResponse = await engine.query({
                                users: {
                                    resource: 'users',
                                    params: {
                                        filter: userFilter,
                                        fields: 'id,username,displayName,firstName,surname',
                                        pageSize: usernames.length
                                    }
                                }
                            });

                            const users = usersResponse.users?.users || [];

                            // Create username to displayName map
                            const userDisplayNameMap = new Map();
                            users.forEach(user => {
                                const displayName = user.displayName?.trim() ||
                                    `${user.firstName?.trim() || ''} ${user.surname?.trim() || ''}`.trim() ||
                                    user.username?.trim();
                                if (displayName) {
                                    userDisplayNameMap.set(user.username, displayName);
                                }
                            });

                            // Map staff IDs to display names
                            staffUsernameMap.forEach((username, staffId) => {
                                const displayName = userDisplayNameMap.get(username) || username;
                                staffMap.set(staffId, displayName);
                            });

                        } catch (userError) {
                            console.warn('⚠️ Failed to batch fetch user details:', userError);
                            // Fallback: use usernames directly
                            staffUsernameMap.forEach((username, staffId) => {
                                staffMap.set(staffId, username);
                            });
                        }
                    }

                } catch (staffError) {
                    console.error('❌ Failed to fetch staff details:', staffError);
                }
            }

            // 🔹 Create final position-to-name mapping
            // BUG FIX: Return the INCUMBENT (person) of the position, not the manager
            const managerNameMap = {};

            Array.from(positionMap.entries()).forEach(([positionId, posData]) => {
                // Primary: Return the incumbent (person holding the position)
                const incumbentId = posData.incumbentId;
                if (incumbentId && staffMap.has(incumbentId)) {
                    managerNameMap[positionId] = staffMap.get(incumbentId);
                    console.log('🔍 [NAME RESOLUTION]', {
                        positionId,
                        incumbentId,
                        resolvedName: staffMap.get(incumbentId),
                        source: 'incumbent'
                    });
                } else {
                    // Fallback: Use position title
                    const positionTitle = posData.attributes['w3bwqUZoc6V'] ||
                        posData.attributes['NWe61Sa41CS'] ||
                        'Unknown Position';
                    managerNameMap[positionId] = positionTitle;
                    console.log('🔍 [NAME RESOLUTION]', {
                        positionId,
                        incumbentId,
                        fallbackTo: positionTitle,
                        source: 'position title'
                    });
                }
            });

            console.log('✅ [MANAGER NAMES RESOLVED]', managerNameMap);
            return managerNameMap;

        } catch (error) {
            console.error('❌ Error resolving position manager names:', error);

            // Graceful fallback
            const fallbackMap = {};
            if (positionIds?.length) {
                positionIds.forEach(id => {
                    if (id) {
                        fallbackMap[id] = `Manager (${id.substring(0, 8)}...)`;
                    }
                });
            }
            return fallbackMap;
        }
    };

    const loadManagerObjectivesForGoal = async (goalId) => {
        if (!currentUserPosition || !engine || !mounted || !user?.organisationUnits?.[0]?.id) return;

        setLoadingFilteredObjectives(true);
        setLoadingHigherLevelObjectives(true);

        try {
            console.log('🔍 [LOADING MANAGER OBJECTIVES] For goal:', goalId);

            // First get ALL objectives for this org unit
            const allObjectivesResponse = await engine.query({
                allObjectives: {
                    resource: 'events',
                    params: {
                        program: 'yiT00XKrrHP', // Performance Management Monitoring Program
                        programStage: 'VIBtpvkpHFR', // Objectives stage
                        fields: 'event,dataValues[dataElement,value],createdBy',
                        orgUnit: user.organisationUnits[0].id,
                        ouMode: 'ACCESSIBLE',
                        pageSize: 500
                    }
                }
            });

            const allObjectives = allObjectivesResponse.allObjectives?.events || [];
            console.log('✅ [ALL OBJECTIVES LOADED]', allObjectives.length, 'total objectives');

            // 🔹 FIX: Get direct manager's position ID from current user's position
            const currentUserAttrs = extractAttributes(currentUserPosition.attributes || []);
            const directManagerPositionTei = currentUserAttrs['SjP47qi3JeB']; // REPORTING_TO UID

            console.log('🔍 [DIRECT MANAGER FILTER] Staff position:', currentUserPosition.trackedEntityInstance);
            console.log('🔍 [DIRECT MANAGER FILTER] Direct manager position TEI:', directManagerPositionTei);

            // 🔹 FIX: Enhanced filtering to include objectives whose parent is aligned to the goal
            // This handles the case where manager links to higher-level objective that IS aligned to a goal
            const managerObjectives = allObjectives.filter(obj => {
                const dataValuesMap = {};
                obj.dataValues?.forEach(dv => {
                    dataValuesMap[dv.dataElement] = dv.value;
                });

                const goalAlignmentValue = dataValuesMap['VhrIQQIVpB9']; // Direct goal alignment
                const objectivePositionId = dataValuesMap['SlgO60cMK3F']; // Position ID
                const parentObjectiveId = dataValuesMap['neuilg3L4d2'] || dataValuesMap['plJovUlxfQ0']; // Parent objective

                // Check if this objective belongs to the direct manager
                const matchesManager = objectivePositionId === directManagerPositionTei;

                // Check if directly aligned to the goal
                const matchesGoalDirectly = goalAlignmentValue === goalId;

                // Check if parent is aligned to goal
                let matchesGoalViaParent = false;

                if (!matchesGoalDirectly && parentObjectiveId) {
                    // Find the parent objective in allObjectives
                    const parentObj = allObjectives.find(p => p.event === parentObjectiveId);
                    if (parentObj) {
                        const parentDvMap = {};
                        parentObj.dataValues?.forEach(dv => {
                            parentDvMap[dv.dataElement] = dv.value;
                        });
                        const parentGoalAlignment = parentDvMap['VhrIQQIVpB9'];
                        matchesGoalViaParent = parentGoalAlignment === goalId;

                        console.log('🔗 [PARENT CHAIN CHECK]', {
                            objectiveId: obj.event,
                            parentId: parentObjectiveId,
                            parentGoalAlignment: parentGoalAlignment,
                            targetGoal: goalId,
                            matchesViaParent: matchesGoalViaParent
                        });
                    }
                }

                const matchesGoal = matchesGoalDirectly || matchesGoalViaParent;

                // Debug logging
                if (matchesGoal && matchesManager) {
                    console.log('✅ [FILTER DEBUG] Objective included:', {
                        objectiveId: obj.event,
                        name: dataValuesMap['EuN5ztFnXsQ'],
                        matchesVia: matchesGoalDirectly ? 'direct goal' : 'parent chain'
                    });
                }

                return matchesGoal && matchesManager;
            });


            console.log('✅ [MANAGER OBJECTIVES FILTERED]', managerObjectives.length, 'objectives from direct manager');

            // If no objectives found from direct manager, log a warning
            if (managerObjectives.length === 0) {
                console.warn('⚠️ [DIRECT MANAGER OBJECTIVES] No objectives found for direct manager position:', directManagerPositionTei);
                console.warn('⚠️ [DIRECT MANAGER OBJECTIVES] This means either:');
                console.warn('   1. The direct manager has not created objectives aligned to this goal');
                console.warn('   2. The reporting hierarchy is not set up correctly');
                console.warn('   3. The position TEI in REPORTING_TO attribute is incorrect');
            }

            // 🔹 STEP 1: Extract position IDs from objectives
            const positionIds = managerObjectives
                .map(obj => {
                    const dataValuesMap = {};
                    obj.dataValues?.forEach(dv => {
                        dataValuesMap[dv.dataElement] = dv.value;
                    });
                    return dataValuesMap['SlgO60cMK3F']; // Position ID data element
                })
                .filter(id => id && typeof id === 'string');

            console.log('🔹 [POSITION IDS TO RESOLVE]', positionIds);

            // 🔹 STEP 2: Resolve manager names using position hierarchy
            const managerNameMap = await resolvePositionManagerNames(positionIds);

            // 🔹 STEP 3: Format objectives with resolved manager names        
            const formattedObjectives = await Promise.all(
                managerObjectives.map(async (obj) => {
                    const dataValuesMap = {};
                    obj.dataValues?.forEach(dv => {
                        dataValuesMap[dv.dataElement] = dv.value;
                    });

                    // Get position ID for this objective
                    const positionId = dataValuesMap['SlgO60cMK3F'];

                    // 🔹 FIX: Get creator name with proper resolution
                    let managerName = 'Unknown Manager';

                    // Use the resolved manager name from position mapping
                    if (positionId && managerNameMap[positionId]) {
                        managerName = managerNameMap[positionId];
                        console.log('👤 [MANAGER NAME FROM POSITION]', {
                            positionId,
                            resolvedName: managerName,
                            objectiveId: obj.event
                        });
                    } else {
                        // Fallback: Check if storedBy is available (original creator)
                        if (obj.storedBy) {
                            try {
                                // Try to get creator details from storedBy
                                const userResponse = await engine.query({
                                    creatorUser: {
                                        resource: `users/${obj.storedBy}`,
                                        params: {
                                            fields: 'displayName,firstName,surname'
                                        }
                                    }
                                });

                                if (userResponse.creatorUser) {
                                    managerName = userResponse.creatorUser.displayName?.trim() ||
                                        userResponse.creatorUser.name?.trim() ||
                                        obj.storedBy;
                                } else {
                                    managerName = obj.storedBy;
                                }
                            } catch (error) {
                                console.warn('⚠️ Could not fetch creator details:', error);
                                managerName = obj.storedBy;
                            }
                        }
                    }

                    // Get goal info from the filtered objectives (which includes inherited goals)
                    const directGoalId = dataValuesMap['VhrIQQIVpB9'] || null;
                    const parentObjId = dataValuesMap['neuilg3L4d2'] || dataValuesMap['plJovUlxfQ0'] || null;

                    return {
                        id: obj.event,
                        name: dataValuesMap['EuN5ztFnXsQ'] || 'Unnamed Objective',
                        description: dataValuesMap['nWqW5BrcgP1'] || '',
                        createdBy: managerName, // ✅ RESOLVED MANAGER/CREATOR NAME
                        userRole: dataValuesMap['vFoMLF5eZ5X'] || '',
                        objectiveRoleAssigned: dataValuesMap['vFoMLF5eZ5X'] || '',
                        positionId: positionId || '',
                        dataValues: obj.dataValues || [],
                        storedBy: obj.storedBy, // Keep original storedBy for reference
                        dateCreated: obj.created || new Date().toISOString(),
                        // Include goal info (direct or inherited)
                        directGoalId: directGoalId,
                        parentObjectiveId: parentObjId,
                        _managerGoalName: goals.find(g => g.id === goalId)?.name || 'Unknown Goal',
                        _goalId: goalId, // The goal this objective is aligned to (direct or via parent)
                        progress: parseInt(dataValuesMap['LEEqJmI9Lyi']) || 0
                    };
                })
            );

            console.log('✅ [FORMATTED OBJECTIVES WITH MANAGER NAMES]', formattedObjectives);

            // ✅ CRITICAL: After loading manager objectives, check if editing objective is aligned to one
            if (editingObjective?.objectiveAlignment && formattedObjectives.length > 0) {
                const alignedObjective = formattedObjectives.find(
                    obj => obj.id === editingObjective.objectiveAlignment
                );

                if (alignedObjective && !selectedHigherLevelObjective?.id) {
                    console.log('✅ [EDIT MODE] Found aligned objective in manager list:', alignedObjective.name);
                    setSelectedHigherLevelObjective(alignedObjective);

                    // Update form fields
                    updateObjectiveField(0, 'objectiveAlignment', alignedObjective.id);
                    updateObjectiveField(0, 'higherLevelObjectiveId', alignedObjective.id);
                    updateObjectiveField(0, 'higherLevelObjectiveName', alignedObjective.name);

                    // Set goal context
                    const managerGoalId = alignedObjective.dataValues?.find(
                        dv => dv.dataElement === 'VhrIQQIVpB9'
                    )?.value;

                    if (managerGoalId && goals.length > 0) {
                        const managerGoal = goals.find(g => g.id === managerGoalId);
                        if (managerGoal) {
                            updateObjectiveField(0, '_managerGoalName', managerGoal.name);
                        }
                    }
                }
            }

            setFilteredManagerObjectives(formattedObjectives);

        } catch (error) {
            console.error('❌ Error loading manager objectives for goal:', error);
            setFilteredManagerObjectives([]);

            let errorMessage = 'Failed to load manager objectives for this goal';
            showAlert(errorMessage, 'error');
        } finally {
            if (mounted) {
                setLoadingFilteredObjectives(false);
                setLoadingHigherLevelObjectives(false);
            }
        }
    };

    // Clear goal selection
    const handleClearGoalSelection = () => {
        setSelectedGoal('');
        setFilteredManagerObjectives([]);
        setSelectedHigherLevelObjective(null);
    };


    // Create memoized mapping of goal ID to name
    const goalNameById = useMemo(() => {
        const map = {};
        goalItems.forEach(goal => {
            map[goal.id] = goal.name;
        });
        return map;
    }, [goalItems]);

    
    
    // Helper function to check if current user is the objective creator
    const isObjectiveCreator = () => {
        if (!editingObjective || !user || !currentUserPosition) return false;

        // Check if current user's position TEI matches the objective's position TEI
        const currentPositionTei = currentUserPosition?.trackedEntityInstance;
        const objectivePositionTei = editingObjective.positionTeiId ||
            editingObjective.positionTrackedEntityInstance;

        return currentPositionTei === objectivePositionTei;
    };

    // State to store manager status
    const [isUserDirectManager, setIsUserDirectManager] = useState(false);
    const [permissionsCache, setPermissionsCache] = useState({
        isCreator: false,
        isManager: false,
        lastChecked: null,
        objectiveId: null
    });

    const [currentStep, setCurrentStep] = useState('objective');

    const {
        currentOrgUnit,
        handleSaveObjective,
        setUiMode,
        editingObjective: contextEditingObjective,
        handleUpdateObjective,
        selectedHigherLevelObjective: contextSelectedHigherLevelObjective,
        setSelectedHigherLevelObjective: contextSetSelectedHigherLevelObjective,
        loadHigherLevelObjectiveDetails,
        uiMode,
        refreshObjectives
    } = usePerformanceContext();


    const selectedHigherLevelObjective = React.useMemo(() => {
        // If we have the prop, use it
        if (propSelectedHigherLevelObjective !== undefined) {
            return propSelectedHigherLevelObjective;
        }

        // If context has just an ID string, we need to find the full object
        if (typeof contextSelectedHigherLevelObjective === 'string') {
            // Try to find it in higherLevelObjectives first
            const foundInList = higherLevelObjectives.find(
                obj => obj.id === contextSelectedHigherLevelObjective
            );

            if (foundInList) {
                console.log('✅ [OBJECTIVE RESOLUTION] Found full object from ID:', foundInList.name);
                return foundInList;
            }

            // Try to find it in filteredManagerObjectives
            const foundInFiltered = filteredManagerObjectives.find(
                obj => obj.id === contextSelectedHigherLevelObjective
            );

            if (foundInFiltered) {
                console.log('✅ [OBJECTIVE RESOLUTION] Found in filtered list:', foundInFiltered.name);
                return foundInFiltered;
            }

            // If not found, return a placeholder with the ID
            console.log('🟡 [OBJECTIVE RESOLUTION] Creating placeholder for ID:', contextSelectedHigherLevelObjective);
            return {
                id: contextSelectedHigherLevelObjective,
                name: `Objective ${contextSelectedHigherLevelObjective.substring(0, 8)}...`,
                description: 'Loading details...',
                isLoading: true
            };
        }

        // Otherwise, return whatever context has
        return contextSelectedHigherLevelObjective;
    }, [propSelectedHigherLevelObjective, contextSelectedHigherLevelObjective, higherLevelObjectives, filteredManagerObjectives]);

    const setSelectedHigherLevelObjective = propSetSelectedHigherLevelObjective !== undefined
        ? propSetSelectedHigherLevelObjective
        : contextSetSelectedHigherLevelObjective;

    const editingObjective = objective !== undefined
        ? objective
        : contextEditingObjective;


    const [objectives, setObjectives] = useState([
        {
            id: null,
            _tempId: `temp_${Date.now()}_0`,
            name: '',
            description: '',
            weight: 0, 
            startDate: '',                     
            completionDate: '',
            orgUnit: user?.organisationUnits?.[0]?.id || '',
            remarks: '',
            score: 0,
            performanceYear: new Date().getFullYear(),
            workType: '',
            companyAlignedGoal: '',
            kras: [{ name: '', description: '', weight: 0 }],
            kpis: [{ name: '', description: '', target: '' }],
        }
    ]);

    const isEditMode = mode === 'edit' && (objectives.some(obj => obj.id) || weightGroupEnrollment);

    const [submissionError, setSubmissionError] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);


    const currentObjectiveIndex = objectives.length - 1;
    const currentObjective = objectives[currentObjectiveIndex];

    const GOAL_DATAELEMENT_MAPPING = {
        'HJqqfzhH614': 'name',
        'zS8QIgZc0Ll': 'description',
        'pOOGDRzEo98': 'progress',
        'MH7DQVegD1j': 'startDate',
        'pUvJQ9vy3A3': 'endDate',
        'Co2i5BleCcZ': 'priority',
        'uBPW95V2VoX': 'category',
        'ir4nG6zZn4w': 'createdBy',
        'VhrIQQIVpB9': 'companyAlignedGoal'
    };

    const handleEditObjectiveWeightChange = (index, newWeight) => {
        const currentTotal = objectives.reduce((sum, o) => sum + getObjectiveWeight(o), 0);
        const currentWeight = getObjectiveWeight(objectives[index]);
        const difference = newWeight - currentWeight;

        // Allow exceeding 100% temporarily - validation happens at save time
        // Users can exceed 100% and adjust before saving
        if (newWeight < 1) {
            showAlert('Minimum Weight', 'warning', 'Weight cannot be less than 1%');
            return;
        }

        // Update the specific objective
        setObjectives(prev => prev.map((obj, i) =>
            i === index ? { ...obj, weight: newWeight } : obj
        ));
    };

    // Add this function to remove an objective in edit mode
    const handleRemoveObjectiveInEditMode = (index) => {
        if (objectives.length <= 1) {
            showAlert('Cannot Remove', 'warning', 'At least one objective is required');
            return;
        }

        const removedWeight = getObjectiveWeight(objectives[index]);
        const remainingWeight = objectives.reduce((sum, o, i) =>
            i !== index ? sum + getObjectiveWeight(o) : sum
            , 0);

        if (remainingWeight === 0) {
            showAlert('Cannot Remove', 'warning', 'Cannot remove the only objective with weight');
            return;
        }

        if (window.confirm(`Remove "${objectives[index]?.name || 'this objective'}"? This will free up ${removedWeight}% weight.`)) {
            setObjectives(prev => prev.filter((_, i) => i !== index));
            setExpandedObjectiveIndex(null);
            showAlert('Objective Removed', 'success', `Freed up ${removedWeight}% weight`);
        }
    };

    // Add this function to add a new objective in edit mode
    const handleAddObjectiveInEditMode = () => {
        const currentTotal = objectives.reduce((sum, o) => sum + getObjectiveWeight(o), 0);
        const remaining = 100 - currentTotal;

        // Allow adding objectives even at 100% - validation will happen at save time
        // Users can exceed 100% temporarily and adjust before saving
        const defaultWeight = remaining > 0 ? Math.min(remaining, 25) : 25;

        setObjectives(prev => [
            ...prev,
            {
                id: null,
                _tempId: `temp_${Date.now()}_${prev.length}`,
                name: '',
                description: '',
                weight: defaultWeight,
                startDate: fiscalYear.start || '', 
                completionDate: fiscalYear.end || '',
                orgUnit: user?.organisationUnits?.[0]?.id || '',
                remarks: '',
                score: 0,
                performanceYear: new Date().getFullYear(),
                workType: '',
                companyAlignedGoal: '',
                kras: [],
                kpis: [{ name: '', description: '', target: '' }],
            }
        ]);

        // Auto-expand the new objective
        setExpandedObjectiveIndex(objectives.length);

        if (remaining > 0) {
            showAlert('New Objective Added', 'success', `You have ${remaining}% remaining to allocate`);
        } else {
            showAlert('New Objective Added', 'info', `Total weight is now ${currentTotal + defaultWeight}%. Please adjust other objectives to maintain 100%.`);
        }
    };
   
    // Add this function to check program access proactively
    const checkProgramAccessProactively = async () => {
        if (!user?.organisationUnits?.[0]?.id) {
            console.warn('⚠️ No user org unit found');
            return;
        }

        const userOrgUnitId = user.organisationUnits[0].id;

        try {
            console.log('🔍 [PROACTIVE CHECK] Checking program assignment for org unit:', userOrgUnitId);

            // Check program assignment
            const response = await engine.query({
                programAssignment: {
                    resource: `programs/yiT00XKrrHP`,
                    params: {
                        fields: 'id,name,organisationUnits[id,name]'
                    }
                }
            });

            const program = response.programAssignment;
            const assignedOrgUnits = program.organisationUnits || [];

            // Check if user's org unit has the program assigned
            const isAssigned = assignedOrgUnits.some(ou => ou.id === userOrgUnitId);

            console.log('🔍 [PROACTIVE CHECK] Result:', {
                programName: program.name,
                userOrgUnit: userOrgUnitId,
                isAssigned,
                assignedOrgUnits: assignedOrgUnits.map(ou => ({ id: ou.id, name: ou.name })),
                totalAssigned: assignedOrgUnits.length
            });

            setProgramAccess({
                isAssigned,
                checked: true,
                missingOrgUnit: isAssigned ? null : userOrgUnitId,
                assignedOrgUnits
            });

            // If not assigned, show immediate warning
            if (!isAssigned) {
                showProgramAccessWarning(userOrgUnitId, assignedOrgUnits);
            }

        } catch (error) {
            console.error('❌ [PROACTIVE CHECK] Error:', error);
            setProgramAccess({
                isAssigned: false,
                checked: true,
                missingOrgUnit: userOrgUnitId,
                assignedOrgUnits: [],
                error: error.message
            });
        }
    };

    const showProgramAccessWarning = (missingOrgUnitId, assignedOrgUnits = []) => {
        // Create a detailed warning message
        const availableUnits = assignedOrgUnits.map(ou => `• ${ou.name} (${ou.id})`).join('\n');

        showAlert(
            '⚠️ Program Configuration Required',
            'warning',
            `The Performance Management App is not assigned to your department.\n\n` +
            `Please contact your administrator to assign the performance App to your department.`
        );
    };

    <TotalWeightTracker
        currentWeight={sumObjectiveWeights(objectives)}
        isComplete={sumObjectiveWeights(objectives) === 100}
    />


    const showAlert = (message, type = 'error', details = '') => {
        // Check if component is still mounted before setting state
        setNotification({
            message: String(message),
            type,
            details: String(details)
        });

        if (type !== 'error') {
            setTimeout(() => {
                setNotification(null);
            }, 10000);
        }
    };

    // Add this function to clear notifications
    const clearNotification = () => {
        setNotification(null);
    };

    const updateObjectiveField = useCallback((index, field, value) => {
        setObjectives(prev => {
            const newComps = [...prev];
            newComps[index] = {
                ...newComps[index],
                [field]: value
            };
            return newComps;
        });
    }, []);

    // KRA Management functions
    const handleKraChange = (objIndex, kraIndex, field, value) => {
        setObjectives(prev => prev.map((obj, i) => {
            if (i === objIndex) {
                const updatedKras = [...(obj.kras || [])];
                updatedKras[kraIndex] = {
                    ...updatedKras[kraIndex],
                    [field]: value,
                    // Preserve the ID if it exists
                    ...(updatedKras[kraIndex].id && { id: updatedKras[kraIndex].id })
                };
                return { ...obj, kras: updatedKras };
            }
            return obj;
        }));
    };

    const handleAddKra = (objIndex) => {
        setObjectives(prev => prev.map((obj, i) =>
            i === objIndex
                ? { ...obj, kras: [...(obj.kras || []), { name: '', description: '', weight: 0 }] }
                : obj
        ));
    };

    const handleRemoveKra = (objIndex, kraIndex) => {
        setObjectives(prev => prev.map((obj, i) => {
            if (i === objIndex && (obj.kras || []).length > 1) {
                const updatedKras = [...(obj.kras || [])];
                updatedKras.splice(kraIndex, 1);
                return { ...obj, kras: updatedKras };
            }
            return obj;
        }));
    };

    // KPI Management functions
    const handleKpiChange = (objIndex, kpiIndex, field, value) => {
        setObjectives(prev => prev.map((obj, i) => {
            if (i === objIndex) {
                const updatedKpis = [...(obj.kpis || [])];
                updatedKpis[kpiIndex] = {
                    ...updatedKpis[kpiIndex],
                    [field]: value,
                    // Preserve the ID if it exists
                    ...(updatedKpis[kpiIndex].id && { id: updatedKpis[kpiIndex].id })
                };
                return { ...obj, kpis: updatedKpis };
            }
            return obj;
        }));
    };

    const handleAddKpi = (objIndex) => {
        setObjectives(prev => prev.map((obj, i) =>
            i === objIndex
                ? { ...obj, kpis: [...(obj.kpis || []), { name: '', description: '', target: '' }] }
                : obj
        ));
    };

    const handleRemoveKpi = (objIndex, kpiIndex) => {
        setObjectives(prev => prev.map((obj, i) => {
            if (i === objIndex && (obj.kpis || []).length > 1) {
                const updatedKpis = [...(obj.kpis || [])];
                updatedKpis.splice(kpiIndex, 1);
                return { ...obj, kpis: updatedKpis };
            }
            return obj;
        }));
    };

    const {
        higherLevelObjectives: contextHigherLevelObjectives,
        loadingHigherLevel
    } = usePerformanceContext();

    useEffect(() => {
    // Auto-expand first objective with remarks
        const indexWithRemarks = objectives.findIndex(obj => obj.remarks);
        if (indexWithRemarks !== -1 && expandedObjectiveIndex === null) {
            setExpandedObjectiveIndex(indexWithRemarks);
        }
    }, [objectives, expandedObjectiveIndex]);

    useEffect(() => {
        if (isInitialLoad && editingObjective && !loadingGoals && goals.length > 0) {
            // Let the consolidated effect run once
            setTimeout(() => setIsInitialLoad(false), 100);
        }
    }, [editingObjective, loadingGoals, goals.length]);

    useEffect(() => {
        const beforeUnload = (e) => {
            const total = objectives.reduce((sum, o) => sum + getObjectiveWeight(o), 0);
            if (total > 0 && total !== 100) {
                e.preventDefault();
                e.returnValue = 'You have unsaved objectives with incomplete weight allocation (not 100%). Are you sure?';
            }
        };
        window.addEventListener('beforeunload', beforeUnload);
        return () => window.removeEventListener('beforeunload', beforeUnload);
    }, [objectives]);

    useEffect(() => {
        if (contextHigherLevelObjectives && contextHigherLevelObjectives.length > 0) {
            setHigherLevelObjectives(contextHigherLevelObjectives);
        }
    }, [contextHigherLevelObjectives]);

useEffect(() => {
    // LOAD FISCAL YEAR FROM PROGRAM EVENT METADATA
    const loadFiscalYear = async () => {
        const orgUnitId = user?.organisationUnits?.[0]?.id;
        if (!orgUnitId || !engine) {
            console.warn('⚠️ Cannot load fiscal year: missing orgUnit or engine');
            return;
        }
        
        try {
            console.log('🔍 [FISCAL YEAR] Loading for orgUnit:', orgUnitId);
            
            // ✅ SIMPLIFIED: Query events directly without TEI lookup first
            const eventsResponse = await engine.query({
                eventsQuery: {
                    resource: 'events',
                    params: {
                        program: 'NkReudUAW4F',
                        programStage: 'htIWgNQWEMm',
                        orgUnit: orgUnitId,  // Use orgUnit instead of ou for events query
                        ouMode: 'ACCESSIBLE',
                        pageSize: 1,
                        order: 'eventDate:desc',
                        fields: 'event,dataValues[dataElement,value]'
                    }
                }
            });
            
            const event = eventsResponse.eventsQuery?.events?.[0];
            
            if (!event) {
                console.warn('⚠️ [FISCAL YEAR] No metadata event found for orgUnit:', orgUnitId);
                return;
            }
            
            console.log('✅ [FISCAL YEAR] Found event:', event.event);
            
            // ✅ Extract dates using correct data element IDs
            let start = '', end = '';
            event.dataValues?.forEach(dv => {
                if (dv.dataElement === 'U55MqwBcRAe') {  // FIN_START
                    start = dv.value;
                    console.log('📅 [FISCAL YEAR] Start date found:', start);
                }
                if (dv.dataElement === 'kgdk4NXu4f5') {  // FIN_END
                    end = dv.value;
                    console.log('📅 [FISCAL YEAR] End date found:', end);
                }
            });
            
            if (start && end) {
                setFiscalYear({ start, end });
                console.log('✅ [FISCAL YEAR] Loaded successfully:', { start, end });
            } else {
                    console.warn('⚠️ [FISCAL YEAR] Event found but missing date values', {
                        hasStart: !!start,
                        hasEnd: !!end,
                        dataValues: event.dataValues
                    });
                }
            } catch (error) {
                console.error('❌ [FISCAL YEAR] Failed to load:', error.message);
                console.error('Full error:', error);
            }
        };
        
        loadFiscalYear();
    }, [user?.organisationUnits?.[0]?.id, engine]); // ✅ Fixed dependency array

    useEffect(() => {
        return () => {
            setMounted(false);
            submissionInProgress.current = false;
        };
    }, []);

    // Refs to prevent useEffect from re-running during typing
    const hasRunRef = useRef(false);
    const lastLoadedObjectiveIdRef = useRef(null);
    const isMountedRef = useRef(false);
    const hasLoadedAddGroupRef = useRef(false);

    useEffect(() => {
        if (mode === 'add') {
            hasLoadedAddGroupRef.current = false;
        }
    }, [mode, currentUserPosition?.trackedEntityInstance]);

    useEffect(() => {
        // Reset only when the objective id actually changes.
        if (objective?.id && lastLoadedObjectiveIdRef.current !== objective.id) {
            lastLoadedObjectiveIdRef.current = objective.id;
            hasRunRef.current = false;
        }
        isMountedRef.current = true;

        const loadExistingData = async () => {
            // Wait for position to be loaded before processing
            if (!currentUserPosition?.trackedEntityInstance) {
                console.log('🎯 [LOAD EXISTING] Waiting for position to be loaded...');
                return; // Will re-run when position changes
            }

            if (!objective || !objective.id || !isMountedRef.current || hasRunRef.current) return;
            hasRunRef.current = true;

            try {
                setUiMode('edit');

                // ✅ STEP 1: FIRST get enrollment ID from the specific objective
                const eventResponse = await engine.query({
                    event: {
                        resource: `events/${objective.id}`,
                        params: { fields: 'event,enrollment,dataValues[dataElement,value]' }
                    }
                });

                const enrollmentId = eventResponse.event?.enrollment;
                console.log('🎯 [EDIT MODE] Objective enrollment ID:', enrollmentId);

                // ✅ STEP 2: LOAD ENTIRE WEIGHT GROUP IF ENROLLMENT EXISTS
                let groupObjectives = [];
                if (enrollmentId) {
                    groupObjectives = await loadObjectivesInEnrollment(engine, enrollmentId);
                    setWeightGroupEnrollment(enrollmentId); // Track for saving later
                }

                // ✅ STEP 3: PROCESS OBJECTIVES (use group if available, else single)
                const objectivesToSet = groupObjectives.length > 0
                    ? groupObjectives
                    : [await fetchObjectiveWithDetails(objective.id, engine).objective];

                // Map to form state format (critical: preserve all fields)
                const mappedObjectives = objectivesToSet.map(obj => {
                    const managerGoalId = obj.rawDataValues?.find(dv => dv.dataElement === 'VhrIQQIVpB9')?.value;
                    const managerGoalName = managerGoalId ? goalNameById[managerGoalId] : null;

                    return {
                        id: obj.id || null,
                        name: obj.name || '',
                        _managerGoalName: managerGoalName,
                        description: obj.description || '',
                        // ✅ CRITICAL: Use CORRECT weight DE value
                        weight: obj.weight || parseInt(obj.rawDataValues?.find(dv => dv.dataElement === 'yfb9RCRpMB2')?.value) || 0,
                        startDate: obj.startDate || fiscalYear.start,
                        completionDate: obj.completionDate || fiscalYear.end,
                        orgUnit: obj.orgUnit || user?.organisationUnits?.[0]?.id || '',
                        remarks: obj.remarks || '',
                        score: obj.score || 0,
                        performanceYear: obj.performanceYear || new Date().getFullYear(),
                        workType: obj.workType || '',
                        companyAlignedGoal: obj.companyAlignedGoal || obj.rawDataValues?.find(dv => dv.dataElement === 'VhrIQQIVpB9')?.value || '',
                        objectiveAlignment: obj.objectiveAlignment || obj.rawDataValues?.find(dv => dv.dataElement === 'neuilg3L4d2')?.value || '',
                        objectiveRoleAssigned: obj.objectiveRoleAssigned || '',


                        
                       // In loadExistingData, update the KRA mapping:
                    kras: obj.kras?.length > 0 
                        ? obj.kras.map(kra => {
                            let kraName = '';
                            let kraDescription = '';
                            let kraWeight = 0;
                            
                            if (kra.dataValues && Array.isArray(kra.dataValues)) {
                                kra.dataValues.forEach(dv => {
                                    if (dv.dataElement === 'obapz6Gnyla') kraName = dv.value || '';
                                    if (dv.dataElement === 'DEECWPTj42E') kraDescription = dv.value || '';
                                    if (dv.dataElement === 'Xl4VEFoIFIw') kraWeight = parseInt(dv.value) || 0;
                                });
                            } else {
                                kraName = kra.name || '';
                                kraDescription = kra.description || '';
                                kraWeight = kra.weight || 0;
                            }
                            
                            return {
                                id: kra.id || kra.eventId || kra.event || null,
                                name: String(kraName || '').trim(),
                                description: String(kraDescription || '').trim(),
                                weight: kraWeight,
                                // ❌ Don't include objectiveAlignment here
                                dataValues: kra.dataValues || [],
                            };
                        })
                        : [],

                    // Update the KPI mapping:
                    kpis: obj.kpis?.length > 0 
                        ? obj.kpis.map(kpi => {
                            let kpiName = '';
                            let kpiDescription = '';
                            let kpiTarget = '';
                            
                            if (kpi.dataValues && Array.isArray(kpi.dataValues)) {
                                kpi.dataValues.forEach(dv => {
                                    if (dv.dataElement === 'DBmylDQA9KX') kpiName = dv.value || '';
                                    if (dv.dataElement === 'rQqF75V9t7K') kpiDescription = dv.value || '';
                                    if (dv.dataElement === 'Ru68Ax2Xo2L') kpiTarget = dv.value || '';
                                });
                            } else {
                                kpiName = kpi.name || '';
                                kpiDescription = kpi.description || '';
                                kpiTarget = kpi.target || '';
                            }
                            
                            return {
                                id: kpi.id || kpi.eventId || kpi.event || null,
                                name: String(kpiName || '').trim(),
                                description: String(kpiDescription || '').trim(),
                                target: String(kpiTarget || '').trim(),
                                // ❌ Don't include objectiveAlignment here
                                dataValues: kpi.dataValues || [],
                            };
                        })
                        : [],
                    };
                });

                setObjectives(prev => mappedObjectives.map(mappedObj => {
                    const existing = prev.find(p => p.id === mappedObj.id);
                    const hasExistingDetailedKpis = existing?.kpis?.some(k =>
                        k?.name?.trim() || k?.target?.trim() || k?.description?.trim()
                    );
                    const hasExistingDetailedKras = existing?.kras?.some(k =>
                        k?.name?.trim() || k?.description?.trim() || (Number(k?.weight) || 0) > 0
                    );

                    if (existing && (hasExistingDetailedKpis || hasExistingDetailedKras)) {
                        return {
                            ...mappedObj,
                            kpis: existing.kpis,
                            kras: existing.kras
                        };
                    }

                    return mappedObj;
                }));

                // ✅ DEBUG: Verify weights are loaded correctly===========================
                console.log('🔍 [WEIGHT LOAD DEBUG] Objectives after mapping:', 
                    mappedObjectives.map(o => ({
                        id: o.id?.substring(0,8),
                        name: o.name?.substring(0,20),
                        weight: o.weight,
                        weightType: typeof o.weight,
                        rawData: o.rawDataValues?.find(dv => dv.dataElement === 'yfb9RCRpMB2')?.value
                    }))
                );
                const loadedTotal = mappedObjectives.reduce((sum, o) => sum + getObjectiveWeight(o), 0);
                console.log(`✅ [WEIGHT LOAD DEBUG] Total loaded weight: ${loadedTotal}%`);
                 // ✅ DEBUG: Verify weights are loaded correctly===========================
                

                // ✅ STEP 4: Restore alignment context for FIRST objective (UI consistency)
                const firstObjective = mappedObjectives[0];
                if (firstObjective?.objectiveAlignment && !selectedHigherLevelObjective?.id) {
                    try {
                        const alignedObj = await loadHigherLevelObjectiveDetails(firstObjective.objectiveAlignment);
                        if (isMountedRef.current && alignedObj) {
                            setSelectedHigherLevelObjective(alignedObj);
                            // Update form fields for alignment context
                            updateObjectiveField(0, 'objectiveAlignment', alignedObj.id);
                            updateObjectiveField(0, 'objectiveRoleAssigned', firstObjective.objectiveRoleAssigned);
                            updateObjectiveField(0, 'higherLevelObjectiveId', alignedObj.id);
                            updateObjectiveField(0, 'higherLevelObjectiveName', alignedObj.name);

                            // Set manager goal name if available
                            const alignedGoalId = alignedObj.dataValues?.find(dv => dv.dataElement === 'VhrIQQIVpB9')?.value;
                            if (alignedGoalId && goals.length > 0) {
                                const alignedGoal = goals.find(g => g.id === alignedGoalId);
                                if (alignedGoal) {
                                    setObjectives(prev => prev.map(o => ({ ...o, _managerGoalName: alignedGoal.name })));
                                }
                            }
                        }
                    } catch (err) {
                        console.warn('⚠️ Could not restore alignment context:', err);
                    }
                }

                // Handle CEO goal linkage
                if (user?.roleIds?.includes('nLSSLch316I') && firstObjective?.companyAlignedGoal) {
                    console.log('🎯 [CEO] Restoring goal linkage:', firstObjective.companyAlignedGoal);
                    setSelectedGoal(firstObjective.companyAlignedGoal);
                }

            } catch (error) {
                console.error('❌ [LOAD EXISTING] Error:', error);
                if (isMountedRef.current) {
                    showAlert('Failed to load objective data', 'error', error.message);
                }
            }
        };

        loadExistingData();
        return () => { isMountedRef.current = false; };
    }, [objective, engine, user, currentUserPosition]);

    // Add this useEffect near your other effects
    useEffect(() => {
        if (user?.organisationUnits?.[0]?.id && !programAccess.checked) {
            checkProgramAccessProactively();
        }
    }, [user, programAccess.checked]);

    // Effect to check manager permissions when editing objective
    useEffect(() => {
        const checkManagerStatus = async () => {
            if (mode === 'edit' && editingObjective && currentUserPosition) {
                // Check if we have cached permissions for this objective
                if (permissionsCache.objectiveId === editingObjective.id &&
                    permissionsCache.lastChecked &&
                    (Date.now() - permissionsCache.lastChecked) < 30000) {
                    setIsUserDirectManager(permissionsCache.isManager);
                    return;
                }

                try {
                    // Get the objective creator's position TEI
                    const objectivePositionTei = editingObjective.positionTeiId ||
                        editingObjective.positionTrackedEntityInstance;

                    if (!objectivePositionTei) {
                        setIsUserDirectManager(false);
                        return;
                    }

                    // Fetch the objective creator's position details
                    const response = await engine.query({
                        creatorPosition: {
                            resource: 'trackedEntityInstances',
                            id: objectivePositionTei,
                            params: {
                                fields: 'attributes[attribute,value]'
                            }
                        }
                    });

                    // Extract the Reporting To attribute
                    const attributes = extractAttributes(response.creatorPosition.attributes || []);
                    const reportingToTei = attributes['SjP47qi3JeB']; // Reporting To TEI

                    // Check if current user's position TEI matches the Reporting To
                    const currentUserPositionTei = currentUserPosition?.trackedEntityInstance;
                    const isManager = currentUserPositionTei === reportingToTei;
                    const isCreator = isObjectiveCreator();

                    // Update cache
                    setPermissionsCache({
                        isCreator,
                        isManager,
                        lastChecked: Date.now(),
                        objectiveId: editingObjective.id
                    });

                    setIsUserDirectManager(isManager);

                } catch (error) {
                    console.error('Error checking manager status:', error);
                    setIsUserDirectManager(false);
                }
            }
        };

        checkManagerStatus();
    }, [mode, editingObjective, currentUserPosition, engine]);

    // Add this NEW useEffect to ObjectiveForm.jsx (don't remove the manager status one)
    useEffect(() => {
        const loadGroupObjectives = async () => {
            if (mode === 'add' && currentUserPosition?.trackedEntityInstance) {
                if (hasLoadedAddGroupRef.current) return;

                const isInitialBlankState =
                    objectives.length === 1 &&
                    !objectives[0]?.id &&
                    !objectives[0]?.name?.trim() &&
                    getObjectiveWeight(objectives[0]) === 0;

                if (!isInitialBlankState) return;

                try {
                    console.log(`🎯 [ADD MODE GROUP LOAD] Loading existing objectives for TEI: ${currentUserPosition.trackedEntityInstance}`);

                    const enrollmentResponse = await engine.query({
                        teiEnrollments: {
                            resource: 'enrollments',
                            params: {
                                trackedEntityInstance: currentUserPosition.trackedEntityInstance,
                                program: 'yiT00XKrrHP',
                                status: 'ACTIVE',
                                fields: 'enrollment,status',
                                ouMode: 'ACCESSIBLE'
                            }
                        }
                    });

                    const enrollments = enrollmentResponse.teiEnrollments?.enrollments || [];
                    const activeEnrollmentId = enrollments[0]?.enrollment;

                    if (!activeEnrollmentId) {
                        hasLoadedAddGroupRef.current = true;
                        console.log('🟡 [ADD MODE GROUP LOAD] No active enrollment found yet.');
                        return;
                    }

                    const groupedObjectives = await loadObjectivesInEnrollment(engine, activeEnrollmentId);
                    setWeightGroupEnrollment(activeEnrollmentId);

                    if (groupedObjectives.length > 0) {
                        console.log(`✅ [ADD MODE GROUP LOAD] Hydrated ${groupedObjectives.length} objectives from enrollment.`);
                        setObjectives([...groupedObjectives, createBlankObjective(groupedObjectives.length)]);
                    } else {
                        console.log('🟡 [ADD MODE GROUP LOAD] Enrollment found, but no objectives to hydrate.');
                    }

                    hasLoadedAddGroupRef.current = true;
                } catch (error) {
                    hasLoadedAddGroupRef.current = true;
                    console.error('❌ [ADD MODE GROUP LOAD] Failed to hydrate objectives:', error);
                }
            }

            if (mode === 'edit' && editingObjective?.id && currentUserPosition?.trackedEntityInstance) {
                console.log(`🎯 [EDIT MODE GROUP LOAD] Loading group for editing objective: ${editingObjective.id}`);
                console.log(`🔍 [LOAD GROUP] User TEI: ${currentUserPosition.trackedEntityInstance}`);

                const hasDetailedData = objectives.some(obj =>
                    obj.kpis?.some(k =>
                        k?.name?.trim() || k?.target?.trim() || k?.description?.trim()
                    ) ||
                    obj.kras?.some(k =>
                        k?.name?.trim() || k?.description?.trim() || (Number(k?.weight) || 0) > 0
                    )
                );
                    
                if (hasDetailedData) {
                        console.log('🟡 [GROUP LOAD] Skipping - objectives already have KPIs/KRAs');
                        return;
                 }

                // Objectives program ID - defined at function level to be accessible in catch block
                const objectivesProgramId = 'yiT00XKrrHP';

                try {
                    // Step 1: Get the current user's enrollments to find a VALID enrollment
                    console.log(`🔍 [LOAD GROUP] Fetching TEI enrollments for: ${currentUserPosition.trackedEntityInstance}`);

                    const teiResponse = await engine.query({
                        teiEnrollments: {
                            resource: 'trackedEntityInstances',
                            id: currentUserPosition.trackedEntityInstance,
                            params: {
                                fields: 'enrollments[enrollment,program,orgUnit,status]'
                            }
                        }
                    });

                    const enrollments = teiResponse.teiEnrollments?.enrollments || [];
                    console.log(`✅ [LOAD GROUP] Found ${enrollments.length} enrollments for TEI`);

                    // Debug: log all enrollments
                    enrollments.forEach((e, i) => {
                        console.log(`   Enrollment ${i}: enrollment=${e.enrollment}, program=${e.program}, status=${e.status}, orgUnit=${e.orgUnit}`);
                    });

                    // Find a valid enrollment for the objectives program
                    let validEnrollment = enrollments.find(e =>
                        e.program === objectivesProgramId &&
                        e.status === 'ACTIVE'
                    );

                    // Fallback: use any active enrollment if objectives program not found
                    if (!validEnrollment) {
                        validEnrollment = enrollments.find(e => e.status === 'ACTIVE');
                    }

                    let enrollmentEvents = [];

                    if (validEnrollment) {
                        const validEnrollmentId = validEnrollment.enrollment;
                        console.log(`✅ [LOAD GROUP] Using valid enrollment: ${validEnrollmentId} (orgUnit: ${validEnrollment.orgUnit})`);

                        // Step 2: Load events from the enrollment
                        const response = await engine.query({
                            enrollmentEvents: {
                                resource: 'enrollments',
                                id: validEnrollmentId,
                                params: {
                                    fields: 'events[event,orgUnit,dataValues[dataElement,value]]'
                                }
                            }
                        });

                        enrollmentEvents = response.enrollmentEvents?.events || [];
                        console.log(`✅ Loaded ${enrollmentEvents.length} events from enrollment`);

                        // Update state with valid enrollment ID
                        setWeightGroupEnrollment(validEnrollmentId);
                    } else {
                        // No valid enrollment - fallback to loading events directly by program and orgUnit
                        console.log('🔄 [LOAD GROUP] No valid enrollment found, using program-based query...');

                        const fallbackResponse = await engine.query({
                            fallbackObjectives: {
                                resource: 'events',
                                params: {
                                    filter: `program:eq:${objectivesProgramId},orgUnit:eq:${user?.organisationUnits?.[0]?.id}`,
                                    fields: 'event,orgUnit,dataValues[dataElement,value],enrollment'
                                }
                            }
                        });

                        enrollmentEvents = fallbackResponse.fallbackObjectives?.events || [];
                        console.log(`✅ Fallback loaded ${enrollmentEvents.length} events`);
                    }

                    // Debug: log events
                    if (enrollmentEvents.length > 0) {
                        console.log(`   First event: ${enrollmentEvents[0].event}, enrollment: ${enrollmentEvents[0].enrollment}`);
                    }

                    // Process and set these objectives
                    const groupedObjectives = enrollmentEvents.map(event =>
                        mapDataValuesToObjective(event)
                    );

                    console.log(`✅ Processed ${groupedObjectives.length} objectives`);

                    setObjectives(prev => {
                        if (!groupedObjectives.length) return prev;

                        return groupedObjectives.map(groupedObj => {
                            const existingObj = prev.find(obj => obj.id === groupedObj.id);

                            const hasExistingDetailedKpis = existingObj?.kpis?.some(k =>
                                k?.name?.trim() || k?.target?.trim() || k?.description?.trim()
                            );
                            const hasExistingDetailedKras = existingObj?.kras?.some(k =>
                                k?.name?.trim() || k?.description?.trim() || (Number(k?.weight) || 0) > 0
                            );

                            if (existingObj && (hasExistingDetailedKpis || hasExistingDetailedKras)) {
                                console.log(`🔗 [MERGE] Preserving KPIs/KRAs for objective: ${groupedObj.id}`);
                                return {
                                    ...groupedObj,
                                    kpis: existingObj.kpis,
                                    kras: existingObj.kras
                                };
                            }

                            return groupedObj;
                        });
                    });

                } catch (error) {
                    console.error('❌ Error loading group objectives:', error);

                    // Final fallback: Try loading by program only
                    try {
                        console.log('🔄 [LOAD GROUP] Final fallback: loading by program only...');
                        const finalFallbackResponse = await engine.query({
                            finalFallbackObjectives: {
                                resource: 'events',
                                params: {
                                    filter: `program:eq:${objectivesProgramId}`,
                                    fields: 'event,orgUnit,dataValues[dataElement,value],enrollment'
                                }
                            }
                        });

                        const fallbackEvents = finalFallbackResponse.finalFallbackObjectives?.events || [];
                        console.log(`✅ Final fallback loaded ${fallbackEvents.length} events`);

                        const fallbackObjectives = fallbackEvents.map(event =>
                            mapDataValuesToObjective(event)
                        );

                        setObjectives(fallbackObjectives);
                    } catch (fallbackError) {
                        console.error('❌ Final fallback also failed:', fallbackError);
                    }
                }
            }
        };

        loadGroupObjectives();
    }, [mode, editingObjective?.id, currentUserPosition?.trackedEntityInstance, user?.organisationUnits?.[0]?.id]);



    // ✅ Only log when specific values change
    const totalWeight = sumObjectiveWeights(objectives);
    const objectiveCount = objectives.length;

    useEffect(() => {
        console.log('🔍 [WEIGHT GROUP DEBUG]', {
            mode,
            weightGroupEnrollment,
            objectiveCount,
            totalWeight
        });
    }, [weightGroupEnrollment, objectiveCount, totalWeight, mode]);

    useEffect(() => {
        let isMounted = true;

        const loadCompanyGoals = async () => {
            if (!user?.organisationUnits?.[0]?.id || !mounted || !reportingStructureLoaded || !currentUserPosition) return;

            setLoadingGoals(true);

            try {
                console.log('🎯 [GOALS LOADING] Starting dynamic goal loading...');

                // ✅ DYNAMIC: Find ANY org unit that has the goals program assigned
                // First, get the goals program details
                const programResponse = await engine.query({
                    goalsProgram: {
                        resource: 'programs',
                        params: {
                            filter: `id:eq:${ORG_GOALS_PROGRAM}`,
                            fields: 'id,name,organisationUnits[id,name,level,path]'
                        }
                    }
                });

                const program = programResponse.goalsProgram?.programs?.[0];
                if (!program) {
                    console.warn('⚠️ Goals program not found');
                    if (isMounted && mounted) {
                        showAlert('Company goals program not found. Please contact administrator.', 'warning');
                    }
                    return;
                }

                const assignedOrgUnits = program.organisationUnits || [];
                console.log('🔍 [GOALS PROGRAM]', {
                    programName: program.name,
                    assignedOrgUnits: assignedOrgUnits.map(ou => ({ id: ou.id, level: ou.level, name: ou.name })),
                    totalAssigned: assignedOrgUnits.length
                });

                if (assignedOrgUnits.length === 0) {
                    console.warn('⚠️ Goals program not assigned to any org unit');
                    if (isMounted && mounted) {
                        showAlert('Company goals program is not assigned to any department. Please contact administrator.', 'warning');
                    }
                    return;
                }

                // ✅ DYNAMIC: Strategy 1 - Find the highest level (strategic) org unit
                // Typically goals should be at higher levels (Level 4-6 for strategic goals)
                const strategicLevels = [4, 5, 6]; // Levels where strategic goals typically reside

                let goalOrgUnitId = null;

                // Try to find a strategic level org unit
                for (const level of strategicLevels) {
                    const strategicOU = assignedOrgUnits.find(ou => ou.level === level);
                    if (strategicOU) {
                        goalOrgUnitId = strategicOU.id;
                        console.log(`🎯 Found strategic org unit (Level ${level}):`, strategicOU.name);
                        break;
                    }
                }

                // Strategy 2: If no strategic level found, use the first assigned org unit
                if (!goalOrgUnitId && assignedOrgUnits.length > 0) {
                    goalOrgUnitId = assignedOrgUnits[0].id;
                    console.log(`🎯 Using first assigned org unit:`, assignedOrgUnits[0].name);
                }

                // Strategy 3: If still no org unit, use user's org unit (with warning)
                if (!goalOrgUnitId) {
                    goalOrgUnitId = user.organisationUnits[0].id;
                    console.warn(`⚠️ No assigned org units, falling back to user org unit:`, goalOrgUnitId);
                }

                console.log('🎯 [FINAL GOAL ORG UNIT]', {
                    id: goalOrgUnitId,
                    details: assignedOrgUnits.find(ou => ou.id === goalOrgUnitId) || { id: goalOrgUnitId, name: 'User Org Unit' }
                });

                // ✅ DYNAMIC: Load goals from the selected org unit
                const response = await engine.query({
                    goalsQuery: {
                        resource: 'events',
                        params: {
                            program: ORG_GOALS_PROGRAM,
                            programStage: ORG_GOALS_STAGE,
                            fields: 'event,orgUnit,dataValues[dataElement,value]',
                            orgUnit: goalOrgUnitId,
                            ouMode: 'ACCESSIBLE', // Get goals from this org unit and all accessible ones
                            pageSize: 1000,
                        },
                    },
                });

                const events = response.goalsQuery?.events || [];
                console.log('✅ [GOALS LOADED] Count:', events.length);

                // Map goals with proper field mapping
                const mappedGoals = events.map(event => {
                    const dataValuesMap = {};
                    event.dataValues?.forEach(dv => {
                        dataValuesMap[dv.dataElement] = dv.value;
                    });

                    return {
                        id: event.event,
                        name: dataValuesMap['HJqqfzhH614'] || dataValuesMap['zS8QIgZc0Ll'] || 'Unnamed Goal',
                        description: dataValuesMap['zS8QIgZc0Ll'] || '',
                        progress: parseInt(dataValuesMap['pOOGDRzEo98']) || 0,
                        startDate: dataValuesMap['MH7DQVegD1j'] || '',
                        endDate: dataValuesMap['pUvJQ9vy3A3'] || '',
                        priority: dataValuesMap['Co2i5BleCcZ'] || '',
                        category: dataValuesMap['uBPW95V2VoX'] || '',
                        createdBy: dataValuesMap['nZDkNlZwX9I'] || '',
                        orgUnit: event.orgUnit,
                        // Add level info if available
                        level: assignedOrgUnits.find(ou => ou.id === event.orgUnit)?.level || 'unknown'
                    };
                });

                if (isMounted && mounted) {
                    setGoals(mappedGoals);
                    console.log('✅ [GOALS SET]', mappedGoals.length, 'goals available');
                }
            } catch (error) {
                console.error('❌ Error fetching goals:', error);
                if (isMounted && mounted) {
                    showAlert('Failed to load company goals. Please contact your administrator.', 'error');
                }
            } finally {
                if (isMounted && mounted) setLoadingGoals(false);
            }
        };

        loadCompanyGoals();

        return () => {
            isMounted = false;
        };
    }, [
        user?.organisationUnits?.[0]?.id,
        engine,
        reportingStructureLoaded,
        currentUserPosition,
        mounted,
        ORG_GOALS_PROGRAM,
        ORG_GOALS_STAGE
    ]);


    useEffect(() => {
        let isMounted = true;

        const fetchGoals = async () => {
            if (!user?.organisationUnits?.[0]?.id || !engine) {
                if (isMounted) setLoadingGoals(false);
                return;
            }

            setLoadingGoals(true);

            try {
                // Get user's org unit to determine hierarchy
                const userOrgUnit = user.organisationUnits[0];

                // Determine Level 5 org unit ID using multiple strategies
                let goalOrgUnitId = null;

                // Strategy 1: Try to get from user's org unit path
                if (userOrgUnit.path) {
                    const pathParts = userOrgUnit.path.split('/').filter(Boolean);
                    if (pathParts.length >= 5) { // Level 5 = index 4 (0-based)
                        goalOrgUnitId = pathParts[4];
                    }
                }

                // Strategy 2: Use program metadata to find org units with goals
                if (!goalOrgUnitId) {
                    try {
                        const programResponse = await engine.query({
                            programMeta: {
                                resource: 'programs',
                                params: {
                                    filter: `id:eq:${ORG_GOALS_PROGRAM}`,
                                    fields: 'id,name,organisationUnits[id,path,level]'
                                }
                            }
                        });

                        const program = programResponse.programMeta?.programs?.[0];
                        if (program) {
                            const level5OrgUnits = program.organisationUnits?.filter(ou => ou.level === 5) || [];
                            if (level5OrgUnits.length > 0) {
                                goalOrgUnitId = level5OrgUnits[0].id;
                            }
                        }
                    } catch (error) {
                        console.warn('⚠️ Failed to get program metadata, falling back to user org unit', error);
                    }
                }

                // Strategy 3: Final fallback to user's org unit
                if (!goalOrgUnitId) {
                    goalOrgUnitId = userOrgUnit.id;
                    console.log('🔄 [FALLBACK] Using user org unit as goal org unit:', goalOrgUnitId);
                }

                console.log('🎯 Fetching goals using org unit ID:', goalOrgUnitId);

                
                const response = await engine.query({
                    goalsQuery: {
                        resource: 'events',
                        params: {
                            program: ORG_GOALS_PROGRAM,
                            programStage: ORG_GOALS_STAGE,
                            fields: 'event,orgUnit,dataValues[dataElement,value]',
                            orgUnit: goalOrgUnitId,
                            ouMode: 'ACCESSIBLE',
                            pageSize: 1000,
                        },
                    },
                });

                const events = response.goalsQuery?.events || [];
                console.log('✅ [GOALS LOADED] Count:', events.length);

                // Detailed debug logging
                events.forEach((event, i) => {
                    const nameDV = event.dataValues?.find(dv => dv.dataElement === 'HJqqfzhH614');
                    console.log(`   Goal ${i}: ID=${event.event}, Name=${nameDV?.value || 'MISSING'}`);
                });

                // Map goals properly
                const mappedGoals = events.map(event => {
                    const dataValuesMap = {};
                    event.dataValues?.forEach(dv => {
                        dataValuesMap[dv.dataElement] = dv.value;
                    });

                    return {
                        id: event.event,
                        name: dataValuesMap['HJqqfzhH614'] || dataValuesMap['zS8QIgZc0Ll'] || 'Unnamed Goal',
                        description: dataValuesMap['zS8QIgZc0Ll'] || '',
                        progress: parseInt(dataValuesMap['pOOGDRzEo98']) || 0,
                        startDate: dataValuesMap['MH7DQVegD1j'] || '',
                        endDate: dataValuesMap['pUvJQ9vy3A3'] || '',
                        priority: dataValuesMap['Co2i5BleCcZ'] || '',
                        category: dataValuesMap['uBPW95V2VoX'] || '',
                        orgUnit: event.orgUnit,
                    };
                });

                if (isMounted) {
                    setGoals(mappedGoals);
                }
            } catch (error) {
                console.error('❌ Error fetching goals:', error);
                if (isMounted) {
                    // Show user-friendly error messages based on specific errors
                    let errorMessage = 'Failed to load company goals';
                    if (error.message?.includes('403') || error.message?.includes('401')) {
                        errorMessage = 'You don\'t have permission to view company goals. Please contact your administrator.';
                    } else if (error.message?.includes('404')) {
                        errorMessage = 'Company goals not found. Please contact your administrator.';
                    }
                    showAlert(errorMessage, 'error');
                }
            } finally {
                if (isMounted) setLoadingGoals(false);
            }
        };

        fetchGoals();

        return () => {
            isMounted = false;
        };
    }, [
        user?.organisationUnits?.[0]?.id,
        engine,
        ORG_GOALS_PROGRAM,
        ORG_GOALS_STAGE
    ]);

    // ✅ ENHANCED CONSOLIDATED: Single source of truth for goal AND objective alignment
    useEffect(() => {
        const detectAndSetAlignment = async () => {
            // Skip if we're not in edit mode or still loading
            if (!editingObjective || loadingGoals) return;

            console.log('🎯 [ALIGNMENT DETECTION] Starting detection', {
                mode,
                isCEO: user?.roleIds?.includes('nLSSLch316I'),
                hasObjectiveAlignment: !!editingObjective.objectiveAlignment,
                hasSelectedHigherLevelObjective: !!selectedHigherLevelObjective?.id,
                goalsLoaded: goals.length
            });

            // STRATEGY 1: CEO USERS - Link to goals directly
            const isCEO = user?.roleIds?.includes('nLSSLch316I');

            if (isCEO) {
                console.log('🎯 [GOAL DETECTION] User is CEO, checking for direct goal linkage');

                // Source 1: Direct companyAlignedGoal property
                let goalId = editingObjective.companyAlignedGoal;

                // Source 2: Check rawDataValues
                if (!goalId && editingObjective.rawDataValues) {
                    console.log('🎯 [GOAL DETECTION] Checking rawDataValues...');
                    const goalDataValue = editingObjective.rawDataValues.find(
                        dv => dv.dataElement === 'VhrIQQIVpB9'
                    );
                    goalId = goalDataValue?.value;
                    console.log('🎯 [GOAL DETECTION] Found in rawDataValues:', goalId);
                }

                // Set the goal if found
                if (goalId && goalId !== '') {
                    const goalExists = goals.some(g => g.id === goalId);
                    if (goalExists && selectedGoal !== goalId) {
                        console.log('🎯 [GOAL DETECTION] Setting selectedGoal for CEO:', goalId);
                        setSelectedGoal(goalId);
                        updateObjectiveField(0, 'companyAlignedGoal', goalId);
                    } else if (!goalExists) {
                        console.warn('🎯 [GOAL DETECTION] Goal ID not found in available goals:', goalId);
                    }
                } else {
                    console.log('🎯 [GOAL DETECTION] No goal assignment found for CEO objective');
                }
            }

            // ✅ STRATEGY 2: SUBORDINATE USERS - Restore manager objective alignment
            else if (editingObjective.objectiveAlignment && !selectedHigherLevelObjective?.id) {
                console.log('🔗 [SUBORDINATE] Restoring manager objective alignment:', editingObjective.objectiveAlignment);

                try {
                    // FIRST: Check if we have higherLevelObjectives loaded
                    if (higherLevelObjectives.length > 0) {
                        const existingInList = higherLevelObjectives.find(
                            obj => obj.id === editingObjective.objectiveAlignment
                        );

                        if (existingInList) {
                            console.log('✅ [SUBORDINATE] Found aligned objective in higherLevelObjectives:', existingInList.name);
                            setSelectedHigherLevelObjective(existingInList);

                            // Update form fields
                            updateObjectiveField(0, 'objectiveAlignment', editingObjective.objectiveAlignment);
                            updateObjectiveField(0, 'higherLevelObjectiveId', editingObjective.objectiveAlignment);
                            updateObjectiveField(0, 'higherLevelObjectiveName', existingInList.name);

                            // Set goal context if available
                            const managerGoalId = existingInList.dataValues?.find(
                                dv => dv.dataElement === 'VhrIQQIVpB9'
                            )?.value;

                            if (managerGoalId && goals.length > 0) {
                                const managerGoal = goals.find(g => g.id === managerGoalId);
                                if (managerGoal) {
                                    updateObjectiveField(0, '_managerGoalName', managerGoal.name);
                                }
                            }
                            return; // Done!
                        }
                    }

                    // SECOND: If not in higherLevelObjectives, check filteredManagerObjectives
                    if (filteredManagerObjectives.length > 0) {
                        const existingInFiltered = filteredManagerObjectives.find(
                            obj => obj.id === editingObjective.objectiveAlignment
                        );

                        if (existingInFiltered) {
                            console.log('✅ [SUBORDINATE] Found aligned objective in filteredManagerObjectives:', existingInFiltered.name);
                            setSelectedHigherLevelObjective(existingInFiltered);

                            updateObjectiveField(0, 'objectiveAlignment', editingObjective.objectiveAlignment);
                            updateObjectiveField(0, 'higherLevelObjectiveId', editingObjective.objectiveAlignment);
                            updateObjectiveField(0, 'higherLevelObjectiveName', existingInFiltered.name);

                            // Set goal context
                            const managerGoalId = existingInFiltered.dataValues?.find(
                                dv => dv.dataElement === 'VhrIQQIVpB9'
                            )?.value;

                            if (managerGoalId && goals.length > 0) {
                                const managerGoal = goals.find(g => g.id === managerGoalId);
                                if (managerGoal) {
                                    updateObjectiveField(0, '_managerGoalName', managerGoal.name);
                                }
                            }
                            return; // Done!
                        }
                    }

                    // THIRD: If not found anywhere, fetch it directly
                    console.log('🔍 [SUBORDINATE] Aligned objective not in lists, fetching directly...');

                    const response = await engine.query({
                        alignedObjective: {
                            resource: `events/${editingObjective.objectiveAlignment}`,
                            params: {
                                fields: 'event,dataValues[dataElement,value],createdBy,storedBy',
                                program: 'yiT00XKrrHP',
                                programStage: 'VIBtpvkpHFR'
                            }
                        }
                    });

                    if (response.alignedObjective) {
                        const objectiveData = response.alignedObjective;
                        const dataValuesMap = {};
                        objectiveData.dataValues?.forEach(dv => {
                            dataValuesMap[dv.dataElement] = dv.value;
                        });

                        // Get manager name
                        let managerName = 'Unknown Manager';
                        try {
                            if (objectiveData.storedBy) {
                                const userResponse = await engine.query({
                                    user: {
                                        resource: `users/${objectiveData.storedBy}`,
                                        params: { fields: 'displayName' }
                                    }
                                });
                                managerName = userResponse.user?.displayName || objectiveData.storedBy;
                            }
                        } catch (err) {
                            console.warn('Could not fetch manager name:', err);
                        }

                        const restoredObjective = {
                            id: objectiveData.event,
                            name: dataValuesMap['EuN5ztFnXsQ'] || 'Unnamed Objective',
                            description: dataValuesMap['nWqW5BrcgP1'] || '',
                            createdBy: managerName,
                            userRole: dataValuesMap['vFoMLF5eZ5X'] || '',
                            objectiveRoleAssigned: dataValuesMap['vFoMLF5eZ5X'] || '',
                            dataValues: objectiveData.dataValues || [],
                            dateCreated: objectiveData.created || new Date().toISOString(),
                            storedBy: objectiveData.storedBy
                        };

                        console.log('✅ [SUBORDINATE] Restored aligned objective directly:', restoredObjective);
                        setSelectedHigherLevelObjective(restoredObjective);

                        // Update form fields
                        updateObjectiveField(0, 'objectiveAlignment', editingObjective.objectiveAlignment);
                        updateObjectiveField(0, 'objectiveRoleAssigned', editingObjective.objectiveRoleAssigned);
                        updateObjectiveField(0, 'higherLevelObjectiveId', editingObjective.objectiveAlignment);
                        updateObjectiveField(0, 'higherLevelObjectiveName', restoredObjective.name);

                        // Set goal context
                        const managerGoalId = dataValuesMap['VhrIQQIVpB9'];
                        if (managerGoalId && goals.length > 0) {
                            const managerGoal = goals.find(g => g.id === managerGoalId);
                            if (managerGoal) {
                                updateObjectiveField(0, '_managerGoalName', managerGoal.name);
                                console.log('🎯 [SUBORDINATE] Set manager goal name:', managerGoal.name);
                            }
                        }
                    }
                } catch (error) {
                    console.error('❌ [SUBORDINATE] Failed to restore aligned objective:', error);
                    // Create a placeholder
                    setSelectedHigherLevelObjective({
                        id: editingObjective.objectiveAlignment,
                        name: editingObjective.higherLevelObjectiveName || `Objective ${editingObjective.objectiveAlignment.substring(0, 8)}...`,
                        description: 'Unable to load aligned objective details',
                        createdBy: 'Unknown Manager',
                        isUnavailable: true,
                        error: 'Could not load aligned objective'
                    });
                }
            }
        };

        // Run detection
        detectAndSetAlignment();
    }, [
        editingObjective,
        loadingGoals,
        goals,
        user?.roleIds,
        higherLevelObjectives,
        filteredManagerObjectives,
        selectedHigherLevelObjective?.id,
        engine,
        updateObjectiveField,
    ]);

    // Call it in useEffect or before sending email
    useEffect(() => {
        if (currentUserPosition) {
            debugPositionData();
        }
    }, [currentUserPosition]);


    // Add this useEffect to handle editing objective linkage restoration
    useEffect(() => {
        const loadLinkedObjective = async () => {
            if (editingObjective?.objectiveAlignment && currentUserPosition && engine) {
                try {
                    console.log('🎯 [EDITING OBJECTIVE] Loading existing objective:', editingObjective);

                    const attributes = extractAttributes(currentUserPosition.attributes || []);
                    const isTopLevelUser = !attributes['SjP47qi3JeB'];

                    if (!isTopLevelUser && editingObjective.objectiveAlignment) {
                        console.log('🔍 [LOADING LINKED OBJECTIVE] ID:', editingObjective.objectiveAlignment);

                        const response = await engine.query({
                            linkedObjective: {
                                resource: `events/${editingObjective.objectiveAlignment}`,
                                params: {
                                    fields: 'event,dataValues[dataElement,value],createdBy',
                                    program: 'yiT00XKrrHP',
                                    programStage: 'VIBtpvkpHFR'
                                }
                            }
                        });

                        if (response.linkedObjective) {
                            const linkedObjective = response.linkedObjective;

                            // Extract data values
                            const dataValuesMap = {};
                            linkedObjective.dataValues?.forEach(dv => {
                                dataValuesMap[dv.dataElement] = dv.value;
                            });

                            // Get manager name - use fallback logic
                            let managerName = editingObjective.createdBy || 'Unknown Manager';
                            if (editingObjective.objectiveRoleAssigned && editingObjective.objectiveRoleAssigned !== 'true') {
                                managerName = editingObjective.objectiveRoleAssigned;
                            }

                            // Get goal name - handle both loaded and not loaded states
                            let goalName = 'Unknown Goal';
                            const goalId = dataValuesMap['VhrIQQIVpB9'];

                            // Find goal in goals array if available
                            if (goalId && goals.length > 0) {
                                const goal = goals.find(g => g.id === goalId);
                                if (goal) {
                                    goalName = goal.name;
                                }
                            }

                            // If we don't have goals yet, try to get from editingObjective
                            if (goalName === 'Unknown Goal' && editingObjective._managerGoalName) {
                                goalName = editingObjective._managerGoalName;
                            }

                            // Format the objective properly
                            const formattedObjective = {
                                id: linkedObjective.event,
                                name: dataValuesMap['EuN5ztFnXsQ'] || 'Unnamed Objective',
                                description: dataValuesMap['nWqW5BrcgP1'] || '',
                                createdBy: managerName,
                                userRole: editingObjective.objectiveRoleAssigned || '',
                                objectiveRoleAssigned: editingObjective.objectiveRoleAssigned || '',
                                dataValues: linkedObjective.dataValues || [],
                                dateCreated: linkedObjective.created || new Date().toISOString(),
                                _managerGoalName: goalName
                            };

                            console.log('✅ [LINKED OBJECTIVE LOADED]', formattedObjective);
                            setSelectedHigherLevelObjective(formattedObjective);

                            // Update form fields
                            updateObjectiveField(0, 'higherLevelObjectiveId', editingObjective.objectiveAlignment);
                            updateObjectiveField(0, 'higherLevelObjectiveName', formattedObjective.name);
                            updateObjectiveField(0, '_managerGoalName', goalName);
                        }
                    }
                } catch (error) {
                    console.error('❌ [LINKED OBJECTIVE ERROR]', error);
                    showAlert('Failed to load linked objective. Please reselect.', 'warning');
                }
            }
        };

        if (editingObjective?.objectiveAlignment) {
            loadLinkedObjective();
        }
    }, [editingObjective?.objectiveAlignment]);


    const getCurrentUserPosition = async () => {
        if (!user?.id) {
            console.warn('No user found');
            return { position: null, staffOrgUnits: [], positionOrgUnit: null };
        }

        const fallbackOrgUnits = user.organisationUnits?.map(ou => ou.id) || [];

        try {
            const searchOrgUnits = fallbackOrgUnits.length ? fallbackOrgUnits.join(';') : null;
            if (!searchOrgUnits) {
                return { position: null, staffOrgUnits: fallbackOrgUnits, positionOrgUnit: null };
            }

            const { positions } = await engine.query({
                positions: {
                    resource: 'trackedEntityInstances',
                    params: {
                        trackedEntityType: 'UGGo0Ebjl0E',
                        filter: `${POSITION_ATTRIBUTES.CURRENT_INCUMBENT}:eq:${user.id}`,
                        ou: searchOrgUnits,
                        ouMode: 'ACCESSIBLE',
                        fields: 'orgUnit,trackedEntityInstance,attributes[attribute,value]',
                        pageSize: 1,
                    },
                },
            });

            const position = positions.trackedEntityInstances?.[0] || null;
            let staffOrgUnits = fallbackOrgUnits;
            let positionOrgUnit = position?.orgUnit || null;

            // Optional: staff username logic (keep if needed)
            const staffUsernameAttr = position?.attributes?.find(
                attr => attr.attribute === 'g3RRIfDiQra'
            );

            if (staffUsernameAttr?.value) {
                try {
                    const userResponse = await engine.query({
                        staffUser: {
                            resource: 'users',
                            params: {
                                filter: `username:eq:${staffUsernameAttr.value}`,
                                fields: 'organisationUnits[id]',
                            },
                        },
                    });
                    const staffUser = userResponse.staffUser?.users?.[0];
                    if (staffUser?.organisationUnits) {
                        staffOrgUnits = staffUser.organisationUnits.map(ou => ou.id);
                    }
                } catch (err) {
                    console.error('Failed to fetch staff user:', err);
                }
            }

            return { position, positionOrgUnit, staffOrgUnits };
        } catch (error) {
            console.error('Error fetching current user position:', error);
            return { position: null, positionOrgUnit: null, staffOrgUnits: fallbackOrgUnits };
        }
    };

    // Replace your current position loading useEffect with this
    useEffect(() => {
        const loadPosition = async () => {
            if (!user?.id || !mounted || !engine) return;

            try {
                // Get current user position directly
                const response = await engine.query({
                    currentUserPosition: {
                        resource: 'trackedEntityInstances',
                        params: {
                            trackedEntityType: 'UGGo0Ebjl0E', // Position TE type
                            filter: `V66IFsw5kq6:eq:${user.id}`, // Current Incumbent Staff ID
                            fields: 'trackedEntityInstance,attributes[attribute,value]',
                            ouMode: 'ACCESSIBLE',
                            pageSize: 1
                        }
                    }
                });

                const position = response.currentUserPosition?.trackedEntityInstances?.[0];
                if (!mounted || !position) return;

                setCurrentUserPosition(position);

                // Extract attributes to determine if top-level user
                const attributes = {};
                position.attributes?.forEach(attr => {
                    attributes[attr.attribute] = attr.value;
                });

                const hasManager = !!attributes['SjP47qi3JeB'];
                setIsTopLevelUser(!hasManager);
                setReportingStructureLoaded(true);

                console.log('🎯 [POSITION LOADED] User is top-level:', !hasManager);
            } catch (error) {
                console.error('❌ Failed to load position ', error);
                if (mounted) setReportingStructureLoaded(true);
            }
        };

        loadPosition();
    }, [user?.id, mounted, engine]);


    useEffect(() => {
        const loadPosition = async () => {
            const result = await getCurrentUserPosition();
            setCurrentUserPosition(result.position);
        };
        if (user?.id) {
            loadPosition();
        }
    }, [user?.id, engine]);

    // Update the alignment detection
    useEffect(() => {
        // Only create placeholder if we have alignment data BUT no selected objective
        if (editingObjective && editingObjective.objectiveAlignment && !selectedHigherLevelObjective?.id) {
            const placeholder = {
                id: editingObjective.objectiveAlignment,
                name: `Objective ${editingObjective.objectiveAlignment}`,
                description: 'Loading alignment details...',
                userRole: editingObjective.objectiveRoleAssigned,
                creatorRole: editingObjective.objectiveRoleAssigned,
                isLoading: true
            };

            setSelectedHigherLevelObjective(placeholder);

            if (loadHigherLevelObjectiveDetails) {
                loadHigherLevelObjectiveDetails(editingObjective.objectiveAlignment);
            }
        }



        console.log('🔍 CURRENT USER POSITION ATTRIBUTES:', currentUserPosition?.attributes);
        const reportingTo = currentUserPosition?.attributes?.find(a => a.attribute === 'SjP47qi3JeB');
        console.log('🔍 REPORTING TO (SjP47qi3JeB):', reportingTo?.value);


        if (isTopLevelUser && editingObjective?.companyAlignedGoal) {

        }
    }, [editingObjective, selectedHigherLevelObjective, loadHigherLevelObjectiveDetails, setSelectedHigherLevelObjective, user]);

    // ✅ RESTORE ALIGNMENT ON EDIT — POSITION-AWARE
    useEffect(() => {
        if (editingObjective?.objectiveAlignment && !selectedHigherLevelObjective) {
            // Create placeholder to preserve UI state
            const placeholder = {
                id: editingObjective.objectiveAlignment,
                name: editingObjective.higherLevelObjectiveName
                    || `Objective ${editingObjective.objectiveAlignment.substring(0, 8)}...`,
                description: editingObjective.description || 'Loading alignment details...',
                userRole: editingObjective.objectiveRoleAssigned,
                creatorRole: editingObjective.objectiveRoleAssigned,
                isLoading: true,
            };
            setSelectedHigherLevelObjective(placeholder);

            // Try to reload full details (even if not in current manager list)
            if (loadHigherLevelObjectiveDetails) {
                loadHigherLevelObjectiveDetails(editingObjective.objectiveAlignment)
                    .then(fullObj => {
                        if (fullObj) {
                            setSelectedHigherLevelObjective(fullObj);
                        } else {
                            // Mark as deleted/unavailable if not found
                            setSelectedHigherLevelObjective(prev => ({
                                ...prev,
                                isDeleted: true,
                                error: 'Higher-level objective not found or inaccessible.'
                            }));
                        }
                    })
                    .catch(err => {
                        console.error('Failed to reload aligned objective:', err);
                        setSelectedHigherLevelObjective(prev => ({
                            ...prev,
                            isDeleted: true,
                            error: 'Failed to load aligned objective.'
                        }));
                    });
            }
        }
    }, [editingObjective, selectedHigherLevelObjective, setSelectedHigherLevelObjective, loadHigherLevelObjectiveDetails]);

  
        // Auto-fill fiscal year dates when metadata is available
    useEffect(() => {
        // Wait until we have basic user/org context AND fiscal year loaded
        if (!user?.organisationUnits?.[0]?.id || !fiscalYear.start || !fiscalYear.end) return;
        
        // Only fill if dates are still empty (don't override user edits)
        setObjectives(prev => {
            const needsUpdate = prev.some(obj => 
                !obj.startDate || !obj.completionDate || !obj.orgUnit
            );
            
            if (!needsUpdate) return prev;
            
            return prev.map(obj => ({
                ...obj,
                startDate: obj.startDate || fiscalYear.start,
                completionDate: obj.completionDate || fiscalYear.end,
                orgUnit: obj.orgUnit || user.organisationUnits[0].id,
            }));
        });
    }, [fiscalYear.start, fiscalYear.end, user?.organisationUnits?.[0]?.id]); // ✅ Specific dependencies

    
    useEffect(() => {

        if (editingObjective?.objectiveAlignment && editingObjective?.objectiveRoleAssigned) {
            console.log('🟡 [ALIGNMENT DETECTION] Backup: Found alignment data in useEffect');
        }
    }, [editingObjective?.objectiveAlignment, editingObjective?.objectiveRoleAssigned]);

    useEffect(() => {
        const checkInitialAccess = async () => {
            if (user?.organisationUnits?.[0]?.id) {
                const orgUnitId = user.organisationUnits[0].id;
                try {
                    const response = await engine.query({
                        programAssignment: {
                            resource: `programs/yiT00XKrrHP`,
                            params: {
                                fields: 'organisationUnits[id]',
                                filter: `organisationUnits.id:eq:${orgUnitId}`
                            }
                        }
                    });

                    const hasAssignment = response.programAssignment.organisationUnits?.length > 0;

                    if (!hasAssignment) {
                        console.warn('⚠️ Program not assigned to user org unit:', orgUnitId);
                        // You can show a warning here if you want
                    }
                } catch (error) {
                    console.error('Initial program access check failed:', error);
                }
            }
        };

        if (mode === 'add') {
            checkInitialAccess();
        }
    }, [user, mode, engine]);


    useEffect(() => {
    const loadManagerObjectives = async () => {
        setLoadingHigherLevelObjectives(true);
        try {
            if (!currentUserPosition?.trackedEntityInstance) {
                setHigherLevelObjectives([]);
                return;
            }

            const attributes = extractAttributes(currentUserPosition.attributes);
            const managerPositionTei = attributes['SjP47qi3JeB']; // REPORTING_TO UID
            if (!managerPositionTei) {
                setHigherLevelObjectives([]);
                return;
            }

            // ✅ SIMPLE: Fetch manager's own objectives
            const response = await engine.query({
                managerObjectives: {
                    resource: 'events',
                    params: {
                        program: 'yiT00XKrrHP',
                        programStage: 'VIBtpvkpHFR',
                        filter: `SlgO60cMK3F:eq:${managerPositionTei}`,
                        fields: 'event,orgUnit,eventDate,storedBy,dataValues[dataElement,value]',
                        pageSize: 100,
                    },
                },
            });

            const managerEvents = response.managerObjectives?.events || [];
            console.log(`✅ Loaded ${managerEvents.length} manager objectives`);

            
            const usernames = [...new Set(managerEvents.map(e => e.storedBy).filter(Boolean))];
            const userMap = {};
            if (usernames.length > 0) {
                try {
                    const userResponse = await engine.query({
                        users: {
                            resource: 'users',
                            params: {
                                filter: usernames.map(u => `username:eq:${u}`).join(';'),
                                fields: 'username,displayName'
                            }
                        }
                    });
                    (userResponse.users?.users || []).forEach(u => {
                        userMap[u.username] = u.displayName;
                    });
                } catch (err) {
                    console.warn('Failed to resolve user display names:', err);
                }
            }

            // Map manager objectives
            const managerObjectives = managerEvents.map(event => {
                const baseObj = mapDataValuesToObjective(event);
                
                return {
                    ...baseObj,
                    id: event.event,
                    name: baseObj.name || 'Unnamed Objective',
                    description: baseObj.description || '',
                    createdBy: userMap[event.storedBy] || event.storedBy || 'Unknown',
                    storedBy: event.storedBy,
                    dataValues: event.dataValues || [],
                    companyAlignedGoal: baseObj.companyAlignedGoal,
                    objectiveAlignment: baseObj.objectiveAlignment,
                    // The goal will be looked up from the global goals list
                };
            });

            console.log('✅ Loaded manager objectives:', managerObjectives.length);
            setHigherLevelObjectives(managerObjectives);

            // ✅ GOALS ARE ALREADY LOADED GLOBALLY
            // The goals state already contains ALL goals in the system
            // So when displaying, we can show ALL goals as options

        } catch (error) {
            console.error('❌ Failed to load manager objectives:', error);
            setHigherLevelObjectives([]);
            showAlert('Failed to load higher-level objectives', 'error');
        } finally {
            setLoadingHigherLevelObjectives(false);
        }
    };

    if (currentUserPosition) {
        loadManagerObjectives();
    } else {
        setLoadingHigherLevelObjectives(false);
    }
}, [currentUserPosition, engine]);

    // Helper function to check if user can edit remarks
    const shouldShowRemarks = () => {
        return mode === 'edit'; // Only show remarks field in edit mode
    };

    const canEditRemarks = () => {
        // Only direct manager can edit
        return isUserDirectManager;
    };

    // Helper function to get user's position title/name
    const getUserPositionTitle = () => {
        if (!currentUserPosition?.attributes) return null;

        const attributes = extractAttributes(currentUserPosition.attributes);

        // Look for position name/title attribute
        return attributes['w3bwqUZoc6V'] || // Example: Position Name attribute
            attributes['g3RRIfDiQra'] || // Staff Username
            'User Position';
    };

    // Helper function to display permission context

    const renderPermissionContext = () => {
        if (mode !== 'edit') return null;

        const isCreator = isObjectiveCreator();
        const isManager = isUserDirectManager;
        const positionTitle = getUserPositionTitle();

        let message = '';
        let color = colors.grey600;
        let icon = <IconView16 />; // default icon as React node

        if (isCreator && !isManager) {
            message = `You are the objective creator (${positionTitle || 'Creator'}). You can view but not edit remarks.`;
            color = colors.blue700;
            icon = <span role="img" aria-label="clipboard">📋</span>;
        } else if (isManager) {
            message = `You are the direct manager of the objective creator. You can edit remarks for feedback.`;
            color = colors.green700;
            icon = <span role="img" aria-label="pencil">✏️</span>;
        } else if (isCreator && isManager) {
            message = `You are the manager. You can edit remarks.`;
            color = colors.purple700;
            icon = <span role="img" aria-label="crown">👑</span>;
        } else {
            message = `You have view-only access to remarks.`;
            color = colors.grey600;
            icon = <IconView16 />;
        }

        return (
            <div style={{
                padding: '8px 12px',
                backgroundColor: `${color}10`,
                border: `1px solid ${color}30`,
                borderRadius: '4px',
                color: color,
                fontSize: '0.85rem',
                marginBottom: '16px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
            }}>
                <span>{icon}</span>
                <span>{message}</span>
            </div>
        );
    };


    const sendParentObjectiveNotification = async (savedObjective, parentObjectiveId) => {

        const notificationKey = `email_sent_${savedObjective.id}_${parentObjectiveId}`;
        const alreadySent = sessionStorage.getItem(notificationKey);
        if (alreadySent) {
            console.log('🔄 [EMAIL] Notification already sent, skipping');
            return { success: true, message: 'Notification already sent', duplicate: true };
        }

        console.log('🔍 [EMAIL DEBUG] Starting email notification', {
            savedObjectiveId: savedObjective.id,
            parentObjectiveId: parentObjectiveId
        });

        try {
            let recipient = null;

            // Get current user info FIRST
            const currentUserName = user?.displayName || user?.name || user?.username || 'Unknown User';
            console.log('✅ [EMAIL] Current user:', currentUserName);

            console.log('✅ [EMAIL] Current user:', currentUserName);
                    
            
            if (!recipient && parentObjectiveId) {
                console.log('🔍 [EMAIL] STRATEGY 2: Trying to get creator of parent objective');
                try {
                    const parentObjectiveResponse = await engine.query({
                        parentObjective: {
                            resource: `events/${parentObjectiveId}`,
                            params: {
                                fields: 'event,storedBy,dataValues[dataElement,value]'
                            }
                        }
                    });

                    if (parentObjectiveResponse.parentObjective) {
                        const parent = parentObjectiveResponse.parentObjective;
                        const creatorUsername = parent.storedBy;

                        console.log('🔍 [EMAIL] Parent objective creator username:', creatorUsername);

                        if (creatorUsername) {
                            // Try to find user by username
                            try {
                                const userResponse = await engine.query({
                                    users: {
                                        resource: 'users',
                                        params: {
                                            filter: `username:eq:${creatorUsername}`,
                                            fields: 'id,displayName,email,userCredentials[username]'
                                        }
                                    }
                                });

                                recipient = userResponse.users?.users?.[0];
                                if (recipient) {
                                    console.log('✅ [EMAIL] Found parent objective creator:', recipient.displayName);
                                }
                            } catch (usernameError) {
                                console.warn('⚠️ [EMAIL] Failed to find user by username:', usernameError);
                            }
                        }
                    }
                } catch (parentError) {
                    console.error('❌ [EMAIL] Failed to fetch parent objective:', parentError);
                }
            }

            // ====== STRATEGY 3: Check reportingTo attribute in current position ======
            if (!recipient && currentUserPosition?.attributes) {
                console.log('🔍 [EMAIL] STRATEGY 3: Checking reportingTo attribute');
                const attributes = extractAttributes(currentUserPosition.attributes);
                const reportingToValue = attributes['SjP47qi3JeB'];

                console.log('🔍 [EMAIL] Reporting To attribute value:', reportingToValue);

                if (reportingToValue) {
                    // Treat reportingTo as a potential User UID first (without assuming a fixed prefix).
                        console.log('🔍 [EMAIL] Trying reportingTo as User UID:', reportingToValue);
                        try {
                            const userResponse = await engine.query({
                                user: {
                                    resource: `users/${reportingToValue}`,
                                    params: {
                                        fields: 'id,displayName,email,userCredentials[username]'
                                    }
                                }
                            });

                            if (userResponse.user) {
                                recipient = userResponse.user;
                                console.log('✅ [EMAIL] Found manager via User UID:', recipient.displayName);
                            }
                        } catch (uidError) {
                            console.warn('⚠️ [EMAIL] User UID lookup failed:', uidError);
                        }
                }
            }

            // ====== STRATEGY 4: Find by position hierarchy ======
            if (!recipient && currentUserPosition?.trackedEntityInstance) {
                console.log('🔍 [EMAIL] STRATEGY 4: Trying position hierarchy');
                try {
                    // Get position details
                    const positionResponse = await engine.query({
                        position: {
                            resource: `trackedEntityInstances/${currentUserPosition.trackedEntityInstance}`,
                            params: {
                                fields: 'trackedEntityInstance,attributes[attribute,value]'
                            }
                        }
                    });

                    if (positionResponse.position) {
                        const positionAttrs = extractAttributes(positionResponse.position.attributes || []);
                        const reportingToTei = positionAttrs['SjP47qi3JeB'];

                        console.log('🔍 [EMAIL] Reporting to TEI:', reportingToTei);

                        // If reportingToTei is a position TEI, fetch its incumbent
                        if (reportingToTei) {
                            try {
                                const reportingPositionResponse = await engine.query({
                                    reportingPosition: {
                                        resource: `trackedEntityInstances/${reportingToTei}`,
                                        params: {
                                            fields: 'trackedEntityInstance,attributes[attribute,value]'
                                        }
                                    }
                                });

                                if (reportingPositionResponse.reportingPosition) {
                                    const reportingAttrs = extractAttributes(reportingPositionResponse.reportingPosition.attributes || []);
                                    const incumbentId = reportingAttrs['V66IFsw5kq6'];

                                    if (incumbentId) {
                                        // Get staff TEI
                                        const staffResponse = await engine.query({
                                            staff: {
                                                resource: `trackedEntityInstances/${incumbentId}`,
                                                params: {
                                                    fields: 'trackedEntityInstance,attributes[attribute,value]'
                                                }
                                            }
                                        });

                                        const staffAttrs = extractAttributes(staffResponse.staff?.attributes || []);
                                        const staffUsername = staffAttrs['g3RRIfDiQra'];

                                        if (staffUsername) {
                                            // Get user by username
                                            const userResponse = await engine.query({
                                                users: {
                                                    resource: 'users',
                                                    params: {
                                                        filter: `username:eq:${staffUsername}`,
                                                        fields: 'id,displayName,email,userCredentials[username]'
                                                    }
                                                }
                                            });

                                            recipient = userResponse.users?.users?.[0];
                                            if (recipient) {
                                                console.log('✅ [EMAIL] Found via position hierarchy:', recipient.displayName);
                                            }
                                        }
                                    }
                                }
                            } catch (hierarchyError) {
                                console.warn('⚠️ [EMAIL] Position hierarchy lookup failed:', hierarchyError);
                            }
                        }
                    }
                } catch (positionError) {
                    console.warn('⚠️ [EMAIL] Position lookup failed:', positionError);
                }
            }

            // ====== STRATEGY 5: LAST RESORT - Find in org unit ======
            if (!recipient) {
                console.log('🔍 [EMAIL] STRATEGY 5: Last resort - finding in org unit');
                try {
                    const userResponse = await engine.query({
                        users: {
                            resource: 'users',
                            params: {
                                filter: `organisationUnits.id:eq:${savedObjective.orgUnit}`,
                                fields: 'id,displayName,email,userCredentials[username],userCredentials[userRoles[id,name]]',
                                pageSize: 20
                            }
                        }
                    });

                    const users = userResponse.users?.users || [];
                    console.log('🔍 [EMAIL] Found users in org unit:', users.length);
                 
                    
                } catch (fallbackError) {
                    console.error('❌ [EMAIL] Fallback failed:', fallbackError);
                }
            }

            // ====== VALIDATION ======
            if (!recipient || !recipient.email) {
                console.warn('⚠️ [EMAIL] No recipient found with email');
                return { success: false, error: 'No recipient with email found' };
            }

            console.log('✅ [EMAIL] FINAL RECIPIENT:', {
                name: recipient.displayName,
                email: recipient.email,
                username: recipient.userCredentials?.username,
                id: recipient.id
            });
       
            console.log('✅ [EMAIL] Recipient resolved via hierarchy:', {
                name: recipient.displayName,
                email: recipient.email,
                id: recipient.id
            });  

            // ====== SEND EMAIL ======
            const emailData = {
                to: recipient.email,
                subject: `New Objective Created by ${currentUserName}: ${savedObjective.name}`,
                message: `
                    Dear ${recipient.displayName},

                    ${currentUserName} has created a new objective that reports to you.

                    **Objective Details:**
                    - Name: ${savedObjective.name}
                    - Description: ${savedObjective.description || 'No description provided'}
                    - Target Date: ${savedObjective.completionDate ? new Date(savedObjective.completionDate).toLocaleDateString() : 'Not specified'}
                    - Created By: ${currentUserName}
                    - Objective ID: ${savedObjective.id}
                    ${parentObjectiveId ? `- Parent Objective ID: ${parentObjectiveId}` : ''}

                    This objective has been created as part of your team's performance goals.

                    Please review this objective in the Performance Management System.

                    Best regards,
                    Performance Management System
            `.trim()
            };

            
            try {
                console.log('📧 [EMAIL] Sending message...');
                const result = await engine.mutate({
                    resource: 'messageConversations',
                    type: 'create',
                    data: {
                        subject: emailData.subject,
                        text: emailData.message,
                        users: [{ id: recipient.id }],
                        organisationUnits: [],
                        userGroups: []
                    }
                });

                console.log('✅ [EMAIL] Sent successfully to:', recipient.displayName);

                // Mark as sent to prevent duplicates
                sessionStorage.setItem(notificationKey, 'true');
                setTimeout(() => {
                    sessionStorage.removeItem(notificationKey);
                }, 3600000); // 1 hour

                return {
                    success: true,
                    message: 'Email sent successfully',
                    recipientName: recipient.displayName,
                    recipientEmail: recipient.email,

                    response: result
                };

            } catch (apiError) {
                console.warn('⚠️ [EMAIL] DHIS2 API failed:', apiError);

                // Log for manual sending
                console.log('📋 [EMAIL DEBUG] Manual send details:', {
                    to: recipient.email,
                    subject: emailData.subject,
                    body: emailData.message
                });

                return {
                    success: false,
                    error: 'Email API failed',
                    recipientName: recipient.displayName,
                    recipientEmail: recipient.email,
                    warning: `Please notify ${recipient.displayName} manually.`
                };
            }

        } catch (error) {
            console.error('❌ [EMAIL] Critical error:', error);
            return {
                success: false,
                error: error.message,
                warning: 'Email notification failed'
            };
        }
    };
    // Add this function to debug position data
    const debugPositionData = async () => {
        if (!currentUserPosition?.trackedEntityInstance) return;

        console.log('🔍 [POSITION DEBUG] Current position TEI:', currentUserPosition.trackedEntityInstance);
        console.log('🔍 [POSITION DEBUG] Current position attributes:', currentUserPosition.attributes);

        const attrs = extractAttributes(currentUserPosition.attributes || []);
        console.log('🔍 [POSITION DEBUG] Extracted attributes:', {
            reportingTo: attrs['SjP47qi3JeB'],
            incumbentId: attrs['V66IFsw5kq6'],
            positionName: attrs['w3bwqUZoc6V'] || attrs['g3RRIfDiQra']
        });

    }

    // Add this debug function to check program assignments
    const checkProgramAssignments = async () => {
        try {

            // Check if program is assigned to org unit
            const programResponse = await engine.query({
                programOrgUnits: {
                    resource: `programs/yiT00XKrrHP`,
                    params: {
                        fields: 'id,name,programType,organisationUnits[id,name]'
                    }
                }
            });

            const program = programResponse.programOrgUnits;

            // Check if our org unit is in the list
            const userOrgUnitId = user?.organisationUnits?.[0]?.id;
            const isAssigned = program.organisationUnits?.some(ou => ou.id === userOrgUnitId);


            return isAssigned;

        } catch (error) {
            console.error('Error checking program assignments:', error);
            return false;
        }
    };

    // Call this in your useEffect
    useEffect(() => {
        if (user?.organisationUnits?.[0]?.id) {
            checkProgramAssignments();
        }
    }, [user, engine]);

    // Add this debug function
    const checkProgramStage = async () => {
        try {
            const response = await engine.query({
                programStage: {
                    resource: 'programStages/VIBtpvkpHFR',
                    params: { fields: 'id,name,repeatable' }
                }
            });


            const response2 = await engine.query({
                programStage: {
                    resource: 'programStages/VIBtpvkpHFR',
                    params: { fields: 'id,name,repeatable' }
                }
            });

        } catch (error) {
            console.error('Error checking program stage:', error);
        }
    };

    // Call it
    useEffect(() => {
        checkProgramStage();
    }, []);



    const renderGoalSelection = () => {
        // ✅ Only show for top-level users (no "Reporting To")
        if (!currentUserPosition) return null;

        const attrs = extractAttributes(currentUserPosition.attributes);
        const isTopLevelUser = !attrs['SjP47qi3JeB']; // No manager
        if (!isTopLevelUser) return null;

        return (
            <div style={{
                backgroundColor: '#f8fafc',
                padding: '16px',
                borderRadius: '4px',
                marginBottom: '16px',
                border: `1px solid ${colors.grey300}`
            }}>
                <h4 style={{
                    marginTop: 0,
                    marginBottom: '16px',
                    color: colors.blue700,
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                }}>
                    <span>🎯</span>
                    Link to Company Goal
                </h4>
                <div style={{ marginTop: '20px' }}>
                    <h5 style={{
                        margin: '0 0 12px 0',
                        color: colors.blue700,
                        fontSize: '0.9rem',

                    }}>
                        Available Company Goals ({goals.length})
                    </h5>
                    <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
                        gap: '12px',
                        maxHeight: '250px',
                        overflowY: 'auto',
                        padding: '8px',
                        border: `1px solid ${colors.grey300}`,
                        borderRadius: '4px',
                        backgroundColor: 'white',
                        paddingBottom: '10px'
                    }}>
                        {goals.map(goal => (
                            <DraggableGoalCard
                                key={goal.id}
                                goal={goal}
                                isSelected={selectedGoal === goal.id}
                                onSelect={setSelectedGoal}
                            />
                        ))}
                    </div>
                </div>

                {loadingGoals ? (
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        padding: '20px',
                        justifyContent: 'center'
                    }}>
                        <CircularLoader small />
                        <span>Loading company goals...</span>
                    </div>
                ) : goals.length === 0 ? (
                    <div style={{
                        padding: '16px',
                        backgroundColor: colors.yellow050,
                        border: `1px solid ${colors.yellow300}`,
                        borderRadius: '4px',
                        color: colors.yellow800,
                        textAlign: 'center'
                    }}>
                        <p style={{ margin: 0 }}>
                            No company goals found. Please create goals in the Goals Management section first.
                        </p>
                    </div>
                ) : (
                    <>
                        <GoalDropZone
                            onDrop={(goalId) => setSelectedGoal(goalId)}
                            selectedGoal={selectedGoal}
                            goals={goals}
                            onClear={() => setSelectedGoal('')}
                        />

                    </>
                )}
            </div>
        );
    };


    const renderHigherLevelObjectives = () => {
        if (!currentUserPosition) {
            return (
                <div style={{
                    padding: '16px',
                    backgroundColor: '#fff3cd',
                    border: '1px solid #ffeaa7',
                    borderRadius: '4px',
                    marginBottom: '16px',
                    textAlign: 'center'
                }}>
                    <p style={{ margin: 0, color: '#856404' }}>
                        Loading position information...
                    </p>
                </div>
            );
        }

        // Get user attributes to determine if they're top-level
        const attributes = extractAttributes(currentUserPosition.attributes || []);
        const isTopLevelUser = !attributes['SjP47qi3JeB']; // No manager = top-level

        // Top-level users don't need to align with higher-level objectives
        if (isTopLevelUser) return null;
 
        if (loadingHigherLevelObjectives) {
            return (
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '20px',
                    justifyContent: 'center',
                    backgroundColor: '#e3f2fd',
                    borderRadius: '4px',
                    marginBottom: '16px'
                }}>
                    <CircularLoader small />
                    <span>Loading higher-level objectives...</span>
                </div>
            );
        }


        const hasSelected = selectedHigherLevelObjective && selectedHigherLevelObjective.id;
        const isDeleted = selectedHigherLevelObjective?.isDeleted;
        const isLoading = selectedHigherLevelObjective?.isLoading;

        // ✅ If we have a string ID from context, show loading state
        if (typeof selectedHigherLevelObjective === 'string' || selectedHigherLevelObjective?.isLoading) {
            return (
                <div style={{
                    backgroundColor: '#e3f2fd',
                    padding: '16px',
                    borderRadius: '4px',
                    marginBottom: '16px',
                    border: '1px solid #bbdefb'
                }}>
                    <h4 style={{
                        marginTop: 0,
                        marginBottom: '16px',
                        color: '#0d47a1',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                    }}>
                        <span>⏳</span>
                        Loading Aligned Objective
                    </h4>
                    <div style={{
                        padding: '16px',
                        backgroundColor: '#ffffff',
                        border: '1px solid #bbdefb',
                        borderRadius: '4px',
                        textAlign: 'center'
                    }}>
                        <CircularLoader small />
                        <p style={{ margin: '12px 0 0 0', color: '#0d47a1' }}>
                            Loading objective details...
                        </p>
                    </div>
                </div>
            );
        }


        if (hasSelected && !isDeleted && !isLoading) {

            let managerGoalName = null;


            const directGoalId = selectedHigherLevelObjective.dataValues?.find(
                dv => dv.dataElement === 'VhrIQQIVpB9'
            )?.value;

            if (directGoalId) {
                managerGoalName = goals.find(g => g.id === directGoalId)?.name;
                console.log('✅ [DIRECT GOAL FOUND]', { directGoalId, managerGoalName });
            } else {
                console.log('🔍 [DIRECT GOAL NOT FOUND] - trying hierarchical lookup');


                const ceoObjectiveId = selectedHigherLevelObjective.dataValues?.find(
                    dv => dv.dataElement === 'neuilg3L4d2'
                )?.value;

                if (ceoObjectiveId) {
                    console.log('🔍 [CEO OBJECTIVE FOUND]', ceoObjectiveId);


                    try {
                        const ceoObjectiveResponse = engine.query({
                            ceoObjective: {
                                resource: `events/${ceoObjectiveId}`,
                                params: { fields: 'event,dataValues[dataElement,value]' }
                            }
                        });

                        if (ceoObjectiveResponse.ceoObjective) {
                            const ceoGoalId = ceoObjectiveResponse.ceoObjective.dataValues?.find(
                                dv => dv.dataElement === 'VhrIQQIVpB9'
                            )?.value;

                            if (ceoGoalId) {
                                managerGoalName = goals.find(g => g.id === ceoGoalId)?.name;
                                console.log('✅ [HIERARCHICAL GOAL FOUND]', { ceoGoalId, managerGoalName });
                            } else {
                                console.warn('⚠️ [CEO GOAL NOT FOUND] CEO objective has no goal linkage', ceoObjectiveId);
                            }
                        } else {
                            console.warn('⚠️ [CEO OBJECTIVE NOT FOUND]', ceoObjectiveId);
                        }
                    } catch (error) {
                        console.error('❌ Failed to fetch CEO objective:', error);
                    }
                } else {
                    console.warn('⚠️ [CEO OBJECTIVE NOT LINKED] Manager objective not aligned to CEO objective');
                }
            }

            return (
                <div style={{
                    backgroundColor: '#e8f5e9',
                    padding: '16px',
                    borderRadius: '4px',
                    marginBottom: '16px',
                    border: '1px solid #c8e6c9'
                }}>
                    <h4 style={{
                        marginTop: 0,
                        marginBottom: '16px',
                        color: '#2e7d32',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                    }}>
                        <span>✅</span>
                        Currently Aligned With
                    </h4>



                    {/* ✅ SHOW THE STRATEGIC GOAL CONTEXT - IMPROVED VERSION */}
                    {managerGoalName ? (
                        <div style={{
                            marginTop: '12px',
                            padding: '10px',
                            backgroundColor: '#e8f5e9',
                            border: '1px solid #4caf50',
                            borderRadius: '4px',
                            position: 'relative',
                            paddingLeft: '32px'
                        }}>
                            <div style={{
                                position: 'absolute',
                                left: '12px',
                                top: '12px',
                                width: '16px',
                                height: '16px',
                                backgroundColor: '#2e7d32',
                                borderRadius: '50%'
                            }} />
                            <strong style={{ color: '#1b5e20', fontSize: '1.05rem' }}>🎯 Strategic Goal:</strong>
                            <p style={{
                                margin: '6px 0 0 0',
                                fontSize: '1.1rem',
                                color: '#2e7d32',
                                fontStyle: 'italic'
                            }}>
                                "{managerGoalName}"
                            </p>
                            <p style={{
                                margin: '8px 0 0 0',
                                fontSize: '0.9rem',
                                color: '#555'
                            }}>
                                Your objective will support this manager objective, which aligns with the company strategic goal above.
                            </p>
                        </div>
                    ) : (
                        <div style={{
                            marginTop: '12px',
                            padding: '10px',
                            backgroundColor: '#fff3e0',
                            border: '1px solid #ffcc80',
                            borderRadius: '4px',
                            fontSize: '0.85rem'
                        }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <span>ℹ️</span>
                                <p style={{ margin: 0 }}>
                                    <strong>Strategic Context:</strong> This manager objective supports company goals.
                                    Your objective will contribute to this strategic alignment.
                                </p>
                            </div>
                            {/* Show ONLY the specific goal this manager objective is aligned with */}
                            {(() => {
                                // Try to find the manager's goal from dataValues
                                const managerGoalId = selectedHigherLevelObjective.dataValues?.find(
                                    dv => dv.dataElement === 'VhrIQQIVpB9'
                                )?.value;

                                if (managerGoalId && goals.length > 0) {
                                    const alignedGoal = goals.find(g => g.id === managerGoalId);
                                    if (alignedGoal) {
                                        return (
                                            <div style={{ marginTop: '8px', paddingTop: '8px', borderTop: '1px solid #ffecb3' }}>
                                                <p style={{ margin: '4px 0', fontSize: '0.8rem', color: '#b6890c' }}>
                                                    📌 Aligned Company Goal:
                                                </p>
                                                <p style={{
                                                    margin: '2px 0 2px 16px',
                                                    fontSize: '0.85rem',
                                                    color: '#2e7d32',
                                                    fontStyle: 'italic',
                                                    fontWeight: 'bold'
                                                }}>
                                                    • {alignedGoal.name}
                                                </p>
                                                {alignedGoal.description && alignedGoal.description !== alignedGoal.name && (
                                                    <p style={{
                                                        margin: '2px 0 2px 16px',
                                                        fontSize: '0.8rem',
                                                        color: '#555',
                                                        fontStyle: 'italic'
                                                    }}>
                                                        {alignedGoal.description}
                                                    </p>
                                                )}
                                            </div>
                                        );
                                    }
                                }

                                // Fallback: Show top 1-2 goals if we can't find the specific one
                                return goals.length > 0 && (
                                    <div style={{ marginTop: '8px', paddingTop: '8px', borderTop: '1px solid #ffecb3' }}>
                                        <p style={{ margin: '4px 0', fontSize: '0.8rem', color: '#b6890c' }}>
                                            📌 Company Goals in Scope:
                                        </p>
                                        {goals.slice(0, 2).map((goal, index) => (
                                            <p key={index} style={{
                                                margin: '2px 0 2px 16px',
                                                fontSize: '0.8rem',
                                                color: '#2e7d32',
                                                fontStyle: 'italic'
                                            }}>
                                                • {goal.name}
                                            </p>
                                        ))}
                                        {goals.length > 2 && (
                                            <p style={{
                                                margin: '4px 0 0 16px',
                                                fontSize: '0.75rem',
                                                fontStyle: 'italic',
                                                color: '#555'
                                            }}>
                                                + {goals.length - 2} more strategic goal{goals.length > 3 ? 's' : ''}
                                            </p>
                                        )}
                                    </div>
                                );
                            })()}
                        </div>
                    )}

                    {/* Rest of your manager objective display remains the same... */}
                    <div style={{
                        padding: '16px',
                        backgroundColor: '#ffffff',
                        border: '1px solid #c8e6c9',
                        borderRadius: '4px'
                    }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                            <div style={{ flex: 1 }}>
                                <p style={{ margin: '0 0 8px 0', fontWeight: 'bold', fontSize: '1.1rem' }}>
                                    {selectedHigherLevelObjective.name}
                                </p>
                                {selectedHigherLevelObjective.description && (
                                    <p style={{ margin: '0 0 12px 0', fontSize: '0.9rem', color: '#666' }}>
                                        {selectedHigherLevelObjective.description}
                                    </p>
                                )}
                            </div>
                            <Button
                                small
                                destructive
                                onClick={() => {
                                    if (window.confirm('Change the aligned objective?')) {
                                        setSelectedHigherLevelObjective(null);
                                        if (objectives.length > 0) {
                                            updateObjectiveField(0, 'objectiveAlignment', '');
                                            updateObjectiveField(0, 'objectiveRoleAssigned', '');
                                            updateObjectiveField(0, 'higherLevelObjectiveId', '');
                                            updateObjectiveField(0, 'higherLevelObjectiveName', '');
                                            updateObjectiveField(0, '_managerGoalName', '');
                                        }
                                    }
                                }}
                                style={{ marginLeft: '12px', flexShrink: 0 }}
                            >
                                Change Objective
                            </Button>
                        </div>
                    </div>
                </div>
            );
        }
        // Rest of the function remains the same...
        // (loading states, deleted states, available objectives list)

        return (
            <div style={{
                backgroundColor: '#f8fafc',
                padding: '16px',
                borderRadius: '4px',
                marginBottom: '16px',
                border: '1px solid #e9ecef'
            }}>
                <h4 style={{
                    marginTop: 0,
                    marginBottom: '16px',
                    color: '#2c6693',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                }}>
                    <span>⬆️</span>
                    Select Higher-Level Objective to Align With
                </h4>

                {/* Available objectives list */}
                {higherLevelObjectives.length > 0 ? (
                    <>
                        <div style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
                            gap: '12px',
                            maxHeight: '250px',
                            overflowY: 'auto',
                            padding: '8px',
                            borderRadius: '8px',
                            backgroundColor: '#ffffff',
                            border: '1px solid #dee2e6',
                            marginBottom: '16px'
                        }}>
                            {higherLevelObjectives.map((objective) => (
                                <DraggableHigherLevelObjective
                                    key={objective.id}
                                    objective={objective}
                                    isSelected={selectedHigherLevelObjective?.id === objective.id}
                                    onSelect={handleHigherLevelObjectiveSelect}
                                />
                            ))}
                        </div>

                        {/* Drop zone */}
                        <HigherLevelObjectiveDropZone
                            onDrop={handleHigherLevelObjectiveSelect}
                            selectedObjective={selectedHigherLevelObjective}
                            onClear={() => {
                                setSelectedHigherLevelObjective(null);
                                if (objectives.length > 0) {
                                    updateObjectiveField(0, 'higherLevelObjectiveId', '');
                                    updateObjectiveField(0, 'objectiveAlignment', '');
                                    updateObjectiveField(0, 'objectiveRoleAssigned', '');
                                    updateObjectiveField(0, 'higherLevelObjectiveName', '');
                                    updateObjectiveField(0, '_managerGoalName', '');
                                }
                            }}
                        />
                    </>
                ) : (
                    <div style={{
                        padding: '16px',
                        backgroundColor: '#d0ebb2ff',
                        border: '2px solid #fcfdfaff',
                        borderRadius: '4px',
                        color: '#47c515ff',
                        textAlign: 'center',
                        boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                    }}>
                        <p style={{ margin: 0 }}>
                            No objectives found from your reporting manager.
                            They may not have created any objectives yet.
                        </p>
                    </div>
                )}
            </div>
        );
    };

    const GoalSelectionSection = () => {
        return (
            <div style={{ marginBottom: '24px', backgroundColor: '#f9f9f9', padding: '20px', borderRadius: '8px', border: '1px solid #e0e0e0' }}>
                <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '16px',
                    padding: '12px',
                    backgroundColor: '#e8f5e9',
                    borderRadius: '6px'
                }}>
                    <h3 style={{
                        margin: 0,
                        color: '#2e7d32',
                        fontSize: '1.15rem',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px'
                    }}>
                        <div style={{
                            width: '28px',
                            height: '28px',
                            backgroundColor: '#4caf50',
                            color: 'white',
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '1.1rem',
                            fontWeight: 'bold'
                        }}>
                            1
                        </div>
                        🎯 Select Strategic Goal
                    </h3>
                    <span style={{
                        backgroundColor: '#4caf50',
                        color: 'white',
                        padding: '4px 10px',
                        borderRadius: '12px',
                        fontSize: '0.85rem',
                        fontWeight: 'bold'
                    }}>
                        {goals.length} goals available
                    </span>
                </div>

                {loadingGoals ? (
                    <div style={{ textAlign: 'center', padding: '20px' }}>
                        <CircularLoader small />
                        <p style={{ color: '#1565c0', marginTop: '8px' }}>Loading company goals...</p>
                    </div>
                ) : goals.length === 0 ? (
                    <div style={{
                        padding: '16px',
                        backgroundColor: '#fff8e1',
                        border: '1px solid #ffecb3',
                        borderRadius: '4px',
                        textAlign: 'center'
                    }}>
                        <p style={{ margin: 0, color: '#b6890c' }}>
                            No company goals available. Please contact your administrator.
                        </p>
                    </div>
                ) : (
                    <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
                        gap: '16px',
                        maxHeight: '300px',
                        overflowY: 'auto',
                        padding: '8px',
                        backgroundColor: '#f8fafc',
                        border: '1px solid #e9ecef',
                        borderRadius: '8px'
                    }}>
                        {goals.map(goal => (
                            <div
                                key={goal.id}
                                onClick={() => handleGoalSelect(goal.id)}
                                style={{
                                    padding: '16px',
                                    border: selectedGoal === goal.id ? '2px solid #2e7d32' : '1px solid #c8e6c9',
                                    borderRadius: '6px',
                                    backgroundColor: selectedGoal === goal.id ? '#e8f5e9' : '#ffffff',
                                    cursor: 'pointer',
                                    transition: 'all 0.2s',
                                    boxShadow: selectedGoal === goal.id ? '0 2px 8px rgba(46, 125, 50, 0.2)' : '0 1px 4px rgba(0,0,0,0.1)'
                                }}
                            >
                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
                                    <div style={{
                                        width: '24px',
                                        height: '24px',
                                        backgroundColor: '#4caf50',
                                        color: 'white',
                                        borderRadius: '50%',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        flexShrink: 0,
                                        fontSize: '0.9rem',
                                        fontWeight: 'bold'
                                    }}>
                                        G
                                    </div>
                                    <strong style={{ color: '#1b5e20', fontSize: '1.1rem' }}>{goal.name}</strong>
                                </div>

                                {goal.description && goal.description !== goal.name && (
                                    <p style={{
                                        margin: '4px 0',
                                        fontSize: '0.9rem',
                                        color: '#555',
                                        lineHeight: 1.4,
                                        maxHeight: '60px',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis'
                                    }}>
                                        {goal.description}
                                    </p>
                                )}

                                {goal.startDate && goal.endDate && (
                                    <div style={{
                                        fontSize: '0.8rem',
                                        color: '#555',
                                        marginTop: '8px',
                                        fontStyle: 'italic'
                                    }}>
                                        Timeline: {new Date(goal.startDate).toLocaleDateString()} - {new Date(goal.endDate).toLocaleDateString()}
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                )}

                {selectedGoal && (
                    <div style={{
                        marginTop: '16px',
                        padding: '12px',
                        backgroundColor: '#e3f2fd',
                        border: '1px solid #bbdefb',
                        borderRadius: '4px',
                        fontSize: '0.9rem'
                    }}>
                        <p style={{ margin: '0', lineHeight: 1.5, color: '#0d47a1' }}>
                            <strong> Goal Selected:</strong> Select one to create your objective that supports this strategic direction.
                        </p>
                    </div>
                )}
            </div>
        );
    };

    const FilteredManagerObjectives = () => {
        // Only show manager objectives if a goal is selected
        if (!selectedGoal) {
            return (
                <div style={{
                    textAlign: 'center',
                    padding: '32px',
                    backgroundColor: '#f8fafc',
                    border: '1px solid #e9ecef',
                    borderRadius: '8px',
                    marginBottom: '24px'
                }}>
                    <div style={{
                        fontSize: '48px',
                        color: '#9c27b0',
                        marginBottom: '16px'
                    }}>🎯</div>
                    <h4 style={{ color: '#2c6693', marginBottom: '8px', fontSize: '1.2rem' }}>
                        Select a Strategic Goal First
                    </h4>
                    <p style={{ color: '#555', maxWidth: '600px', margin: '0 auto' }}>
                        Choose a company goal above to see the manager objectives aligned to it.
                        Then you can select one to create your own supporting objective.
                    </p>
                </div>
            );
        }

        // Group objectives by manager name for better organization
        const groupedObjectives = filteredManagerObjectives.reduce((groups, objective) => {
            const managerName = objective.createdBy || 'Unknown Manager';
            if (!groups[managerName]) {
                groups[managerName] = [];
            }
            groups[managerName].push(objective);
            return groups;
        }, {});

        return (
            <div style={{ marginBottom: '24px', backgroundColor: '#f9f9f9', padding: '20px', borderRadius: '8px', border: '1px solid #e0e0e0' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', padding: '12px', backgroundColor: '#e3f2fd', borderRadius: '6px' }}>
                    <h3 style={{ margin: 0, color: '#0d47a1', fontSize: '1.15rem', display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <div style={{ width: '28px', height: '28px', backgroundColor: '#2196f3', color: 'white', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.1rem', fontWeight: 'bold' }}>
                            2
                        </div>
                        👥 Manager Objectives for Selected Goal
                    </h3>
                    <span style={{
                        backgroundColor: '#e3f2fd',
                        color: '#0d47a1',
                        padding: '4px 10px',
                        borderRadius: '12px',
                        fontSize: '0.85rem',
                        fontWeight: 'bold'
                    }}>
                        {filteredManagerObjectives.length} objectives found
                    </span>
                </div>

                {loadingFilteredObjectives ? (
                    <div style={{ textAlign: 'center', padding: '20px' }}>
                        <CircularLoader small />
                        <p style={{ color: '#1565c0', marginTop: '8px' }}>Loading manager objectives aligned to this goal...</p>
                    </div>
                ) : filteredManagerObjectives.length === 0 ? (
                    <div style={{
                        padding: '24px',
                        backgroundColor: '#fff3cd',
                        border: '1px solid #ffeaa7',
                        borderRadius: '8px',
                        textAlign: 'center'
                    }}>
                        <div style={{
                            fontSize: '36px',
                            color: '#e65100',
                            marginBottom: '16px'
                        }}>⚠️</div>
                        <h4 style={{ color: '#856404', marginBottom: '8px', fontSize: '1.1rem' }}>
                            No Manager Objectives Found
                        </h4>
                        <p style={{ color: '#856404', maxWidth: '600px', margin: '0 auto' }}>
                            No manager objectives are currently aligned to this strategic goal.
                            You may need to:
                            <ul style={{ textAlign: 'left', marginTop: '12px', paddingLeft: '24px', lineHeight: 1.5 }}>
                                <li>Select a different company goal</li>
                                <li>Contact your manager to create objectives for this goal</li>
                                <li>Check back later as objectives are added</li>
                            </ul>
                        </p>
                    </div>
                ) : (
                    <>
                        {/* Goal context banner */}
                        <div style={{
                            marginBottom: '20px',
                            padding: '16px',
                            backgroundColor: '#e8f5e9',
                            border: '1px solid #4caf50',
                            borderRadius: '6px'
                        }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                <div style={{
                                    width: '28px',
                                    height: '28px',
                                    backgroundColor: '#2e7d32',
                                    borderRadius: '50%',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    color: 'white',
                                    fontSize: '1.1rem',
                                    fontWeight: 'bold'
                                }}>
                                    G
                                </div>
                                <div>
                                    <strong style={{ color: '#1b5e20', fontSize: '1.1rem' }}>Strategic Goal:</strong>
                                    <p style={{
                                        margin: '6px 0 0 0',
                                        fontSize: '1.15rem',
                                        fontWeight: 'bold',
                                        color: '#2e7d32',
                                        fontStyle: 'italic'
                                    }}>
                                        "{goals.find(g => g.id === selectedGoal)?.name}"
                                    </p>
                                    <p style={{
                                        margin: '8px 0 0 0',
                                        fontSize: '0.95rem',
                                        color: '#555'
                                    }}>
                                        Select a manager objective below to create your supporting objective
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* Grouped objectives by manager */}
                        {Object.entries(groupedObjectives).map(([managerName, objectives]) => (
                            <div key={managerName} style={{ marginBottom: '24px' }}>
                                <div style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px',
                                    marginBottom: '12px',
                                    padding: '8px',
                                    backgroundColor: '#e3f2fd',
                                    borderRadius: '4px'
                                }}>
                                    <div style={{
                                        width: '24px',
                                        height: '24px',
                                        backgroundColor: '#2196f3',
                                        color: 'white',
                                        borderRadius: '50%',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        fontSize: '0.85rem',
                                        fontWeight: 'bold'
                                    }}>
                                        M
                                    </div>
                                    <strong style={{ color: '#0d47a1', fontSize: '1.05rem' }}>
                                        {managerName}'s Objectives ({objectives.length})
                                    </strong>
                                </div>

                                <div style={{
                                    display: 'grid',
                                    gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
                                    gap: '16px'
                                }}>
                                    {objectives.map(objective => (
                                        <div
                                            key={objective.id}
                                            onClick={() => handleHigherLevelObjectiveSelect(objective)}
                                            style={{
                                                padding: '16px',
                                                border: selectedHigherLevelObjective?.id === objective.id ? '2px solid #2196f3' : '1px solid #bbdefb',
                                                borderRadius: '6px',
                                                backgroundColor: selectedHigherLevelObjective?.id === objective.id ? '#e3f2fd' : '#ffffff',
                                                cursor: 'pointer',
                                                transition: 'all 0.2s',
                                                boxShadow: '0 1px 4px rgba(0,0,0,0.1)'
                                            }}
                                        >
                                            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                                                <div style={{
                                                    minWidth: '24px',
                                                    height: '24px',
                                                    backgroundColor: '#9c27b0',
                                                    color: 'white',
                                                    borderRadius: '50%',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    fontSize: '0.8rem',
                                                    flexShrink: 0
                                                }}>
                                                    M
                                                </div>
                                                <div style={{ flex: 1 }}>
                                                    <strong style={{
                                                        color: '#4a148c',
                                                        fontSize: '1.1rem',
                                                        display: 'block',
                                                        marginBottom: '4px'
                                                    }}>
                                                        {objective.name}
                                                    </strong>
                                                    {objective.description && (
                                                        <p style={{
                                                            margin: '0 0 4px 0',
                                                            fontSize: '0.9rem',
                                                            color: '#555',
                                                            lineHeight: 1.4,
                                                            maxHeight: '60px',
                                                            overflow: 'hidden',
                                                            textOverflow: 'ellipsis'
                                                        }}>
                                                            {objective.description}
                                                        </p>
                                                    )}
                                                    <div style={{
                                                        fontSize: '0.8rem',
                                                        color: '#555',
                                                        marginTop: '8px',
                                                        display: 'flex',
                                                        gap: '12px',
                                                        flexWrap: 'wrap'
                                                    }}>
                                                        <span>👤 Owner: {objective.createdBy}</span>
                                                        <span>📅 Created: {objective.dateCreated ? new Date(objective.dateCreated).toLocaleDateString() : 'N/A'}</span>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ))}

                        {/* Clear selection button */}
                        {selectedHigherLevelObjective && (
                            <div style={{ marginTop: '16px', textAlign: 'center' }}>
                                <Button
                                    destructive
                                    onClick={handleClearGoalSelection}
                                    style={{ padding: '8px 16px' }}
                                >
                                    Clear Selection & Choose Different Goal
                                </Button>
                            </div>
                        )}
                    </>
                )}
            </div>
        );
    };

    // Fetch status options
    useEffect(() => {
        const fetchInitialData = async () => {
            try {
                const statusResponse = await engine.query({
                    optionSet: {
                        resource: 'optionSets/U8X3RbnJrYs',
                        params: { fields: 'options[name,code]' }
                    }
                });
                setStatusOptions(statusResponse.optionSet.options);
            } catch (error) {
                console.error('Error fetching initial data:', error);
            }
        };
        fetchInitialData();
    }, [engine]);

    useEffect(() => {
        const detectAndSetGoal = async () => {
            if (!editingObjective || loadingGoals || !goals.length) return;

            let goalId = editingObjective.companyAlignedGoal;

            // Check rawDataValues if no direct goal
            if (!goalId && editingObjective.rawDataValues) {
                const goalDataValue = editingObjective.rawDataValues.find(
                    dv => dv.dataElement === 'VhrIQQIVpB9'
                );
                goalId = goalDataValue?.value;
            }

            // Set goal if found and valid
            if (goalId && goals.some(g => g.id === goalId) && selectedGoal !== goalId) {
                console.log('🎯 [GOAL SET] Setting goal:', goalId);
                setSelectedGoal(goalId);

                // Update form state once
                updateObjectiveField(0, 'companyAlignedGoal', goalId);
            }
        };

        detectAndSetGoal();
    }, [editingObjective, loadingGoals, goals]); // Remove unnecessary dependencies


    useEffect(() => {
        let isMounted = true;
        let hasRun = false;

        const loadExistingData = async () => {
            if (!objective || !objective.id || !isMounted || !mounted || hasRun) return;
            hasRun = true;

            try {
                setUiMode('edit');

                // Use the unified service to fetch objective with all details
                const { objective: objectiveDetails, kras, kpis } = await fetchObjectiveWithDetails(
                    objective.id,
                    engine
                );

                if (!isMounted || !mounted) return;

                // Handle case where objectiveDetails might not have rawDataValues
                const rawDataValues = objectiveDetails.rawDataValues || objectiveDetails.dataValues || [];

                const managerGoalId = rawDataValues.find(
                    dv => dv.dataElement === 'VhrIQQIVpB9'
                )?.value;

                const managerGoalName = managerGoalId ? goalNameById[managerGoalId] : null;

                // Normalize detailed KPI/KRA payload once
                const normalizedKras = kras.length > 0
                    ? kras.map(kra => ({
                        ...kra,
                        name: kra.name != null ? String(kra.name).trim() : '',
                        description: kra.description != null ? String(kra.description).trim() : '',
                        weight: typeof kra.weight === 'number' ? kra.weight : 0
                    }))
                    : [{ name: '', description: '', weight: 0 }];

                const normalizedKpis = kpis.length > 0
                    ? kpis.map(kpi => ({
                        ...kpi,
                        target: kpi.target != null ? String(kpi.target).trim() : '',
                        name: kpi.name != null ? String(kpi.name).trim() : '',
                        description: kpi.description != null ? String(kpi.description).trim() : ''
                    }))
                    : [{ name: '', description: '', target: '' }];

                console.log('🎯 [LOAD EXISTING DEBUG] Editing objective enrollment:', objectiveDetails.enrollmentId || objectiveDetails.enrollment);

                // In edit mode with enrollment, ALWAYS merge detailed KPI/KRA payload into the edited objective.
                if (objectiveDetails.enrollmentId || objectiveDetails.enrollment) {
                    const enrollmentId = objectiveDetails.enrollmentId || objectiveDetails.enrollment;
                    console.log('🎯 [LOAD EXISTING] Edit mode with enrollment - merging objectives');
                    setObjectives(prev => {
                        const detailedObjective = {
                            id: objectiveDetails.id || null,
                            name: objectiveDetails.name || '',
                            _managerGoalName: managerGoalName,
                            description: objectiveDetails.description || '',
                            weight: objectiveDetails.weight || 0,
                            startDate: objectiveDetails.startDate || fiscalYear.start,
                            completionDate: objectiveDetails.completionDate || fiscalYear.end,
                            orgUnit: objectiveDetails.orgUnit || user?.organisationUnits?.[0]?.id || '',
                            remarks: objectiveDetails.remarks || '',
                            _debugRemarks: {
                                raw: objectiveDetails.remarks,
                                length: (objectiveDetails.remarks || '').length,
                                isEmpty: !objectiveDetails.remarks
                            },
                            score: objectiveDetails.score || 0,
                            performanceYear: objectiveDetails.performanceYear || new Date().getFullYear(),
                            workType: objectiveDetails.workType || '',
                            companyAlignedGoal: objectiveDetails.companyAlignedGoal || '',
                            objectiveAlignment: objectiveDetails.objectiveAlignment || '',
                            objectiveRoleAssigned: objectiveDetails.objectiveRoleAssigned || '',
                            enrollmentId,
                            kras: normalizedKras,
                            kpis: normalizedKpis
                        };

                        const hasMatch = prev.some(obj => obj.id === objectiveDetails.id);
                        if (hasMatch) {
                            return prev.map(obj =>
                                obj.id === objectiveDetails.id ? { ...obj, ...detailedObjective } : obj
                            );
                        }

                        // Fallback for initial edit state where the objective row is not present yet.
                        if (prev.length <= 1) return [detailedObjective];
                        return [...prev, detailedObjective];
                    });
                } else {
                    // No enrollment - set single objective (normal behavior)
                    setObjectives([{
                        id: objectiveDetails.id || null,
                        name: objectiveDetails.name || '',
                        _managerGoalName: managerGoalName,
                        description: objectiveDetails.description || '',
                        weight: objectiveDetails.weight || 0,
                        startDate: objectiveDetails.startDate || fiscalYear.start,
                        completionDate: objectiveDetails.completionDate || fiscalYear.end,
                        orgUnit: objectiveDetails.orgUnit || user?.organisationUnits?.[0]?.id || '',
                        remarks: objectiveDetails.remarks || '',
                        score: objectiveDetails.score || 0,
                        performanceYear: objectiveDetails.performanceYear || new Date().getFullYear(),
                        workType: objectiveDetails.workType || '',
                        companyAlignedGoal: objectiveDetails.companyAlignedGoal || '',
                        objectiveAlignment: objectiveDetails.objectiveAlignment || '',
                        objectiveRoleAssigned: objectiveDetails.objectiveRoleAssigned || '',

                        kras: normalizedKras,
                        kpis: normalizedKpis,
                    }]);
                }

                // ✅ CRITICAL FIX: Handle objective alignment for non-CEO users
                if (objectiveDetails.objectiveAlignment) {
                    console.log('🎯 [LOAD EXISTING] Loading aligned objective:', objectiveDetails.objectiveAlignment);

                    try {
                        const alignedObjective = await loadHigherLevelObjectiveDetails(objectiveDetails.objectiveAlignment);

                        if (isMounted && mounted && alignedObjective) {
                            // ✅ FIX: Make sure we're setting the FULL OBJECT, not just the ID
                            console.log('✅ [LOAD EXISTING] Setting full aligned objective:', {
                                id: alignedObjective.id,
                                name: alignedObjective.name,
                                type: typeof alignedObjective
                            });

                            // Set the full object in state
                            setSelectedHigherLevelObjective(alignedObjective);

                            // Also set the goal name for the aligned objective if available
                            const alignedGoalId = alignedObjective.dataValues?.find(
                                dv => dv.dataElement === 'VhrIQQIVpB9'
                            )?.value;

                            if (alignedGoalId && goals.length > 0) {
                                const alignedGoal = goals.find(g => g.id === alignedGoalId);
                                if (alignedGoal && isMounted && mounted) {
                                    // Update the objective with the manager goal name
                                    setObjectives(prev => prev.map(obj => ({
                                        ...obj,
                                        _managerGoalName: alignedGoal.name
                                    })));
                                }
                            }

                            // ✅ Also update form fields for consistency
                            updateObjectiveField(0, 'objectiveAlignment', alignedObjective.id);
                            updateObjectiveField(0, 'objectiveRoleAssigned', objectiveDetails.objectiveRoleAssigned);
                            updateObjectiveField(0, 'higherLevelObjectiveId', alignedObjective.id);
                            updateObjectiveField(0, 'higherLevelObjectiveName', alignedObjective.name);
                        } else if (isMounted && mounted) {
                            console.warn('⚠️ [LOAD EXISTING] Aligned objective not found or failed to load');
                            // Set a placeholder if the objective can't be loaded
                            setSelectedHigherLevelObjective({
                                id: objectiveDetails.objectiveAlignment,
                                name: `Objective ${objectiveDetails.objectiveAlignment.substring(0, 8)}...`,
                                description: 'Unable to load objective details',
                                isUnavailable: true
                            });
                        }
                    } catch (error) {
                        console.error('❌ [LOAD EXISTING] Error loading aligned objective:', error);
                        if (isMounted && mounted) {
                            // Set error placeholder
                            setSelectedHigherLevelObjective({
                                id: objectiveDetails.objectiveAlignment,
                                name: `Objective ${objectiveDetails.objectiveAlignment.substring(0, 8)}...`,
                                description: 'Error loading objective details',
                                error: error.message,
                                isUnavailable: true
                            });
                        }
                    }
                }

                // ✅ Handle goal linkage for CEO users
                if (user?.roleIds?.includes('nLSSLch316I') && objectiveDetails.companyAlignedGoal) {
                    console.log('🎯 [LOAD EXISTING] CEO user with goal:', objectiveDetails.companyAlignedGoal);
                    // Goal setting will be handled by the consolidated effect
                }

            } catch (error) {
                console.error('❌ [LOAD EXISTING] Error loading existing data:', error);
                if (isMounted && mounted) {
                    showAlert('Failed to load existing objective data', 'error', error.message);
                }
            }
        };

        loadExistingData();

        return () => {
            isMounted = false;
        };
    }, [objective]);

    const objectiveIds = useMemo(() => objectives.map(o => o.id), [objectives]);
    const kpiCounts = useMemo(() => objectives.map(o => o.kpis?.length || 0), [objectives]);
    const kraCounts = useMemo(() => objectives.map(o => o.kras?.length || 0), [objectives]);


    // Sync orgUnit with context
    useEffect(() => {
        if (currentOrgUnit?.id) {
            setObjectives(prev => {
                // Only update if orgUnit actually changed
                const needsUpdate = prev.some(obj => obj.orgUnit !== currentOrgUnit.id);
                if (!needsUpdate) return prev;

                return prev.map(obj => ({
                    ...obj,
                    orgUnit: currentOrgUnit.id
                }));
            });
        }
    }, [currentOrgUnit?.id]); // ✅ Only when ID changes

    // Handle higher-level objective selection
    // This captures BOTH:
    // 1. Direct Manager's Objective (who they report to)
    // 2. Company Goal alignment (which strategic goal supports)
    const handleHigherLevelObjectiveSelect = (objective) => {
        setSelectedHigherLevelObjective(objective);

        // Extract the company goal ID - use _goalId (which includes inherited) or fall back to dataValues
        const managerGoalId = objective._goalId ||
            objective.dataValues?.find(dv => dv.dataElement === 'VhrIQQIVpB9')?.value;

        // Extract the parent objective ID (for linking to higher-level)
        const parentObjectiveId = objective.dataValues?.find(
            dv => dv.dataElement === 'neuilg3L4d2' || dv.dataElement === 'plJovUlxfQ0'
        )?.value;

        console.log('🔗 [OBJECTIVE SELECTION] Capturing linkage info:', {
            selectedObjectiveId: objective.id,
            selectedObjectiveName: objective.name,
            companyGoalId: managerGoalId,
            parentObjectiveId: parentObjectiveId,
            goalSource: objective._goalId ? 'inherited from parent' : 'direct alignment'
        });

        if (objectives.length > 0) {
            const targetIndex = mode === 'add' ? objectives.length - 1 : 0;
            updateObjectiveField(targetIndex, 'description', objective.description || '');

            // 1. Link to direct manager's objective
            updateObjectiveField(targetIndex, 'objectiveAlignment', objective.id);
            updateObjectiveField(targetIndex, 'objectiveRoleAssigned', objective.creatorRole || objective.userRole);

            // 2. Set the higher-level objective ID
            updateObjectiveField(targetIndex, 'higherLevelObjectiveId', objective.id);
            updateObjectiveField(targetIndex, 'higherLevelObjectiveName', objective.name);

            // 3. AUTO-POPULATE Company Goal if the selected objective has one
            if (managerGoalId) {
                console.log('🎯 [AUTO-GOAL] Setting company goal from manager objective:', managerGoalId);
                setSelectedGoal(managerGoalId);
                updateObjectiveField(targetIndex, 'companyAlignedGoal', managerGoalId);

                // Also set the manager's goal name for display
                const managerGoalName = objective._managerGoalName ||
                    goalItems.find(g => g.id === managerGoalId)?.name || 'Unknown Goal';
                updateObjectiveField(targetIndex, '_managerGoalName', managerGoalName);
            }

            updateObjectiveField(targetIndex, 'orgUnit', user?.organisationUnits?.[0]?.id || '');

            console.log('✅ [OBJECTIVE LINKAGE] Captured:', {
                reportsTo: objective.name,
                companyGoal: managerGoalId || 'Not set',
                parentObjective: parentObjectiveId || 'None (top-level)'
            });
        }
    };

    useEffect(() => {
        let isMounted = true;

        return () => {
            isMounted = false;
        };
    }, []);

    // Create goal linkage
    const createGoalLinkage = async (savedObjective, goalId) => {
        try {
            const LINKAGE_PROGRAM_STAGE = 'IEsUtu84bQU';

            if (!savedObjective.id) {
                console.warn('Cannot create linkage: objective has no ID');
                return { status: 'warning', message: 'Objective ID missing' };
            }

            const eventDate = savedObjective.startDate || new Date().toISOString().slice(0, 10);

            const linkageData = {
                program: ORG_GOALS_PROGRAM,
                programStage: LINKAGE_PROGRAM_STAGE,
                orgUnit: savedObjective.orgUnit,
                trackedEntityInstance: savedObjective.trackedEntityInstance,
                enrollment: savedObjective.enrollment,
                eventDate: eventDate,
                status: 'ACTIVE',
                dataValues: [
                    { dataElement: 'tKCq1kNpebc', value: savedObjective.id },
                    { dataElement: 'iZfRwpspva7', value: goalId },
                    { dataElement: 'ie2a6Q686VZ', value: 'i7BLYsMDzT0' }
                ]
            };

            console.log('🔍 [GOAL LINKAGE] Final linkage data:', linkageData);


            const result = await saveObjectiveEvent(linkageData, engine, false, 'linkage');
            console.log('Goal linkage created:', result);
            return result;
        } catch (error) {
            console.warn('Failed to create goal linkage (may already exist):', error);

            return { status: 'warning', message: 'Goal linkage may already exist' };
        }
    };


    useEffect(() => {
        if (editingObjective && user?.roleIds?.includes('nLSSLch316I')) {

            // ✅ FIX: Check rawDataValues instead of dataValues
            if (editingObjective.rawDataValues) {
                const goalDataValue = editingObjective.rawDataValues.find(dv =>
                    dv.dataElement === 'VhrIQQIVpB9'
                );

            }
        }
    }, [editingObjective, user]);



    useEffect(() => {
        if (editingObjective && user?.roleIds?.includes('nLSSLch316I') && !loadingGoals) {

            // ✅ Set the selectedGoal when we have goal linkage data AND goals are loaded
            if (editingObjective.companyAlignedGoal && goals.length > 0) {
                const goalExists = goals.some(g => g.id === editingObjective.companyAlignedGoal);
                if (goalExists) {
                    setSelectedGoal(editingObjective.companyAlignedGoal);
                    console.log('🎯 [GOAL SYNC] Set selectedGoal:', editingObjective.companyAlignedGoal);
                } else {
                    console.warn('🎯 [GOAL SYNC] Goal ID not found in available goals:', editingObjective.companyAlignedGoal);
                }
            }
        }
    }, [editingObjective, user, setSelectedGoal, goals, loadingGoals]); // Added goals and loadingGoals dependencies

    // Auto-fill dates for newly added objectives with empty dates
    useEffect(() => {
        // Only run if fiscal year is loaded
        if (!fiscalYear.start || !fiscalYear.end) return;
        
        // Check if any objectives have empty dates that should be filled
        const needsUpdate = objectives.some(obj => 
            (!obj.startDate || obj.startDate === '') && 
            (!obj.completionDate || obj.completionDate === '')
        );
        
        if (needsUpdate) {
            setObjectives(prev => prev.map(obj => ({
                ...obj,
                // Only fill if truly empty (not just falsy)
                startDate: (!obj.startDate || obj.startDate === '') ? fiscalYear.start : obj.startDate,
                completionDate: (!obj.completionDate || obj.completionDate === '') ? fiscalYear.end : obj.completionDate,
                orgUnit: obj.orgUnit || user?.organisationUnits?.[0]?.id || '',
            })));
        }
    }, [objectives.length, fiscalYear.start, fiscalYear.end]); // ✅ Watch objectives.length for new additions

    const checkExistingGoalLinkage = async (objectiveId) => {
        try {
            // Check if goal linkage already exists in the main objective data
            const response = await engine.query({
                event: {
                    resource: `events/${objectiveId}`,
                    params: {
                        fields: 'dataValues[dataElement,value]'
                    }
                }
            });

            // Look for existing goal alignment in the objective's dataValues
            const existingGoalValue = response.event.dataValues?.find(
                dv => dv.dataElement === dataElementMapping.companyAlignedGoal && dv.value
            );

            console.log('🔍 [GOAL LINKAGE CHECK]', {
                objectiveId,
                hasExistingGoal: !!existingGoalValue,
                existingGoalId: existingGoalValue?.value
            });

            return !!existingGoalValue;

        } catch (error) {
            console.error('❌ Error checking existing goal linkage:', error);
            return false; // Default to false if we can't check
        }
    };

 
    // Form submit
    const handleSubmit = async (e, mode = 'final') => {
            if (e?.preventDefault) {
                e.preventDefault();
            }

            const currentObjectives = [...objectives]; // Create fresh copy
                const currentTotalWeight = currentObjectives.reduce((sum, o) => {
                    const w = parseInt(o.weight) || 0;
                    return sum + w;
             }, 0);

            // FIX: REUSE EXISTING ENROLLMENT + FILTER BY FINANCIAL YEAR
            if (mode === 'add' && !weightGroupEnrollment) {
                try {
                    console.log('🔍 [ENROLLMENT] Fetching existing active enrollment for position:', currentUserPosition?.trackedEntityInstance);

                    // ALWAYS fetch existing enrollment FIRST (never create new one)
                    const existingEnrollments = await engine.query({
                        enrollments: {
                            resource: 'enrollments',
                            params: {
                                trackedEntityInstance: currentUserPosition?.trackedEntityInstance,
                                program: 'yiT00XKrrHP',
                                status: 'ACTIVE',
                                fields: 'enrollment',
                                ouMode: 'ACCESSIBLE'
                            }
                        }
                    });

                    const enrollmentsList = existingEnrollments.enrollments?.enrollments || [];

                    if (enrollmentsList.length === 0) {
                        // CRITICAL: Only create if NONE exists (should rarely happen)
                        console.log('🔄 [ENROLLMENT] NO active enrollment found - creating FIRST enrollment');
                        const enrollmentData = {
                            trackedEntityInstance: currentUserPosition?.trackedEntityInstance,
                            program: 'yiT00XKrrHP',
                            orgUnit: user?.organisationUnits?.[0]?.id,
                            enrollmentDate: new Date().toISOString().slice(0, 10),
                            incidentDate: new Date().toISOString().slice(0, 10),
                            status: 'ACTIVE'
                        };

                        const createResponse = await engine.mutate({
                            resource: 'enrollments',
                            type: 'create',
                            data: enrollmentData
                        });

                        let newEnrollmentId =
                            createResponse.response?.importSummaries?.[0]?.reference ||
                            createResponse.response?.enrollment ||
                            createResponse.enrollment;

                        if (!newEnrollmentId) {
                            throw new Error('Failed to obtain enrollment ID after creation');
                        }
                        setWeightGroupEnrollment(newEnrollmentId);
                        console.log('✅ [ENROLLMENT] Created FIRST enrollment:', newEnrollmentId);
                    } else {
                        // ✅ CORRECT BEHAVIOR: REUSE EXISTING ENROLLMENT
                        const enrollmentId = enrollmentsList[0].enrollment;
                        setWeightGroupEnrollment(enrollmentId);
                        console.log(`✅ [ENROLLMENT] Reusing existing enrollment (${enrollmentsList.length} found):`, enrollmentId);

                        // Warn if multiple active enrollments exist (data cleanup needed)
                        if (enrollmentsList.length > 1) {
                            console.warn('⚠️ [ENROLLMENT] MULTIPLE active enrollments found! Using first one. Contact admin to clean up.');
                            showAlert(
                                'Data Warning',
                                'warning',
                                `Multiple active enrollments detected. Using first one. Contact admin to consolidate.`
                            );
                        }
                    }
                } catch (err) {
                    console.error('❌ [ENROLLMENT] Fatal error:', err);
                    showAlert(
                        'Enrollment Error',
                        'error',
                        `Failed to initialize weight group: ${err.message || 'Unknown error'}`
                    );
                    return;
                }
            }

            const totalWeight = objectives.reduce((sum, o) => sum + getObjectiveWeight(o), 0);

           // ✅ Use the pre-calculated currentTotalWeight
            if (mode === 'final' && currentTotalWeight !== 100) {
                showAlert(
                    'Weight Requirement Not Met',
                    'error',
                    `Total objective weight must equal exactly 100% (current: ${currentTotalWeight}%).
            Please adjust your objectives before saving.`
                );
                submissionInProgress.current = false;
                setIsSubmitting(false);
                return;
            }

            // For draft mode: allow any weight, but warn if completely empty
            if (mode === 'draft' && currentTotalWeight === 0 && currentObjectives.every(o => !o.name?.trim())) {
                showAlert('Empty Draft', 'warning', 'No objectives with weight defined. Add at least one objective before saving as draft.');
            }

            console.log('🟢 Starting form submission');

            //  COMPREHENSIVE DUPLICATE SUBMISSION PREVENTION
            if (submissionInProgress.current) {
                console.warn('🚫 Submission already in progress — blocked duplicate');
                return;
            }

            submissionInProgress.current = true;
            setIsSubmitting(true);

            clearNotification();

            const attributes = {};
            currentUserPosition?.attributes?.forEach(attr => {
                attributes[attr.attribute] = attr.value;
            });
            const isTopLevelUser = !attributes['SjP47qi3JeB'];

            // ✅ ADD THIS BEFORE THE OBJECTIVE PROCESSING LOOP
            if (mode === 'add' && !weightGroupEnrollment) {
                showAlert('Error', 'error', 'Enrollment not initialized. Please wait a moment and try again.');
                setIsSubmitting(false);
                submissionInProgress.current = false;
                return;
            }

            // ✅ FIX: Validate each objective's alignment individually
            for (let i = 0; i < objectives.length; i++) {
                const objective = objectives[i];
                
                // Skip validation for existing objectives that already have alignment
                if (objective.id && objective.objectiveAlignment) {
                    continue; // Already has alignment, no need to validate
                }

                // For new objectives or existing ones without alignment
                if (!isTopLevelUser && !selectedHigherLevelObjective) {
                    showAlert(
                        'Validation Error',
                        'error',
                        `Objective ${i + 1} (${objective.name || 'Unnamed'}) requires a higher-level objective alignment. Please select one before saving.`,
                        { duration: 5000 }
                    );
                    submissionInProgress.current = false;
                    setIsSubmitting(false);
                    return;
                }

                if (isTopLevelUser && !selectedGoal) {
                    showAlert(
                        'Validation Error',
                        'error',
                        `Objective ${i + 1} (${objective.name || 'Unnamed'}) requires a company goal linkage. Please select a goal before saving.`,
                        { duration: 5000 }
                    );
                    submissionInProgress.current = false;
                    setIsSubmitting(false);
                    return;
                }

                if (!objective.kpis?.some(k => k?.name?.trim())) {
                    showAlert(
                    'Validation Error',
                    'error',
                    `Objective ${i + 1} (${objective.name || 'Unnamed'}) requires at least one KPI.`,
                    { duration: 5000 }

                    );
                    submissionInProgress.current = false;
                    setIsSubmitting(false);
                    return;
                }
            }

            setSubmissionError(null);

            // Track results for better reporting
            const saveResults = {
                objectives: { created: 0, updated: 0, failed: 0 },
                kras: { success: 0, failed: 0 },
                kpis: { success: 0, failed: 0 },
                linkages: { success: 0, failed: 0 },
                notifications: { success: 0, failed: 0 }
            };

            // ✅ FIX: Track all successfully saved objective IDs and their weights
            const savedObjectiveWeights = [];

            try {
                // ✅ FIX: Process ALL objectives (both new and existing for editing)
                const processedTempIds = new Set();
                const allObjectivesToProcess = objectives.filter(obj =>
                    !processedTempIds.has(obj._tempId || obj.id)
                );

                // Separate into new and existing objectives
                const newObjectives = allObjectivesToProcess.filter(obj => !obj.id);
                const existingObjectives = allObjectivesToProcess.filter(obj => obj.id);

                console.log('🎯 Saving objectives:', {
                    new: newObjectives.length,
                    existing: existingObjectives.length,
                    total: objectives.length
                });

                submissionInProgress.current = true;
                setIsSubmitting(true);
                clearNotification();

                for (const [index, objectiveData] of allObjectivesToProcess.entries()) {
                    // ✅ CRITICAL: Mark as processed IMMEDIATELY (prevents reprocessing in same submission)
                    processedTempIds.add(objectiveData._tempId || objectiveData.id);

                    // ✅ Get position TEI (keep existing validation)
                    const positionTeiId = currentUserPosition?.trackedEntityInstance;
                    if (!positionTeiId) {
                        showAlert('Error', 'error', 'Position not assigned. Contact admin.');
                        saveResults.objectives.failed++;
                        continue;
                    }

                    // Preserve existing alignment if it exists, otherwise use selected values
                    const enhancedObjectiveData = {
                        ...objectiveData,
                        positionTeiId,
                        orgUnit: user?.organisationUnits?.[0]?.id,
                        enrollment: weightGroupEnrollment || objectiveData.enrollment,
                    };

                    const statusValue = mode === 'draft' ? 'draft' : 'pending'; // Match your Option Set codes in U8X3RbnJrYs
                    enhancedObjectiveData.status = statusValue;
                    enhancedObjectiveData._statusDataElement = 'DL7KxNhiV0x'; // Store DE UID for payload injection

                    // Preserve existing alignment for existing objectives
                    if (objectiveData.id) {
                        // This is an existing objective - preserve ALL its original alignment data
                            enhancedObjectiveData.objectiveAlignment = objectiveData.objectiveAlignment;
                            enhancedObjectiveData.companyAlignedGoal = objectiveData.companyAlignedGoal;
                            enhancedObjectiveData.higherLevelObjectiveId = objectiveData.higherLevelObjectiveId;
                            enhancedObjectiveData.higherLevelObjectiveName = objectiveData.higherLevelObjectiveName;
                            enhancedObjectiveData.linkedGoalId = objectiveData.linkedGoalId;
                            enhancedObjectiveData.linkedGoalName = objectiveData.linkedGoalName;
                            enhancedObjectiveData._managerGoalName = objectiveData._managerGoalName;

                    } else {
                        // This is a new objective - apply current selections
                        if (!isTopLevelUser && selectedHigherLevelObjective?.id) {
                                enhancedObjectiveData.objectiveAlignment = selectedHigherLevelObjective.id;
                                enhancedObjectiveData.higherLevelObjectiveId = selectedHigherLevelObjective.id;
                                enhancedObjectiveData.higherLevelObjectiveName = selectedHigherLevelObjective.name;
                                enhancedObjectiveData.objectiveRoleAssigned = selectedHigherLevelObjective.userRole || user?.userCredentials?.userRoles?.[0]?.id;
                                
                                // Also capture the linked goal from the selected higher-level objective
                                if (selectedHigherLevelObjective.companyAlignedGoal) {
                                    enhancedObjectiveData.companyAlignedGoal = selectedHigherLevelObjective.companyAlignedGoal;
                                    const linkedGoal = goals.find(g => g.id === selectedHigherLevelObjective.companyAlignedGoal);
                                    if (linkedGoal) {
                                        enhancedObjectiveData._managerGoalName = linkedGoal.name;
                                        enhancedObjectiveData.linkedGoalId = linkedGoal.id;
                                        enhancedObjectiveData.linkedGoalName = linkedGoal.name;
                                    }
                                }
                            }
                        
                        if (isTopLevelUser && selectedGoal) {
                            enhancedObjectiveData.companyAlignedGoal = selectedGoal;
                            const selectedGoalObj = goals.find(g => g.id === selectedGoal);
                            if (selectedGoalObj) {
                                enhancedObjectiveData._managerGoalName = selectedGoalObj.name;
                                enhancedObjectiveData.linkedGoalId = selectedGoalObj.id;
                                enhancedObjectiveData.linkedGoalName = selectedGoalObj.name;
                            }
                        }
                    }

                    let savedObjective;
                    try {
                        if (objectiveData.id) {
                            // ✅ UPDATE EXISTING OBJECTIVE
                            console.log('🔄 [EDIT MODE] Updating existing objective:', objectiveData.id, objectiveData.name);

                            const userTeiId = positionTeiId || currentUserPosition?.trackedEntityInstance;

                            if (!userTeiId) {
                                throw new Error('Position TEI not found');
                            }

                            // Step 1: Fetch the EXISTING event from DHIS2 to get its ACTUAL enrollment
                            console.log('🔍 [UPDATE] Fetching existing event to get actual enrollment...');
                            let actualEnrollmentId = null;
                            let actualOrgUnit = null;

                            try {
                                const eventResponse = await engine.query({
                                    existingEvent: {
                                        resource: 'events',
                                        id: objectiveData.id,
                                        params: {
                                            fields: 'event,enrollment,orgUnit'
                                        }
                                    }
                                });

                                if (eventResponse?.existingEvent) {
                                    actualEnrollmentId = eventResponse.existingEvent.enrollment;
                                    actualOrgUnit = eventResponse.existingEvent.orgUnit;
                                    console.log(`✅ [UPDATE] Event exists with enrollment: ${actualEnrollmentId}, orgUnit: ${actualOrgUnit}`);
                                } else {
                                    console.warn('⚠️ [UPDATE] Event not found in DHIS2');
                                }
                            } catch (fetchError) {
                                console.error('❌ [UPDATE] Failed to fetch event:', fetchError);
                            }

                            // Step 2: Use the actual enrollment from the event, or try to get from TEI
                            let validEnrollmentId = actualEnrollmentId;

                            if (!validEnrollmentId || validEnrollmentId === 'undefined') {
                                console.log('🔍 [UPDATE] Event has no enrollment, fetching from TEI...');
                                try {
                                    const teiResponse = await engine.query({
                                        teiEnrollments: {
                                            resource: 'trackedEntityInstances',
                                            id: userTeiId,
                                            params: {
                                                fields: 'enrollments[enrollment,program,orgUnit,status]'
                                            }
                                        }
                                    });

                                    const enrollments = teiResponse.teiEnrollments?.enrollments || [];
                                    console.log(`✅ [UPDATE] Found ${enrollments.length} enrollments`);

                                    const objectivesProgramId = 'yiT00XKrrHP';
                                    const validEnrollment = enrollments.find(e =>
                                        e.program === objectivesProgramId &&
                                        e.status === 'ACTIVE'
                                    ) || enrollments.find(e => e.status === 'ACTIVE');

                                    if (validEnrollment) {
                                        validEnrollmentId = validEnrollment.enrollment;
                                        console.log(`✅ [UPDATE] Using enrollment from TEI: ${validEnrollmentId}`);
                                    } else {
                                        console.warn('⚠️ [UPDATE] No enrollment found, will update without enrollment');
                                        validEnrollmentId = undefined;
                                    }
                                } catch (enrollmentError) {
                                    console.error('❌ [UPDATE] Failed to get enrollment:', enrollmentError);
                                    validEnrollmentId = undefined;
                                }
                            }

                            // Step 3: Build the update payload - use actual event data
                            const updatePayload = {
                                ...enhancedObjectiveData,
                                // Use actual enrollment if found
                                ...(validEnrollmentId && { enrollment: validEnrollmentId }),
                                // Use actual orgUnit from event if available
                                orgUnit: actualOrgUnit || enhancedObjectiveData.orgUnit
                            };

                            // Step 4: Create event payload

                         const updateEventPayload = createEventPayload(
                            'objective',
                            {
                                ...updatePayload,
                                // Explicitly include all alignment fields
                                objectiveAlignment: updatePayload.objectiveAlignment,
                                higherLevelObjectiveId: updatePayload.higherLevelObjectiveId,
                                companyAlignedGoal: updatePayload.companyAlignedGoal,
                                linkedGoalId: updatePayload.linkedGoalId,
                                _managerGoalName: updatePayload._managerGoalName, // This won't be saved (not in mapping)
                            },
                            objectiveData.id,
                            updatePayload.orgUnit,
                            userTeiId,
                            validEnrollmentId || undefined
                            );

                            // === MANUALLY INJECT STATUS FOR UPDATES ===
                            if (!updateEventPayload.dataValues) updateEventPayload.dataValues = [];
                            // Remove any existing status value to avoid duplicates
                            updateEventPayload.dataValues = updateEventPayload.dataValues.filter(
                                dv => dv.dataElement !== 'DL7KxNhiV0x'
                            );
                            // Add the new status value
                            updateEventPayload.dataValues.push({
                                dataElement: 'DL7KxNhiV0x',
                                value: statusValue
                            });

                            // Add event ID to the payload for UPDATE operations
                            updateEventPayload.event = objectiveData.id;

                            // If event has actual enrollment, use it directly
                            if (actualEnrollmentId && actualEnrollmentId !== 'undefined') {
                                updateEventPayload.enrollment = actualEnrollmentId;
                            } else {
                                delete updateEventPayload.enrollment;
                            }

                            console.log('🔄 [UPDATE] Payload:', {
                                event: updateEventPayload.event,
                                enrollment: updateEventPayload.enrollment,
                                orgUnit: updateEventPayload.orgUnit
                            });

                            // Call saveObjectiveEvent with isUpdate=true
                            const updateResult = await saveObjectiveEvent(
                                updateEventPayload,
                                engine,
                                true, // isUpdate = true
                                'objective'
                            );

                            if (updateResult?.success) {
                                savedObjective = { ...enhancedObjectiveData, id: objectiveData.id };
                                saveResults.objectives.updated++;
                                console.log('✅ [EDIT MODE] Objective updated successfully:', objectiveData.id);

                                // Update state if we found a valid enrollment
                                if (validEnrollmentId) {
                                    setWeightGroupEnrollment(validEnrollmentId);
                                }
                            } else {
                                throw new Error('Update operation failed');
                            }
                        } else {
                                                   
                            // ✅ CREATE NEW OBJECTIVE
                             console.log('🆕 [CREATE MODE] Creating new objective:', objectiveData.name, 'with status:', statusValue);

                            const createPayload = {
                                ...enhancedObjectiveData,
                                positionTeiId,
                                // Ensure status data element is included
                                _statusValue: statusValue,
                                _statusDataElement: 'DL7KxNhiV0x'
                            };   
             savedObjective = await handleSaveObjective(createPayload);
    
    // ✅ POST-SAVE: If status wasn't saved via payload, manually update the event
    if (savedObjective?.id && mode === 'draft') {
        try {
            // Verify status was saved by fetching the event
            const verifyResponse = await engine.query({
                verifyEvent: {
                    resource: `events/${savedObjective.id}`,
                    params: { fields: 'dataValues[dataElement,value]' }
                }
            });
            
            const savedStatus = verifyResponse.verifyEvent?.dataValues?.find(
                dv => dv.dataElement === 'DL7KxNhiV0x'
            )?.value;
            
            if (String(savedStatus || '').toLowerCase() !== 'draft') {
                console.log('🔧 [STATUS FIX] Manually updating status to draft');
                await engine.mutate({
                    resource: 'events',
                    type: 'update',
                        id: savedObjective.id,
                        data: {
                            dataValues: [{ dataElement: 'DL7KxNhiV0x', value: 'draft' }]
                        }
                    });
                            }
                                } catch (verifyError) {
                                console.warn('⚠️ Could not verify/update draft status:', verifyError);
                            }
                        }

                        saveResults.objectives.created++;

                        }
                    } catch (error) {
                        console.error('❌ Error saving objective:', error);
                        showAlert('Save Failed', 'error', error.message || 'Unknown error during save');
                        saveResults.objectives.failed++;
                        continue;
                    }

                    if (!savedObjective?.id) {
                        console.error('❌ Failed to save Objective', index);
                        saveResults.objectives.failed++;
                        continue;
                    }

                    // ✅ CRITICAL: Track the saved objective weight (for both CREATE and UPDATE)
                    savedObjectiveWeights.push({
                        id: savedObjective.id,
                        weight: objectiveData.weight,
                        name: objectiveData.name
                    });

                    if (objectiveData.id) {
                        // ✅ UPDATE MODE: Update the objective in state with modified data
                        console.log('🔄 [EDIT MODE] Updating objective in state:', objectiveData.id);
                        setObjectives(prev => prev.map(obj => {
                            if (obj.id === objectiveData.id) {
                                return {
                                    ...obj,
                                    ...objectiveData,
                                    status: statusValue,
                                    // Preserve the original ID and enrollment
                                    id: objectiveData.id,
                                    enrollment: obj.enrollment || objectiveData.enrollment
                                };
                            }
                            return obj;
                        }));
                    } else {
                        // ✅ CREATE MODE: Update the objective in state with the NEW ID
                        console.log('🆕 [CREATE MODE] Adding new objective ID to state:', savedObjective.id);
                        setObjectives(prev => prev.map(obj => {
                            // Match by weight and name (since we don't have ID yet)
                            if (!obj.id && obj.weight === objectiveData.weight && obj.name === objectiveData.name) {
                                return { ...obj, id: savedObjective.id, status: statusValue };
                            }
                            return obj;
                        }));

                        setObjectives(prev => prev.map(obj =>
                            obj._tempId === objectiveData._tempId ? { ...obj, id: savedObjective.id, status: statusValue } : obj
                        ));
                    }
                 
                        // Resolve parent objective from objective-level data first so email still works
                        // when global selectedHigherLevelObjective is not populated.
                        const parentObjectiveIdForNotification =
                            objectiveData.objectiveAlignment ||
                            objectiveData.higherLevelObjectiveId ||
                            objectives[index]?.objectiveAlignment ||
                            objectives[index]?.higherLevelObjectiveId ||
                            selectedHigherLevelObjective?.id ||
                            null;

                        // Send when:
                        // 1. NEW objective with parent, OR
                        // 2. EXISTING objective that was returned/resubmitted and is now being finalized
                        const isReturnedForCorrection =
                            isResubmittedObjective(objectiveData) ||
                            isResubmittedObjective(objectives[index]);

                        const shouldSendEmail = mode === 'final' && 
                                            !!parentObjectiveIdForNotification && 
                                            !emailNotificationSent.current &&
                                            (!objectiveData.id || isReturnedForCorrection);

                        if (shouldSendEmail) {
                            emailNotificationSent.current = true;
                            try {
                                console.log('📧 [SUBMISSION DEBUG] Sending email notification', {
                                    objectiveId: savedObjective.id,
                                    parentObjectiveId: parentObjectiveIdForNotification,
                                    isNew: !objectiveData.id,
                                    isResubmission: isReturnedForCorrection,
                                    approvalStatus: objectiveData.approvalStatus
                                });
                                
                                const notificationResult = await sendParentObjectiveNotification(savedObjective, parentObjectiveIdForNotification);
                                console.log('📧 [SUBMISSION DEBUG] Email notification result:', notificationResult);
                                
                                if (notificationResult?.success) {
                                    saveResults.notifications.success++;
                                    console.log('✅ [SUBMISSION] Email sent successfully');
                                    // Show contextual success message
                                    const emailType = isReturnedForCorrection ? 'Resubmission' : 'New';
                                    showAlert(
                                        'Email Sent',
                                        'success',
                                        `${emailType} notification sent to ${notificationResult.recipientName || 'manager'}`
                                    );
                                } else if (notificationResult?.duplicate) {
                                    console.log('🔄 [SUBMISSION] Email already sent, skipping');
                                    saveResults.notifications.success++;
                                } else {
                                    saveResults.notifications.failed++;
                                    console.warn('⚠️ [SUBMISSION] Email failed:', notificationResult?.error);
                                    showAlert(
                                        'Email Notification Failed',
                                        'warning',
                                        `Objective saved but email notification failed: ${notificationResult?.error || 'Unknown error'}`
                                    );
                                }
                            } catch (err) {
                                console.error('❌ [SUBMISSION] Email notification exception:', err);
                                saveResults.notifications.failed++;
                                showAlert(
                                    'Email Error',
                                    'warning',
                                    `Objective saved but email failed: ${err.message || 'Unknown error'}`
                                );
                            } finally {
                                // Reset after delay to allow multiple legitimate notifications in same session
                                setTimeout(() => {
                                    emailNotificationSent.current = false;
                                }, 30000);
                            }
                        } else if (objectiveData.id && parentObjectiveIdForNotification) {
                            // Log when we skip email for existing non-returned objectives
                            if (!isReturnedForCorrection) {
                                console.log(`🔄 [EXISTING OBJECTIVE ${index}] Skipping email - regular edit (not resubmission)`);
                            }
                        }

                    // FIXED: Save KRAs (both new and existing) - only save if they have content
                    for (const [kraIndex, kra] of objectiveData.kras.filter(k => k.name && k.name.trim()).entries()) {
                        try {
                            const kraData = {
                                name: kra.name,
                                description: kra.description || '',
                                weight: kra.weight || 0,
                                // Use the objective's alignment, not the global selection
                                objectiveAlignment: objectiveData.objectiveAlignment || enhancedObjectiveData.objectiveAlignment,
                                objectiveRoleAssigned: objectiveData.objectiveRoleAssigned || enhancedObjectiveData.objectiveRoleAssigned
                            };
                            const isKraUpdate = !!kra.id;
                            const kraEventData = createEventPayload(
                                'kra',
                                kraData,
                                savedObjective.id,
                                savedObjective.orgUnit,
                                savedObjective.trackedEntityInstance,
                                savedObjective.enrollment
                            );
                            if (isKraUpdate) {
                                kraEventData.event = kra.id;
                            }
                            const result = await saveObjectiveEvent(kraEventData, engine, isKraUpdate, 'kra');
                            if (result?.success) {
                                saveResults.kras.success++;
                                // ✅ CRITICAL: Update form state with new ID for CREATE operations
                                if (!isKraUpdate && result.eventId) {
                                    console.log('🟡 Updating KRA ID in state:', {
                                        oldId: kra.id,
                                        newId: result.eventId,
                                        kraName: kra.name
                                    });
                                    // Update the specific KRA in the objectives state
                                    setObjectives(prev => prev.map(obj => {
                                        if (obj.id === savedObjective.id) {
                                            const updatedKras = [...obj.kras];
                                            if (updatedKras[kraIndex]) {
                                                updatedKras[kraIndex] = {
                                                    ...updatedKras[kraIndex],
                                                    id: result.eventId
                                                };
                                            }
                                            return { ...obj, kras: updatedKras };
                                        }
                                        return obj;
                                    }));
                                }
                            } else {
                                console.error('❌ KRA save failed:', kra.name, result);
                                saveResults.kras.failed++;
                            }
                        } catch (error) {
                            console.error('❌ Error saving KRA:', kra.name, error);
                            saveResults.kras.failed++;
                        }
                    }

                    // ✅ FIXED: Save KPIs (both new and existing) - only save if they have content
                    for (const [kpiIndex, kpi] of objectiveData.kpis.filter(k => k.name && k.name.trim()).entries()) {
                        try {
                            const kpiData = {
                                name: kpi.name,
                                description: kpi.description || '',
                                target: kpi.target || '',
                                // Use the objective's alignment, not the global selection
                                objectiveAlignment: objectiveData.objectiveAlignment || enhancedObjectiveData.objectiveAlignment,
                                objectiveRoleAssigned: objectiveData.objectiveRoleAssigned || enhancedObjectiveData.objectiveRoleAssigned
                            };
                            const isKpiUpdate = !!kpi.id;
                            const kpiEventData = createEventPayload(
                                'kpi',
                                kpiData,
                                savedObjective.id,
                                savedObjective.orgUnit,
                                savedObjective.trackedEntityInstance,
                                savedObjective.enrollment
                            );
                            if (isKpiUpdate) {
                                kpiEventData.event = kpi.id;
                            }
                            const result = await saveObjectiveEvent(kpiEventData, engine, isKpiUpdate, 'kpi');
                            if (result?.success) {
                                console.log('✅ KPI saved successfully:', kpi.name);
                                saveResults.kpis.success++;
                                // ✅ CRITICAL: Update form state with new ID for CREATE operations
                                if (!isKpiUpdate && result.eventId) {
                                    console.log('🟡 Updating KPI ID in state:', {
                                        oldId: kpi.id,
                                        newId: result.eventId,
                                        kpiName: kpi.name
                                    });
                                    // Update the specific KPI in the objectives state
                                    setObjectives(prev => prev.map(obj => {
                                        if (obj.id === savedObjective.id) {
                                            const updatedKpis = [...obj.kpis];
                                            if (updatedKpis[kpiIndex]) {
                                                updatedKpis[kpiIndex] = {
                                                    ...updatedKpis[kpiIndex],
                                                    id: result.eventId
                                                };
                                            }
                                            return { ...obj, kpis: updatedKpis };
                                        }
                                        return obj;
                                    }));
                                }
                            } else {
                                console.error('❌ KPI save failed:', kpi.name, result);
                                saveResults.kpis.failed++;
                            }
                        } catch (error) {
                            console.error('❌ Error saving KPI:', kpi.name, error);
                            saveResults.kpis.failed++;
                        }
                    }

                    // Create goal linkage only for new objectives that need it
                    if (!objectiveData.id && user?.roleIds?.includes('nLSSLch316I') && selectedGoal) {
                        try {
                            // ✅ ADD THIS CHECK HERE - before creating goal linkage
                            const hasExistingGoalLinkage = await checkExistingGoalLinkage(savedObjective.id);
                            if (!hasExistingGoalLinkage) {
                                console.log('🟡 Creating new goal linkage...');
                                const linkageResult = await createGoalLinkage(savedObjective, selectedGoal);
                                if (linkageResult?.status === 'warning') {
                                    saveResults.linkages.failed++;
                                } else {
                                    saveResults.linkages.success++;
                                }
                            } else {
                                console.log('🟡 Goal linkage already exists, skipping creation');
                                saveResults.linkages.success++; // Count as success since linkage exists
                            }
                        } catch (error) {
                            console.error('❌ Error checking/creating goal linkage:', error);
                            saveResults.linkages.failed++;
                        }
                    }

                    console.groupEnd(); // End objective processing group
                }

                // This accounts for ALL objectives (both newly created and updated ones)
                // For updated objectives: they already have IDs, so they're in the objectives array
                // For new objectives: their IDs were added to savedObjectiveWeights during processing
                const updatedObjectiveIds = new Set(savedObjectiveWeights.filter(sw =>
                    objectives.some(obj => obj.id === sw.id)
                ).map(sw => sw.id));

                const allObjectiveWeights = [
                    ...objectives.filter(obj => obj.id && !updatedObjectiveIds.has(obj.id)).map(obj => getObjectiveWeight(obj)), // Already saved (not just updated)
                    ...savedObjectiveWeights.map(sw => getObjectiveWeight(sw)) // Just saved OR just updated
                ];
                const totalWeight = allObjectiveWeights.reduce((sum, w) => sum + w, 0);
                console.log('📊 [WEIGHT CALCULATION] Save results:', {
                    created: saveResults.objectives.created,
                    updated: saveResults.objectives.updated,
                    totalProcessed: allObjectivesToProcess.length,
                    totalWeight: totalWeight,
                    weightBreakdown: allObjectiveWeights
                });

                if (mode === 'draft') {
                        showAlert('Draft Saved', 'success', 'Objective saved as draft. You can finalize it later.');
                        
                        // Keep form open with current objectives (don't add blank one)
                        submissionInProgress.current = false;
                        setIsSubmitting(false);
                        
                        // Optionally refresh parent list to show draft status
                        if (onComplete && typeof onComplete === 'function') {
                            onComplete();
                        }
                        
                        // Don't close form - let user continue editing
                        return;
                  }

                // ✅ CRITICAL: Check if total weight equals 100% before closing form
                if (totalWeight === 100) {
                    // ✅ REACHED 100% - Close form and refresh
                    console.log('✅ [FORM SUBMISSION] Total weight = 100%, closing form');

                    // Build success message with details
                    const createdMsg = saveResults.objectives.created > 0 ? `${saveResults.objectives.created} created` : '';
                    const updatedMsg = saveResults.objectives.updated > 0 ? `${saveResults.objectives.updated} updated` : '';
                    const separator = createdMsg && updatedMsg ? ', ' : '';
                    const objectiveMsg = `${createdMsg}${separator}${updatedMsg}`.trim() || 'objectives';

                    // Show success message
                    showAlert(
                        'Success',
                        'success',
                        `All ${objectiveMsg} saved successfully with 100% total weight!`
                    );

                    // Clear all form state
                    setObjectives([]);
                    setSelectedGoal('');
                    setSelectedHigherLevelObjective(null);

                    // ✅ CALL onComplete FIRST to trigger parent refresh
                    if (onComplete && typeof onComplete === 'function') {
                        console.log('✅ [FORM] Triggering parent refresh via onComplete');
                        onComplete(); // Parent will refetch objectives
                    }

                    // ✅ ALSO CALL refreshObjectives to ensure data is updated
                    if (refreshObjectives && typeof refreshObjectives === 'function') {
                        console.log('✅ [FORM] Triggering context refresh');
                        refreshObjectives().catch(error => {
                            console.warn('⚠️ [FORM] Context refresh failed:', error);
                        });
                    }

                    // ✅ CALL onSave IMMEDIATELY to close form
                    if (onSave && typeof onSave === 'function') {
                        console.log('✅ [FORM] Closing form via onSave');
                        onSave(); // This sets uiMode='list' in parent
                    } else {
                        console.warn('⚠️ onSave callback missing - using fallback');
                        if (setUiMode) setUiMode('list');
                    }

                    // Reset submission guard
                    setTimeout(() => {
                        submissionInProgress.current = false;
                    }, 1000);

                    return; // EXIT IMMEDIATELY
                } else {
                    // ❌ NOT 100% YET - Keep form open with new blank objective

                    console.log('🟡 [FORM SUBMISSION] Total weight < 100%, keeping form open', {
                        currentWeight: totalWeight,
                        remaining: 100 - totalWeight
                    });

                    // Keep saved objectives and add new blank one
                    setObjectives(prev => [
                        ...prev.filter(obj => obj.id), // Keep saved objectives with IDs
                        {
                            id: null,
                            _tempId: `temp_${Date.now()}_${prev.length}`,
                            name: '',
                            description: '',
                            weight: 0,
                            startDate: fiscalYear.start,
                            completionDate: fiscalYear.end,
                            orgUnit: user?.organisationUnits?.[0]?.id || '',
                            remarks: '',
                            score: 0,
                            performanceYear: new Date().getFullYear(),
                            workType: '',
                            companyAlignedGoal: '',
                            kras: [],
                            kpis: [{ name: '', description: '', target: '' }],
                        }
                    ]);

                    // Show informational message
                    showAlert(
                        'Continue Adding Objectives',
                        'info',
                        `Saved ${savedObjectiveWeights.length} objective(s). Current total: ${totalWeight}%. 
                            Please add more objectives to reach exactly 100%. 
                            Remaining: ${100 - totalWeight}%`
                    );

                    // Reset submission guard but keep form open
                    submissionInProgress.current = false;
                    setIsSubmitting(false);

                    // Scroll to top of form for better UX
                    window.scrollTo({ top: 0, behavior: 'smooth' });

                    return;
                }
            } catch (error) {
                console.error('❌ [FORM SUBMISSION CRITICAL ERROR]', error);
                showAlert(
                    'Submission Failed',
                    'error',
                    `Failed to save objectives: ${error.message || 'Unknown error'}`
                );
            } finally {
                submissionInProgress.current = false;
                setIsSubmitting(false);
                console.log('🏁 [FORM SUBMISSION FINISHED]');
            }
     };

    return (
        <DndProvider backend={HTML5Backend}>
            <WeightEditorModal />
            <form onSubmit={handleSubmit} style={{
                maxWidth: '1200px',
                padding: '24px',
                backgroundColor: '#fff',
                borderRadius: '8px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            }}>
                <h4 style={{
                    marginTop: 0,
                    marginBottom: '24px',
                    color: '#06910fff',
                    fontSize: '1rem',
                    fontWeight: '600',
                    borderBottom: '2px solid #e0e0e0',
                    paddingBottom: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px'
                }}>
                    {isEditMode ? (
                        <>
                            <span style={{
                                width: '32px',
                                height: '32px',
                                backgroundColor: colors.blue600,
                                color: 'white',
                                borderRadius: '50%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: '1.2rem'
                            }}>
                                {objectives.length}
                            </span>
                            Edit Weight Group ({objectives.length} Objectives)
                        </>
                    ) : (
                        title
                    )}
                </h4>

                {renderPermissionContext()}    
               
                {/* SHOWS FOR ALL OBJECTIVES WITH FEEDBACK */}
                    {isEditMode && objectives.some(obj => obj.remarks) && ( 
                    <div style={{ 
                        marginTop: '24px', 
                        marginBottom: '16px',
                        backgroundColor: '#fff3cd',
                        border: '1px solid #ffc107',
                        borderRadius: '8px',
                        padding: '16px'
                    }}>
                        <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px',
                        marginBottom: '12px',
                        color: '#856404',
                        fontWeight: 'bold',
                        fontSize: '1.1rem'
                        }}>
                        <span role="img" aria-label="warning">⚠️</span>
                        <span>Manager Feedback Required</span>
                        </div>
                        
                        {objectives
                        .filter(obj => obj.remarks) 
                        .map((obj, idx) => (
                            <div 
                            key={`global-remarks-${obj.id || idx}`} 
                            style={{
                                padding: '14px',
                                backgroundColor: '#fff9e6',
                                borderRadius: '6px',
                                borderLeft: '3px solid #ffc107',
                                marginBottom: idx === objectives.length - 1 ? '0' : '12px',
                                position: 'relative'
                            }}
                            >
                            <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                                marginBottom: '8px',
                                fontWeight: '600',
                                color: '#5f4d00'
                            }}>
                                <div style={{
                                width: '24px',
                                height: '24px',
                                backgroundColor: colors.blue600,
                                color: 'white',
                                borderRadius: '50%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: '0.85rem',
                                flexShrink: 0
                                }}>
                                {idx + 1}
                                </div>
                                <span>"{obj.name || `Objective ${idx + 1}`}"</span>
                            </div>
                            
                            <div style={{
                                fontStyle: 'italic',
                                color: '#333',
                                lineHeight: 1.5,
                                paddingLeft: '32px'
                            }}>
                                {obj.remarks}
                            </div>
                            
                            {obj.approvalStatus === 'returned' && (
                                <div style={{
                                marginTop: '8px',
                                padding: '4px 10px',
                                backgroundColor: '#ffe0b2',
                                borderRadius: '4px',
                                fontSize: '0.85rem',
                                color: '#e65100',
                                display: 'inline-block',
                                fontWeight: '500'
                                }}>
                                🔄 Status: Returned for revision
                                </div>
                            )}
                            </div>
                        ))}
                        
                        <div style={{
                        marginTop: '16px',
                        padding: '10px',
                        backgroundColor: '#ffe0b2',
                        borderRadius: '6px',
                        fontSize: '0.9rem',
                        color: '#5f4d00',
                        borderLeft: '3px solid #e65100'
                        }}>
                        <strong>💡 Action Required:</strong> Address manager feedback above before resubmitting objectives.
                        </div>
                    </div>
                    )}

                {notification && (
                    <div style={{
                        position: 'fixed',
                        bottom: '20px',
                        right: '200px',
                        zIndex: 10000,
                        width: '500px'
                    }}>
                        <AlertBar
                            {...notification.type === 'success' ? { success: true } : { critical: true }}
                            duration={8000}
                            onHidden={clearNotification}
                        >
                            <div style={{ display: 'flex', flexDirection: 'column' }}>
                                <span>{notification.message}</span>
                                {notification.details && (
                                    <span style={{ marginTop: '4px', fontSize: '0.9em' }}>
                                        {notification.details}
                                    </span>
                                )}
                            </div>
                        </AlertBar>
                    </div>
                )}


                {/* ✅ CONDITIONAL RENDERING BASED ON MODE AND SELECTION */}
                {(() => {

                    if (mode === 'edit' && selectedHigherLevelObjective?.id) {
                        return (
                            <>
                                {/* Show the "Currently Aligned With" section */}
                                {renderHigherLevelObjectives()}
                                {/* Also show goal selection for context */}
                                {!isTopLevelUser && <GoalSelectionSection />}
                            </>
                        );
                    }

                    // ADD MODE OR EDIT MODE WITHOUT ALIGNMENT
                    if (isTopLevelUser) {
                        // CEO/top-level users see goal selection
                        return (
                            <>
                                {renderHigherLevelObjectives()}
                                {renderGoalSelection()}
                            </>
                        );
                    } else {
                        // Staff members see new goal-driven workflow
                        return (
                            <>
                                <GoalSelectionSection />
                                <FilteredManagerObjectives />
                            </>
                        );
                    }
                })()}

                {/* Draft Filter Toggle */}
                    {objectives.some(o => String(o.status || '').toLowerCase() === 'draft') && (
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '12px',
                            marginBottom: '16px',
                            padding: '12px',
                            backgroundColor: colors.grey050,
                            borderRadius: '6px',
                            border: `1px solid ${colors.grey300}`
                        }}>
                            
                            {showDraftsOnly && (
                                <Button
                                    small
                                    secondary
                                    onClick={() => setShowDraftsOnly(false)}
                                    style={{ marginLeft: 'auto' }}
                                >
                                    Show All
                                </Button>
                            )}
                        </div>
                    )}

                {isEditMode ? (
                    <EditModeObjectiveList
                        objectives={objectives}
                        expandedObjectiveIndex={expandedObjectiveIndex}
                        totalWeight={totalWeight}
                        isSubmitting={isSubmitting}
                        onCancel={onCancel}
                        handleSubmit={handleSubmit}
                        handleRemoveObjectiveInEditMode={handleRemoveObjectiveInEditMode}
                        handleAddObjectiveInEditMode={handleAddObjectiveInEditMode}
                        updateObjectiveField={updateObjectiveField}
                        handleEditObjectiveWeightChange={handleEditObjectiveWeightChange}
                        setExpandedObjectiveIndex={setExpandedObjectiveIndex}
                        goals={goals}
                        loadingGoals={loadingGoals}
                        isTopLevelUser={isTopLevelUser}
                        onGoalSelect={(goalId, objectiveIndex) => {
                            // Optional: Add any additional logic when goal is selected
                            console.log(`Goal ${goalId} selected for objective ${objectiveIndex}`);
                        }}
                        higherLevelObjectives={higherLevelObjectives}
                        loadingHigherLevel={loadingHigherLevelObjectives}
                        onHigherLevelObjectiveSelect={(objective, objectiveIndex) => {
                            // Optional: Add any additional logic when higher-level objective is selected
                            console.log(`Higher-level objective ${objective.id} selected for objective ${objectiveIndex}`);
                        }}
                        userRole={user?.roleIds}
                        handleKraChange={handleKraChange}

                        handleAddKra={handleAddKra}
                        handleRemoveKra={handleRemoveKra}
                        handleKpiChange={handleKpiChange}
                        handleAddKpi={handleAddKpi}
                        handleRemoveKpi={handleRemoveKpi}
                    />
                ) : (
                    <>
                        {/* Existing ADD mode UI - keep your current implementation */}
                        <TotalWeightTracker
                            currentWeight={sumObjectiveWeights(objectives)}
                            isComplete={sumObjectiveWeights(objectives) === 100}
                        />

                        {/* === STEP 1: OBJECTIVE === */}
                        {currentStep === 'objective' && objectives.length > 0 && (
                       <div style={{ marginBottom: '24px', padding: '16px', border: `1px solid ${colors.grey300}`, borderRadius: '6px' }}>

                                {/* Previously Added Objectives */}
                                {objectives.length > 1 && (
                                    <div style={{
                                        marginBottom: '20px',
                                        padding: '12px',
                                        backgroundColor: colors.grey050,
                                        border: `1px solid ${colors.grey300}`,
                                        borderRadius: '6px'
                                    }}>
                                        <h6 style={{ margin: '0 0 12px 0', color: colors.grey700 }}>
                                            Previously Added Objectives ({objectives.length - 1})
                                        </h6>
                                        {objectives.slice(0, -1).map((obj, idx) => (
                                            <div key={idx} style={{
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                padding: '8px 0',
                                                borderBottom: idx < objectives.length - 2 ? `1px solid ${colors.grey200}` : 'none'
                                            }}>
                                                <div>
                                                    <strong>{obj.name || 'Untitled'}</strong>
                                                    <div style={{ fontSize: '0.85rem', color: colors.grey600 }}>
                                                        Weight: {obj.weight}%
                                                    </div>
                                                </div>
                                                <button
                                                    style={{
                                                        boxShadow: "0 2px 4px rgba(108, 105, 105, 0.5)",
                                                        color: 'white',
                                                        backgroundColor: 'rgb(35, 59, 240)',
                                                        borderRadius: '8px',
                                                        border: 'none',
                                                        width: '60x',
                                                        height: '35px',
                                                        cursor: 'pointer',
                                                        margin: '5px'
                                                    }}        
                                                    onClick={() => {
                                                        console.log('Edit Weight clicked for index:', idx, 'current weight:', objectives[idx].weight);
                                                        setEditingWeightFor(idx);
                                                        setWeightEditValue(objectives[idx].weight || 0);
                                                    }}
                                                >
                                                   <IconEdit16 /> Edit Weight
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                )}

                                {/* Form Fields */}
                                <div style={{
                                    display: 'grid',
                                    gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
                                    gap: '16px',
                                    marginBottom: '16px',
                                }}>
                                    <label style={{ display: 'flex', flexDirection: 'column' }}>
                                        <strong>Objective Name*</strong>
                                        <input
                                            type="text"
                                            value={objectives[objectives.length - 1]?.name || ''}
                                            onChange={e => updateObjectiveField(objectives.length - 1, 'name', e.target.value)}
                                            style={{ padding: '8px', marginTop: '4px', width: '100%', boxSizing: 'border-box' }}
                                        />
                                    </label>
                                </div>

                                {/* Grouped Weight and Dates in Single Row */}
                                <div style={{
                                    display: 'grid',
                                    gridTemplateColumns: '1fr 1fr 1fr',
                                    gap: '16px',
                                    marginBottom: '16px',
                                    alignItems: 'end'
                                }}>
                                    <label style={{ display: 'flex', flexDirection: 'column' }}>
                                        <strong>Weight (%) *</strong>
                                        <input
                                            type="number"
                                            min="1"
                                            max="100"
                                            required
                                            value={objectives[objectives.length - 1]?.weight || ''}
                                            onChange={e => {
                                                const rawValue = e.target.value.trim();
                                                if (rawValue === '' || rawValue === '0') {
                                                    updateObjectiveField(objectives.length - 1, 'weight', null);
                                                    return;
                                                }
                                                const val = parseInt(rawValue, 10);
                                                if (!isNaN(val) && val >= 1 && val <= 100) {
                                                    updateObjectiveField(objectives.length - 1, 'weight', val);
                                                } else {
                                                    updateObjectiveField(objectives.length - 1, 'weight', null);
                                                }
                                            }}
                                            style={{
                                                padding: '8px',
                                                marginTop: '4px',
                                                width: '100%',
                                                boxSizing: 'border-box',
                                                borderColor: objectives[objectives.length - 1]?.weight < 1 || objectives[objectives.length - 1]?.weight > 100
                                                    ? 'red'
                                                    : 'inherit'
                                            }}
                                            placeholder="Enter 1-100"
                                        />
                                        {objectives[objectives.length - 1]?.weight !== null &&
                                            (objectives[objectives.length - 1].weight < 1 || objectives[objectives.length - 1].weight > 100) && (
                                                <span style={{ color: 'red', fontSize: '0.85em', marginTop: '4px' }}>
                                                    ⚠️ Between 1-100%
                                                </span>
                                            )}
                                    </label>

                                    <label style={{ display: 'flex', flexDirection: 'column' }}>
                                        <strong>Start Date</strong>
                                        <input
                                            type="date"
                                            value={objectives[objectives.length - 1]?.startDate || ''}
                                            onChange={e => updateObjectiveField(objectives.length - 1, 'startDate', e.target.value)}
                                            style={{ padding: '8px', marginTop: '4px', width: '100%', boxSizing: 'border-box' }}
                                        />
                                    </label>

                                    <label style={{ display: 'flex', flexDirection: 'column' }}>
                                        <strong>Completion Date</strong>
                                        <input
                                            type="date"
                                            value={objectives[objectives.length - 1]?.completionDate || ''}
                                            onChange={e => updateObjectiveField(objectives.length - 1, 'completionDate', e.target.value)}
                                            style={{ padding: '8px', marginTop: '4px', width: '100%', boxSizing: 'border-box' }}
                                        />
                                    </label>
                                </div>

                                <label style={{ display: 'flex', flexDirection: 'column', marginBottom: '16px' }}>
                                    <strong>Objective Description</strong>
                                    <textarea
                                        value={objectives[objectives.length - 1]?.description || ''}
                                        onChange={e => updateObjectiveField(objectives.length - 1, 'description', e.target.value)}
                                        rows="3"
                                        style={{ padding: '8px', marginTop: '4px', width: '100%', boxSizing: 'border-box', resize: 'vertical' }}
                                    />
                                </label>

                                {isTopLevelUser ? (
                                    /* CEO/Top-Level: Direct Goal Selection */
                                    <div style={{ marginBottom: '16px' }}>
                                        <label style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                            <strong>Company Goal Alignment *</strong>
                                            <select
                                                value={objectives[objectives.length - 1]?.companyAlignedGoal || selectedGoal || ''}
                                                onChange={(e) => {
                                                    const goalId = e.target.value;
                                                    updateObjectiveField(objectives.length - 1, 'companyAlignedGoal', goalId);
                                                    setSelectedGoal(goalId);
                                                }}
                                                style={{
                                                    padding: '10px',
                                                    border: `1px solid ${colors.blue300}`,
                                                    borderRadius: '4px',
                                                    fontSize: '0.95rem',
                                                    backgroundColor: objectives[objectives.length - 1]?.companyAlignedGoal ? colors.blue050 : 'white',
                                                    fontWeight: objectives[objectives.length - 1]?.companyAlignedGoal ? 'bold' : 'normal',
                                                    width: '100%',
                                                    boxSizing: 'border-box'
                                                }}
                                                disabled={loadingGoals || isSubmitting}
                                            >
                                                <option value="">-- Select a Company Goal --</option>
                                                {goals.map(goal => (
                                                    <option key={goal.id} value={goal.id}>
                                                        {goal.name} {goal.description ? `- ${goal.description.substring(0, 30)}...` : ''}
                                                    </option>
                                                ))}
                                            </select>
                                            {loadingGoals && <small style={{ color: colors.blue600 }}>Loading goals...</small>}
                                        </label>
                                        {/* Show goal details if selected */}
                                        {(objectives[objectives.length - 1]?.companyAlignedGoal || selectedGoal) && (
                                            <div style={{
                                                marginTop: '8px',
                                                padding: '8px',
                                                backgroundColor: colors.green050,
                                                border: `1px solid ${colors.green300}`,
                                                borderRadius: '4px',
                                                fontSize: '0.9rem'
                                            }}>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                    <span style={{ fontSize: '1.2rem' }}>🎯</span>
                                                    <span>
                                                        <strong>Aligned Goal:</strong>{' '}
                                                        {goals.find(g => g.id === (objectives[objectives.length - 1]?.companyAlignedGoal || selectedGoal))?.name || 'Unknown Goal'}
                                                    </span>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                ) : (
                                    /* Staff: Higher-Level Objective Selection */
                                    <div style={{ marginBottom: '16px' }}>
                                        <label style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                            <strong>Higher-Level Objective Alignment *</strong>
                                            <select
                                                value={objectives[objectives.length - 1]?.objectiveAlignment || selectedHigherLevelObjective?.id || ''}
                                                onChange={(e) => {
                                                    const objectiveId = e.target.value;
                                                    const selectedObj = higherLevelObjectives.find(obj => obj.id === objectiveId);
                                                    if (selectedObj) {
                                                        updateObjectiveField(objectives.length - 1, 'objectiveAlignment', selectedObj.id);
                                                        updateObjectiveField(objectives.length - 1, 'higherLevelObjectiveId', selectedObj.id);
                                                        updateObjectiveField(objectives.length - 1, 'higherLevelObjectiveName', selectedObj.name);
                                                        setSelectedHigherLevelObjective(selectedObj);
                                                        // Auto-populate goal if manager objective has one
                                                        if (selectedObj.companyAlignedGoal) {
                                                            updateObjectiveField(objectives.length - 1, 'companyAlignedGoal', selectedObj.companyAlignedGoal);
                                                            const goal = goals.find(g => g.id === selectedObj.companyAlignedGoal);
                                                            if (goal) {
                                                                updateObjectiveField(objectives.length - 1, '_managerGoalName', goal.name);
                                                            }
                                                        }
                                                    }
                                                }}
                                                style={{
                                                    padding: '10px',
                                                    border: `1px solid ${colors.purple300}`,
                                                    borderRadius: '4px',
                                                    fontSize: '0.95rem',
                                                    backgroundColor: objectives[objectives.length - 1]?.objectiveAlignment ? colors.purple050 : 'white',
                                                    fontWeight: objectives[objectives.length - 1]?.objectiveAlignment ? 'bold' : 'normal',
                                                    width: '100%',
                                                    boxSizing: 'border-box'
                                                }}
                                                disabled={loadingHigherLevelObjectives || isSubmitting}
                                            >
                                                <option value="">-- Select a Manager Objective --</option>
                                                {higherLevelObjectives.map(obj => (
                                                    <option key={obj.id} value={obj.id}>
                                                        {obj.name} (by: {obj.createdBy})
                                                    </option>
                                                ))}
                                            </select>
                                        </label>
                                        {/* Show alignment summary */}
                                        {(objectives[objectives.length - 1]?.objectiveAlignment || selectedHigherLevelObjective?.id) && (
                                            <div style={{
                                                marginTop: '8px',
                                                padding: '8px',
                                                backgroundColor: '#f5f5f5',
                                                borderRadius: '4px',
                                                fontSize: '0.9rem'
                                            }}>
                                                <strong>Aligned to:</strong> {objectives[objectives.length - 1]?.higherLevelObjectiveName || selectedHigherLevelObjective?.name}
                                                {(objectives[objectives.length - 1]?.companyAlignedGoal) && (
                                                    <>
                                                        <br />
                                                        <strong>Goal:</strong> {goals.find(g => g.id === objectives[objectives.length - 1]?.companyAlignedGoal)?.name}
                                                    </>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                )}
                                {/* ===== ✅ END GOAL ALIGNMENT SECTION ✅ ===== */}

                                {/* Remarks field with position-based permissions */}
                                {shouldShowRemarks() && (
                                    <div style={{
                                        marginTop: '16px',
                                        padding: '12px',
                                        backgroundColor: canEditRemarks() ? '#f8f9fa' : '#f0f2f5',
                                        border: `1px solid ${canEditRemarks() ? colors.grey400 : colors.grey300}`,
                                        borderRadius: '4px',
                                        opacity: isSubmitting ? 0.6 : 1
                                    }}>
                                        <label style={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            marginBottom: '12px'
                                        }}>
                                            <div style={{
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                alignItems: 'center',
                                                marginBottom: '4px'
                                            }}>
                                                <strong style={{
                                                    color: canEditRemarks() ? colors.grey900 : colors.grey700
                                                }}>
                                                    Remarks / Feedback
                                                </strong>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                    <span style={{
                                                        fontSize: '0.75rem',
                                                        padding: '2px 6px',
                                                        borderRadius: '3px',
                                                        backgroundColor: canEditRemarks() ? colors.blue100 : colors.grey200,
                                                        color: canEditRemarks() ? colors.blue700 : colors.grey600
                                                    }}>
                                                        {canEditRemarks() ? 'Editable' : 'View-only'}
                                                    </span>
                                                    {mode === 'edit' && (
                                                        <span style={{
                                                            fontSize: '0.7rem',
                                                            color: colors.grey500,
                                                            fontStyle: 'italic'
                                                        }}>
                                                            {isObjectiveCreator() ? 'Creator' : isUserDirectManager ? 'Manager' : 'Viewer'}
                                                        </span>
                                                    )}
                                                </div>
                                            </div>

                                            <textarea
                                                value={objectives[objectives.length - 1]?.remarks || ''}
                                                onChange={e => {
                                                    if (canEditRemarks()) {
                                                        updateObjectiveField(objectives.length - 1, 'remarks', e.target.value);
                                                    }
                                                }}
                                                rows="3"
                                                style={{
                                                    padding: '8px',
                                                    marginTop: '4px',
                                                    width: '100%',
                                                    boxSizing: 'border-box',
                                                    resize: 'vertical',
                                                    fontStyle: 'italic',
                                                    border: `1px solid ${canEditRemarks() ? colors.grey400 : colors.grey300}`,
                                                    borderRadius: '4px',
                                                    backgroundColor: canEditRemarks() ? '#ffffff' : '#f8f9fa',
                                                    cursor: canEditRemarks() ? 'text' : 'not-allowed'
                                                }}
                                                placeholder={
                                                    mode === 'add'
                                                        ? "Add initial remarks for this objective..."
                                                        : canEditRemarks()
                                                            ? "Provide feedback or remarks for objective approval..."
                                                            : "View-only remarks (only direct managers can edit feedback)"
                                                }
                                                readOnly={!canEditRemarks() || isSubmitting}
                                                disabled={!canEditRemarks() || isSubmitting}
                                            />

                                            <small style={{
                                                color: colors.grey600,
                                                marginTop: '4px',
                                                fontStyle: 'italic'
                                            }}>
                                                {mode === 'add'
                                                    ? "You can add initial remarks for this objective."
                                                    : canEditRemarks()
                                                        ? "As the direct manager, you can provide feedback and remarks."
                                                        : "Only the direct manager can edit remarks for feedback and approval."
                                                }
                                            </small>
                                        </label>
                                    </div>
                                )}
                            {/* ===== KRAs & KPIs FOR CURRENT OBJECTIVE ===== */}
                                <div style={{
                                    marginTop: '24px',
                                    marginBottom: '16px',
                                    padding: '16px',
                                    border: `1px solid ${colors.grey300}`,
                                    borderRadius: '6px',
                                    backgroundColor: colors.grey200
                                }}>
                                    <div style={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        marginBottom: '16px',
                                        paddingBottom: '12px',
                                        borderBottom: `2px solid ${colors.grey300}`
                                    }}>
                                        <div>
                                            <h6 style={{
                                                color: colors.grey800,
                                                margin: '0 0 4px 0',
                                                fontWeight: 600,
                                                fontSize: '1.1rem'
                                            }}>
                                                KRAs & KPIs for: "{objectives[objectives.length - 1]?.name?.trim() || 'New Objective'}"
                                            </h6>
                                            <p style={{
                                                margin: 0,
                                                fontSize: '0.85rem',
                                                color: colors.grey600
                                            }}>
                                                Define measurable outcomes for this objective
                                            </p>
                                        </div>
                                        <span style={{
                                            fontSize: '0.8rem',
                                            backgroundColor: colors.blue100,
                                            color: colors.blue800,
                                            padding: '4px 12px',
                                            borderRadius: '12px',
                                            fontWeight: 500
                                        }}>
                                            KPIs required • KRAs optional
                                        </span>
                                    </div>

                                    {/* KRAs Section (OPTIONAL) */}
                                    <div style={{ marginBottom: '24px' }}>
                                        <div style={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'center',
                                            marginBottom: '12px',
                                            padding: '8px',
                                            backgroundColor: colors.grey100,
                                            borderRadius: '4px'
                                        }}>
                                            <div>
                                                <label style={{ fontWeight: 600, color: colors.grey700, margin: 0 }}>
                                                    Key Result Areas
                                                </label>
                                                <p style={{
                                                    margin: '4px 0 0 0',
                                                    fontSize: '0.8rem',
                                                    color: colors.grey600,
                                                    fontStyle: 'italic'
                                                }}>
                                                    Optional - High-level areas of focus
                                                </p>
                                            </div>
                                            <button
                                                type="button"
                                                style={{
                                                    boxShadow: "0 2px 4px rgba(108, 105, 105, 0.5)",
                                                    color: 'white',
                                                    backgroundColor: 'rgb(35, 59, 240)',
                                                    borderRadius: '8px',
                                                    border: 'none',
                                                    width: '100px',
                                                    height: '35px',
                                                    cursor: 'pointer',
                                                    margin: '10px'
                                                }}
                                                onClick={() => {
                                                    if (objectives.length === 0) return; // SAFETY CHECK
                                                    const updated = [...objectives];
                                                    if (updated[objectives.length - 1]) {
                                                        if (!Array.isArray(updated[objectives.length - 1].kras)) updated[objectives.length - 1].kras = [];
                                                        updated[objectives.length - 1].kras.push({ name: '', description: '', weight: 0 });
                                                        setObjectives(updated);
                                                    }
                                                }}
                                            >
                                                <IconAdd16 /> Add KRA
                                            </button>
                                        </div>

                                        {objectives[objectives.length - 1]?.kras?.map((kra, kraIndex) => (
                                            <div key={kraIndex} style={{
                                                display: 'grid',
                                                gridTemplateColumns: '1fr 100px 45px',
                                                gap: '12px',
                                                marginBottom: '12px',
                                                alignItems: 'center',
                                                padding: '12px',
                                                backgroundColor: 'white',
                                                border: `1px solid ${colors.grey300}`,
                                                borderRadius: '4px'
                                            }}>
                                                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                                    <input
                                                        placeholder="e.g., Improve customer satisfaction"
                                                        value={kra?.name || ''}
                                                        onChange={(e) => {
                                                            if (objectives.length === 0) return; // SAFETY CHECK
                                                            const updated = [...objectives];
                                                            if (updated[objectives.length - 1]) {
                                                                if (!Array.isArray(updated[objectives.length - 1].kras)) updated[objectives.length - 1].kras = [];
                                                                if (!updated[objectives.length - 1].kras[kraIndex]) updated[objectives.length - 1].kras[kraIndex] = { name: '', description: '', weight: 0 };
                                                                updated[objectives.length - 1].kras[kraIndex].name = e.target.value;
                                                                setObjectives(updated);
                                                            }
                                                        }}
                                                        style={{
                                                            padding: '8px 12px',
                                                            border: `1px solid ${colors.grey300}`,
                                                            borderRadius: '4px',
                                                            fontSize: '0.9rem',
                                                            width: '100%',
                                                            boxSizing: 'border-box'
                                                        }}
                                                    />
                                                    {kra?.name?.trim() && (
                                                        <textarea
                                                            placeholder="Describe this KRA in detail..."
                                                            value={kra?.description || ''}
                                                            onChange={(e) => {
                                                                if (objectives.length === 0) return; // SAFETY CHECK
                                                                const updated = [...objectives];
                                                                if (updated[objectives.length - 1]) {
                                                                    if (!Array.isArray(updated[objectives.length - 1].kras)) updated[objectives.length - 1].kras = [];
                                                                    if (!updated[objectives.length - 1].kras[kraIndex]) updated[objectives.length - 1].kras[kraIndex] = { name: '', description: '', weight: 0 };
                                                                    updated[objectives.length - 1].kras[kraIndex].description = e.target.value;
                                                                    setObjectives(updated);
                                                                }
                                                            }}
                                                            rows="2"
                                                            style={{
                                                                padding: '6px 10px',
                                                                border: `1px solid ${colors.grey200}`,
                                                                borderRadius: '4px',
                                                                fontSize: '0.85rem',
                                                                width: '100%',
                                                                boxSizing: 'border-box',
                                                                resize: 'vertical'
                                                            }}
                                                        />
                                                    )}
                                                </div>
                                                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                                    <label style={{ fontSize: '0.75rem', color: colors.grey600, fontWeight: 500 }}>
                                                        Weight (%)
                                                    </label>
                                                    <input
                                                        type="number"
                                                        placeholder="0"
                                                        value={kra?.weight || ''}
                                                        onChange={(e) => {
                                                            if (objectives.length === 0) return; // SAFETY CHECK
                                                            const updated = [...objectives];
                                                            if (updated[objectives.length - 1]) {
                                                                const weightValue = Number(e.target.value) || 0;
                                                                if (!Array.isArray(updated[objectives.length - 1].kras)) updated[objectives.length - 1].kras = [];
                                                                if (!updated[objectives.length - 1].kras[kraIndex]) updated[objectives.length - 1].kras[kraIndex] = { name: '', description: '', weight: 0 };
                                                                updated[objectives.length - 1].kras[kraIndex].weight = weightValue;
                                                                setObjectives(updated);
                                                            }
                                                        }}
                                                        min="0"
                                                        max="100"
                                                        style={{
                                                            padding: '8px',
                                                            border: `1px solid ${colors.grey300}`,
                                                            borderRadius: '4px',
                                                            fontSize: '0.9rem',
                                                            textAlign: 'center',
                                                            width: '100%',
                                                            boxSizing: 'border-box'
                                                        }}
                                                    />
                                                </div>
                                                <Button
                                                    type="button"
                                                    small
                                                    destructive
                                                    onClick={() => {
                                                        if (objectives.length === 0) return; // SAFETY CHECK
                                                        const updated = [...objectives];
                                                        if (updated[objectives.length - 1]) {
                                                            if (!Array.isArray(updated[objectives.length - 1].kras)) updated[objectives.length - 1].kras = [];
                                                            updated[objectives.length - 1].kras.splice(kraIndex, 1);
                                                            setObjectives([...updated]);
                                                        }
                                                    }}
                                                    style={{ minWidth: '40px', height: '40px' }}
                                                >
                                                    <IconDelete16 />
                                                </Button>
                                            </div>
                                        ))}

                                        {objectives[objectives.length - 1]?.kras?.length === 0 && (
                                            <div style={{
                                                textAlign: 'center',
                                                padding: '20px',
                                                backgroundColor: colors.grey050,
                                                borderRadius: '4px',
                                                border: `1px dashed ${colors.grey300}`
                                            }}>
                                                <p style={{
                                                    margin: '8px 0 0 0',
                                                    fontSize: '0.8rem',
                                                    color: colors.grey300,
                                                    fontStyle: 'italic'
                                                }}>
                                                    Click "Add KRA" above if you want to define key result areas
                                                </p>
                                            </div>
                                        )}
                                    </div>

                                    {/* KPIs Section (MANDATORY) */}
                                    <div style={{ borderTop: `2px solid ${colors.grey300}`, paddingTop: '20px' }}>
                                        <div style={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'center',
                                            marginBottom: '12px',
                                            padding: '8px',
                                            backgroundColor: colors.orange050,
                                            borderRadius: '4px',
                                            border: `1px solid ${colors.orange300}`
                                        }}>
                                            <div>
                                                <label style={{ fontWeight: 600, color: colors.grey700, margin: 0 }}>
                                                    Key Performance Indicators <span style={{ color: colors.red500 }}>*</span>
                                                </label>
                                                <p style={{
                                                    margin: '4px 0 0 0',
                                                    fontSize: '0.8rem',
                                                    color: colors.grey600,
                                                    fontStyle: 'italic'
                                                }}>
                                                    Required - Specific, measurable metrics
                                                </p>
                                            </div>
                                            <button
                                                type="button"
                                                style={{
                                                    boxShadow: "0 2px 4px rgba(108, 105, 105, 0.5)",
                                                    color: 'white',
                                                    backgroundColor: 'rgb(35, 59, 240)',
                                                    borderRadius: '8px',
                                                    border: 'none',
                                                    width: '100px',
                                                    height: '35px',
                                                    cursor: 'pointer',
                                                    margin: '10px'
                                                }}
                                                onClick={() => {
                                                    if (objectives.length === 0) return; // SAFETY CHECK
                                                    const updated = [...objectives];
                                                    if (updated[objectives.length - 1]) {
                                                        if (!Array.isArray(updated[objectives.length - 1].kpis)) updated[objectives.length - 1].kpis = [];
                                                        updated[objectives.length - 1].kpis.push({ name: '', description: '', target: '' });
                                                        setObjectives(updated);
                                                    }
                                                }}
                                            >
                                                 <IconAdd16 /> Add KPI
                                            </button>
                                        </div>

                                        {objectives[objectives.length - 1]?.kpis?.map((kpi, kpiIndex) => (
                                            <div key={kpiIndex} style={{
                                                display: 'grid',
                                                gridTemplateColumns: '1fr 1fr 100px 45px',
                                                gap: '12px',
                                                marginBottom: '12px',
                                                alignItems: 'center',
                                                padding: '12px',
                                                backgroundColor: 'white',
                                                border: kpi?.name?.trim() ? `1px solid ${colors.green300}` : `1px solid ${colors.grey300}`,
                                                borderRadius: '4px'
                                            }}>
                                                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                                    <input
                                                        placeholder="e.g., Revenue growth, Response time"
                                                        value={kpi?.name || ''}
                                                        onChange={(e) => {
                                                            if (objectives.length === 0) return; // SAFETY CHECK
                                                            const updated = [...objectives];
                                                            if (updated[objectives.length - 1]) {
                                                                if (!Array.isArray(updated[objectives.length - 1].kpis)) updated[objectives.length - 1].kpis = [];
                                                                if (!updated[objectives.length - 1].kpis[kpiIndex]) updated[objectives.length - 1].kpis[kpiIndex] = { name: '', description: '', target: '' };
                                                                updated[objectives.length - 1].kpis[kpiIndex].name = e.target.value;
                                                                setObjectives(updated);
                                                            }
                                                        }}
                                                        required
                                                        style={{
                                                            padding: '8px 12px',
                                                            border: `1px solid ${kpi?.name?.trim() ? colors.green300 : colors.grey300}`,
                                                            borderRadius: '4px',
                                                            fontSize: '0.9rem',
                                                            width: '100%',
                                                            boxSizing: 'border-box',
                                                            fontWeight: kpi?.name?.trim() ? 500 : 400,
                                                            backgroundColor: kpi?.name?.trim() ? colors.green050 : 'white'
                                                        }}
                                                    />
                                                    {kpi?.name?.trim() && (
                                                        <textarea
                                                            placeholder="Describe how this KPI will be measured..."
                                                            value={kpi?.description || ''}
                                                            onChange={(e) => {
                                                                if (objectives.length === 0) return; // SAFETY CHECK
                                                                const updated = [...objectives];
                                                                if (updated[objectives.length - 1]) {
                                                                    if (!Array.isArray(updated[objectives.length - 1].kpis)) updated[objectives.length - 1].kpis = [];
                                                                    if (!updated[objectives.length - 1].kpis[kpiIndex]) updated[objectives.length - 1].kpis[kpiIndex] = { name: '', description: '', target: '' };
                                                                    updated[objectives.length - 1].kpis[kpiIndex].description = e.target.value;
                                                                    setObjectives(updated);
                                                                }
                                                            }}
                                                            rows="2"
                                                            style={{
                                                                padding: '6px 10px',
                                                                border: `1px solid ${colors.grey200}`,
                                                                borderRadius: '4px',
                                                                fontSize: '0.85rem',
                                                                width: '100%',
                                                                boxSizing: 'border-box',
                                                                resize: 'vertical'
                                                            }}
                                                        />
                                                    )}
                                                </div>
                                                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                                    <label style={{ fontSize: '0.75rem', color: colors.grey600, fontWeight: 500 }}>
                                                        Target Value
                                                    </label>
                                                    <input
                                                        placeholder="e.g., 90%, 15%, 24 hours"
                                                        value={kpi?.target || ''}
                                                        onChange={(e) => {
                                                            if (objectives.length === 0) return; // SAFETY CHECK
                                                            const updated = [...objectives];
                                                            if (updated[objectives.length - 1]) {
                                                                if (!Array.isArray(updated[objectives.length - 1].kpis)) updated[objectives.length - 1].kpis = [];
                                                                if (!updated[objectives.length - 1].kpis[kpiIndex]) updated[objectives.length - 1].kpis[kpiIndex] = { name: '', description: '', target: '' };
                                                                updated[objectives.length - 1].kpis[kpiIndex].target = e.target.value;
                                                                setObjectives(updated);
                                                            }
                                                        }}
                                                        required
                                                        style={{
                                                            padding: '8px 12px',
                                                            border: `1px solid ${kpi?.target?.trim() ? colors.green300 : colors.grey300}`,
                                                            borderRadius: '4px',
                                                            fontSize: '0.9rem',
                                                            width: '100%',
                                                            boxSizing: 'border-box',
                                                            backgroundColor: kpi?.target?.trim() ? colors.green050 : 'white'
                                                        }}
                                                    />
                                                </div>
                                                <div style={{
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    backgroundColor: kpi?.name?.trim() && kpi?.target?.trim() ? colors.green100 : colors.grey100,
                                                    borderRadius: '50%',
                                                    width: '40px',
                                                    height: '40px'
                                                }}>
                                                    {kpi?.name?.trim() && kpi?.target?.trim() ? (
                                                        <span style={{ color: colors.green900, fontSize: '1.2rem' }}>✓</span>
                                                    ) : (
                                                        <span style={{ color: colors.grey500, fontSize: '0.9rem' }}>%</span>
                                                    )}
                                                </div>
                                                <Button
                                                    type="button"
                                                    small
                                                    destructive
                                                    onClick={() => {
                                                        if (objectives.length === 0) return; // SAFETY CHECK
                                                        const updated = [...objectives];
                                                        if (updated[objectives.length - 1]) {
                                                            if (!Array.isArray(updated[objectives.length - 1].kpis)) updated[objectives.length - 1].kpis = [];
                                                            updated[objectives.length - 1].kpis.splice(kpiIndex, 1);
                                                            setObjectives([...updated]);
                                                        }
                                                    }}
                                                    style={{ minWidth: '40px', height: '40px' }}
                                                >
                                                    <IconDelete16 />
                                                </Button>
                                            </div>
                                        ))}

                                        {/* KPI Validation Warning */}
                                        {!objectives[objectives.length - 1]?.kpis?.some(k => k?.name?.trim()) && (
                                            <div style={{
                                                marginTop: '12px',
                                                padding: '12px',
                                                backgroundColor: colors.orange050,
                                                border: `1px solid ${colors.orange500}`,
                                                borderRadius: '4px',
                                                fontSize: '0.9rem',
                                                color: colors.orange800,
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '10px'
                                            }}>
                                                <span style={{ fontSize: '1.5rem' }}>⚠️</span>
                                                <div>
                                                    <strong>At least one KPI is required</strong>
                                                </div>
                                            </div>
                                        )}

                                        {/* KPI Summary */}
                                        {objectives[objectives.length - 1]?.kpis?.filter(k => k?.name?.trim()).length > 0 && (
                                            <div style={{
                                                marginTop: '12px',
                                                padding: '10px',
                                                backgroundColor: colors.green050,
                                                border: `1px solid ${colors.green300}`,
                                                borderRadius: '4px',
                                                fontSize: '0.85rem',
                                                marginTop: '10px'
                                            }}>
                                                <strong>{objectives[objectives.length - 1]?.kpis?.filter(k => k?.name?.trim()).length} KPI(s) defined:</strong>
                                                <ul style={{ margin: '6px 0 0 20px', paddingLeft: 0 }}>
                                                    {objectives[objectives.length - 1]?.kpis
                                                        .filter(k => k?.name?.trim())
                                                        .map((kpi, idx) => (
                                                            <li key={idx} style={{ margin: '2px 0' }}>
                                                                <strong>{kpi?.name}</strong> - Target: {kpi?.target || 'Not set'}
                                                            </li>
                                                        ))}
                                                </ul>
                                            </div>
                                        )}
                                    </div>
                                </div>   

                                {totalWeight < 100 && (
                                        <Button
                                            primary
                                            onClick={() => {
                                                const remaining = 100 - totalWeight;
                                                const defaultWeight = remaining > 0 ? Math.min(remaining, 25) : 25;
                                                setObjectives(prev => [
                                                    ...prev,
                                                    {
                                                        id: null,
                                                        _tempId: `temp_${Date.now()}_${prev.length}`,
                                                        name: '',
                                                        description: '',
                                                        weight: defaultWeight,
                                                        startDate: fiscalYear.start || '',
                                                        completionDate: fiscalYear.end || '',
                                                        orgUnit: user?.organisationUnits?.[0]?.id || '',
                                                        remarks: '',
                                                        score: 0,
                                                        performanceYear: new Date().getFullYear(),
                                                        workType: '',
                                                        companyAlignedGoal: '',
                                                        kras: [],
                                                        kpis: [{ name: '', description: '', target: '' }],
                                                    }
                                                ]);
                                                setExpandedObjectiveIndex(objectives.length);
                                                showAlert('New Objective Added', 'success', `You have ${remaining}% remaining to allocate`);
                                            }}
                                            style={{ marginTop: '16px', width: '100%', marginBottom: '16px' }}
                                            disabled={isSubmitting}
                                        >
                                            ➕ Add New Objective ({100 - totalWeight}% remaining)
                                        </Button>
                                    )}                     

                                {/* Save Button */}
                           <div style={{
                                display: 'flex',
                                justifyContent: 'flex-start',
                                alignItems: 'center',
                                gap: '10px',
                                marginTop: '15px',
                                flexWrap: 'wrap',
                                '@media (max-width: 768px)': {
                                    flexDirection: 'column',
                                    '& button': {
                                        width: '100%'
                                    }
                                }
                            }}>
                                <button
                                    onClick={onCancel}
                                    disabled={isSubmitting}
                                    style={{ 
                                        boxShadow: "0 2px 4px rgba(241, 18, 18, 0.5)",
                                        color: 'white',
                                        backgroundColor: 'rgb(208, 9, 9)',
                                        borderRadius: '8px',
                                        border: 'none',
                                        width: '90px',
                                        height: '35px',
                                        cursor: 'pointer',
                                        margin: '10px' }}
                                    >
                                    Cancel
                                </button>
                                <button
                                    style={{
                                        boxShadow: "0 2px 4px rgba(108, 105, 105, 0.5)",
                                        color: 'white',
                                        backgroundColor: '#6c757d', // Grey for draft
                                        borderRadius: '8px',
                                        border: 'none',
                                        width: '150px',
                                        height: '35px',
                                        cursor: 'pointer'
                                    }}
                                    onClick={() => {
                                        // Run similar validation as handleSubmit but call with 'draft'
                                        // For simplicity, you can call handleSubmit directly with mode
                                        handleSubmit(null, 'draft');
                                    }}
                                    disabled={isSubmitting}
                                >
                                    Save as Draft
                                </button>
                                <button
                                    style={{
                                            boxShadow: "0 2px 4px rgba(108, 105, 105, 0.5)",
                                            color: 'white',
                                            backgroundColor: isSubmitting ? colors.grey400 : 'rgb(35, 59, 240)',
                                            borderRadius: '8px',
                                            border: 'none',
                                            width: '230px',
                                            height: '35px',
                                            cursor: isSubmitting ? 'not-allowed' : 'pointer',
                                            margin: '10px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            gap: '8px'
                                        }}
                                    onClick={() => {
                                        const obj = objectives[objectives.length - 1];
                                        const totalWeight = objectives.reduce((sum, o) => sum + getObjectiveWeight(o), 0);

                                        if (!isTopLevelUser && !obj.objectiveAlignment && !selectedHigherLevelObjective) {
                                            showAlert('Validation Error', 'error', 'Select a higher-level objective for this objective.');
                                            return;
                                        }
                                        if (isTopLevelUser && !obj.companyAlignedGoal && !selectedGoal) {
                                            showAlert('Validation Error', 'error', 'Select a company goal for this objective.');
                                            return;
                                        }

                                        // Strict Validation for Final
                                        if (totalWeight !== 100) {
                                            showAlert('Validation Error', 'error', `Total weight must be 100% for final submission. Current: ${totalWeight}%`);
                                            return;
                                        }

                                        // VALIDATION
                                        if (obj.weight === undefined || obj.weight === null || obj.weight <= 0) {
                                            showAlert('Validation Error', 'error', 'Weight is required and must be greater than 0%.');
                                            return;
                                        }
                                        if (!obj.name?.trim()) {
                                            showAlert('Validation Error', 'error', 'Objective name is required.');
                                            return;
                                        }
                                        if (!obj.startDate || !obj.completionDate) {
                                            showAlert('Validation Error', 'error', 'Both dates are required.');
                                            return;
                                        }
                                        if (!isTopLevelUser && !selectedHigherLevelObjective) {
                                            showAlert('Validation Error', 'error', 'Select a higher-level objective.');
                                            return;
                                        }
                                        if (isTopLevelUser && !selectedGoal) {
                                            showAlert('Validation Error', 'error', 'Select a company goal.');
                                            return;
                                        }
                                        if (!obj.kpis?.some(k => k.name?.trim())) {
                                            showAlert('Validation Error', 'error', 'At least one KPI is required for this objective.');
                                            return;
                                        }

                                        // HANDLE WEIGHT FLOW
                                        if (totalWeight < 100) {
                                            setObjectives(prev => [
                                                ...prev,
                                                {
                                                    id: null,
                                                    name: '',
                                                    description: '',
                                                    weight: 0,
                                                    startDate: fiscalYear.start || '', 
                                                    completionDate: fiscalYear.end || '',  
                                                    orgUnit: user?.organisationUnits?.[0]?.id || '',
                                                    remarks: '',
                                                    score: 0,
                                                    performanceYear: new Date().getFullYear(),
                                                    workType: '',
                                                    companyAlignedGoal: '',
                                                    enrollment: weightGroupEnrollment || '', 
                                                    kras: [],
                                                    kpis: [{ name: '', description: '', target: '' }],
                                                }
                                            ]);
                                            showAlert('Objective Added', 'success', `Total weight: ${totalWeight}%. Add more objectives to reach 100%.`);
                                            return;
                                        }

                                        if (totalWeight > 100) {
                                            showAlert('Validation Error', 'error', `Total weight cannot exceed 100% (${totalWeight}%)`);
                                            return;
                                        }

                                        handleSubmit();
                                    }}
                                    disabled={isSubmitting}
                                >
                                    {isSubmitting ? (
                                        <>
                                            <CircularLoader small />
                                            <span>Processing...</span>
                                        </>
                                    ) : (
                                        totalWeight < 100 ? 'Add Objective & Continue' : 'Reach 100% - Save as Final'
                                    )}
                                </button>
                              </div>
                            </div>
                        )}
                        
                        {/* Handle empty objectives state during save/clear */}
                        {currentStep === 'objective' && objectives.length === 0 && (
                            <div style={{
                                textAlign: 'center',
                                padding: '32px',
                                border: `1px solid ${colors.grey300}`,
                                borderRadius: '6px',
                                backgroundColor: colors.grey050
                            }}>
                                <CircularLoader small />
                                <p style={{ marginTop: '16px', color: colors.grey700, fontSize: '1.1rem' }}>
                                    Saving complete. Closing form...
                                </p>
                            </div>
                        )}


                        {/* Only show global save in edit mode */}
                        {mode === 'edit' && (
                            <div style={{
                                display: 'flex',
                                justifyContent: 'flex-end',
                                gap: '12px',
                                marginTop: '24px',
                                borderTop: '1px solid #e0e0e0',
                                paddingTop: '20px'
                            }}>
                                <Button onClick={onCancel} secondary disabled={isSubmitting} style={{ minWidth: '100px' }}>
                                    Cancel
                                </Button>
                                <Button primary type="submit" disabled={isSubmitting} style={{ minWidth: '120px' }} icon={isSubmitting ? <CircularLoader small /> : null}>
                                    Save Changes
                                </Button>
                            </div>
                        )}
                    </>
                )}
            </form>
        </DndProvider>
    );
};

export default ObjectiveForm;
